import { hasViewedBattle, markBattleViewed } from '../app/replay-seen.js';
// components/replay-panel.js -- Jackpot Replay Viewer
// Browse historical jackpot draws: pick a level/day, view tickets, replay the reveal animation.
// Slot-machine spin cycles badges through quadrants with live background coloring per player
// trait ownership, then full-quadrant bubbles pop to reveal prize amounts.
// The center diamond pops independently to reveal Craps, the flame, or a FLIP prize.
//
// Ported faithfully from jackpot-demo.html scratch/reveal UX.

import { createBubbleCover } from './bubble-reveal.js';
import { prepareWinFlights, launchWinFlights, clearWinFlights, positionBadgeReward } from './win-flights.js';
import { deriveWinningTraits, traitToBadge, toDisplayOrder, DISPLAY_ORDER } from '../app/jackpot-data.js';
import { joScaledToTickets } from '../app/jackpot-rolls.js';
import { formatWhalePassAward, formatWhalePassAmount, whalePassTicketEntries } from '../app/whale-pass-format.js';
export { formatWhalePassAward } from '../app/whale-pass-format.js';
import {
  buildRoll1BucketSummaries,
  buildRoll2BucketSummaries,
  splitOpeningFlipDraw,
} from '../app/jackpot-buckets.js';
import {
  winningBadgeLayout,
  winningBadgeRewardDirection,
} from '../app/jackpot-badge-layout.js';
// SHELL-01 patch (Phase 52 followup, mirrors D-09 from jackpot-panel.js):
// swap the wallet-tainted utils.js import for the wallet-free viewer/utils.js
// equivalents so play/ can consume this component via a recursive-import walk
// without tripping the SHELL-01 guardrail on `ethers`.
import {
  formatEth,
  formatEthTruncated,
  formatFlip,
  truncateAddress,
} from '../viewer/utils.js';
import { BADGE_QUADRANTS, BADGE_COLORS, BADGE_ITEMS, badgeCircularPath } from '../app/constants.js';
import { coinDrawCentreModel } from '../app/coin-draw-centre.js';
import { CHAIN } from '../app/chain-config.js';
import { warmBadgeStore } from '../app/badge-sprite.js';
import { GOLD_SIX_MONKEY_BADGE, previewSpinTrait } from '../app/gold-six.js';
import { readLightweightModePreference, subscribeUiPreferences } from '../app/ui-preferences.js';
import { fetchJSON } from '../app/api.js';
import { isDegenesisActive } from '../app/degenesis.js';
import { reusableJackpotPayload } from '../app/last-day-state.js';
import { readCompletedJackpot, readCompletedJackpotRng, rememberCompletedJackpot, readJackpotTraits, rememberJackpotTraits } from '../app/jackpot-cache.js';
import { batch, update } from '../app/reactive-store.js';
import { setMajorDrawActivity } from '../app/major-draw-activity.js';
import { isMuted as isSfxMuted, sfxBubblePop } from '../app/jackpot-sfx.js';
import { subscribePendingActions } from '../app/pending-actions.js';
import { celebrateProtocol } from '../protocol-celebration.js';
import {
  jackpotProcessingPresentationStep,
  jackpotSpinControlState,
  latchedJackpotProcessingStage,
  rngMilestoneSatisfied,
} from '../app/jackpot-processing.js';

const DAY_DATA_RETRY_BASE_MS = 1_500;
const DAY_DATA_RETRY_MAX_MS = 15_000;
// A failed jackpot read keeps the ordinary loading face this long before the
// button admits it: the page-load burst routinely trips the public RPC's
// per-second meter, and the retry loop recovers well inside this window.
const JACKPOT_UNAVAILABLE_GRACE_MS = 20_000;
const RATE_LIMIT_FALLBACK_MS = 15_000;
const MAIN_SPIN_LABEL = 'SPIN JACKPOT';
const BONUS_SPIN_LABEL = 'BONUS SPIN';
const BONUS_SPIN_LOCKED_LABEL = 'POP TO UNLOCK BONUS';
const SPIN_AGAIN_LABEL = 'SPIN AGAIN';
const SPIN_FAILED_LABEL = 'SPIN FAILED · TRY AGAIN';
const COINFLIP_LABEL = 'FLIP COIN';
const CRAPS_BATTLE_LABEL = 'JOIN CRAPS BATTLE';
const JACKPOT_POP_MS = 140;
// Longest a battle open may keep the centre dice spinning before it gives up.
const CRAPS_BATTLE_OPEN_DEADLINE_MS = 45_000;
// The centre's craps dice are the dice trait badges the craps table rolls with (face 5 silver,
// face 2 blue), never hand-drawn pips. They are the whole face: nothing else sits on the diamond.
const CRAPS_CENTRE_DICE = Object.freeze([
  badgeCircularPath('dice', 4, 'silver'),
  badgeCircularPath('dice', 1, 'blue'),
]);
// Shown on the LCD key while the resolver owns its action phase. The resolver's
// successful mineRun() simulation is the chain-authoritative work predicate;
// the face is `white-space: nowrap` monospace at 0.12em tracking, so this stays
// at or under the longest label the key already carries (RESOLVE + RUN
// DECIMATOR), which keeps it on one line down to a 390px viewport.
const MINE_FLIP_CRANK_LABEL = 'MINE FLIP · PROCESSING';
// Same family, shown while one crank call is in flight. The key is disabled
// for that window, so without this it would sit inert on the idle wording and
// give a player who just pressed it nothing back.
const MINE_FLIP_MINING_LABEL = 'MINE FLIP · MINING';
let replayApiRetryAfterUntil = 0;

/** A missing draw (404) is processing; a failed data service is not. */
export function jackpotReadIsUnavailable(error) {
  if (!error || error.name === 'AbortError') return false;
  const status = Number(error.status ?? error.response?.status ?? error.statusCode);
  return !Number.isFinite(status) || status === 0 || status === 429 || status >= 500;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

/** Resolve the ticket cohort used to color the main jackpot reel. */
export function replayHoldingsLevel({
  exactPurchaseLevel = null,
  processingPurchaseLevel = null,
  processingDay = null,
  selectedDay = null,
  selectedLevel = null,
} = {}) {
  const exact = Number(exactPurchaseLevel);
  if (Number.isInteger(exact) && exact > 0) return exact;

  const live = Number(processingPurchaseLevel);
  if (Number(processingDay) === Number(selectedDay)
    && Number.isInteger(live) && live > 0) return live;

  const settled = Number(selectedLevel);
  return Number.isInteger(settled) && settled >= 0 ? settled + 1 : null;
}

/** Keep a live reel on screen anywhere the board does not yet own a final face. */
export function replayAttractShouldRun({
  revealCleared = null,
  dayLoading = false,
  dayWarming = false,
  spinning = false,
  interactiveReveal = false,
  inViewport = true,
  tabVisible = true,
} = {}) {
  return inViewport && tabVisible && !spinning && !interactiveReveal && (
    revealCleared !== true
    || Boolean(dayLoading)
    || Boolean(dayWarming)
  );
}

function ticketWinIdentity(win) {
  const traitId = win?.traitId == null ? '' : Number(win.traitId);
  const level = win?.level == null ? '' : Number(win.level);
  const ticketIndex = win?.ticketIndex == null ? '' : Number(win.ticketIndex);
  return [
    String(win?.winner || '').toLowerCase(),
    String(win?.awardType || '').toLowerCase(),
    traitId,
    String(win?.amount ?? '0'),
    level,
    ticketIndex,
  ].join('|');
}

/**
 * Whether a /replay/rng row carries every board its day drew. A run-56 day always logs a bonus set
 * (a purchase day too, for its foil claims); from run 57 a day logs one board and no bonus set,
 * which the row marks with bonusTraitDraw false and a null bonusTraitsPacked.
 */
function hasDayBoards(rng) {
  return rng?.mainTraitsPacked != null && (rng?.bonusTraitsPacked != null || rng?.bonusTraitDraw === false);
}

/**
 * Restore day-one early-bird ticket awards to the bonus reel.
 *
 * The exact Roll 1/2 endpoints currently omit these rows, while the
 * composed winner total (and therefore DAY SUMMARY) included them. The replay
 * day feed already contains every discrete payout, so subtract the ticket rows
 * represented by the two exact rolls and seat the remaining bonus-trait rows
 * on Roll 2. The multiset subtraction also makes this safe once the API starts
 * returning early-bird rows directly: already-accounted rows are never added
 * twice.
 */
export function includeEarlyBirdTicketWins({ roll1, roll2, distributions, bonusTraits } = {}) {
  const roll1Wins = Array.isArray(roll1?.wins) ? roll1.wins : [];
  const roll2Wins = Array.isArray(roll2?.wins) ? roll2.wins : [];
  const allRows = Array.isArray(distributions) ? distributions : [];
  const allowedTraits = new Set(
    (Array.isArray(bonusTraits) ? bonusTraits : [])
      .map(Number)
      .filter((traitId) => Number.isInteger(traitId) && traitId >= 0 && traitId <= 255),
  );
  if (allowedTraits.size === 0) return roll2;

  const accounted = new Map();
  for (const win of [...roll1Wins, ...roll2Wins]) {
    if (!['ticket', 'tickets'].includes(String(win?.awardType || '').toLowerCase())) continue;
    const key = ticketWinIdentity(win);
    accounted.set(key, (accounted.get(key) || 0) + 1);
  }

  const earlyBirdWins = [];
  for (const row of allRows) {
    if (!['ticket', 'tickets'].includes(String(row?.awardType || '').toLowerCase())) continue;
    const key = ticketWinIdentity(row);
    const remaining = accounted.get(key) || 0;
    if (remaining > 0) {
      accounted.set(key, remaining - 1);
      continue;
    }
    const traitId = Number(row?.traitId);
    if (!Number.isInteger(traitId) || !allowedTraits.has(traitId)) continue;
    earlyBirdWins.push({
      winner: String(row?.winner || '').toLowerCase(),
      awardType: 'tickets',
      traitId,
      quadrant: Math.floor(traitId / 64),
      amount: String(row?.amount ?? '0'),
      level: row?.level == null ? null : Number(row.level),
      sourceLevel: row?.sourceLevel == null ? null : Number(row.sourceLevel),
      ticketIndex: row?.ticketIndex == null ? null : Number(row.ticketIndex),
      earlyBird: true,
    });
  }
  if (earlyBirdWins.length === 0) return roll2;

  return {
    ...(roll2 || {}),
    day: roll2?.day ?? roll1?.day ?? null,
    level: roll2?.level ?? roll1?.level ?? null,
    purchaseLevel: roll2?.purchaseLevel ?? roll1?.purchaseLevel ?? null,
    wins: [...roll2Wins, ...earlyBirdWins],
  };
}

/** Keep the tiny center-diamond FLIP prize to three significant figures. */
export function formatCenterBonusFlip(weiValue) {
  let raw;
  try { raw = BigInt(weiValue ?? 0); }
  catch (_error) { return '0'; }
  if (raw < 0n) raw = -raw;

  const whole = raw / (10n ** 18n);
  if (whole < 1_000n) return whole.toLocaleString('en-US');

  const scientific = () => {
    const source = whole.toString();
    let exponent = source.length - 1;
    let leading = BigInt(source.slice(0, 3));
    if (Number(source[3] || 0) >= 5) leading += 1n;
    if (leading >= 1_000n) {
      leading = 100n;
      exponent += 1;
    }
    const digits = leading.toString().padStart(3, '0');
    const fraction = digits.slice(1).replace(/0+$/, '');
    return `${digits[0]}${fraction ? `.${fraction}` : ''}e${exponent}`;
  };

  const tiers = [
    [10n ** 15n, 'Q'],
    [10n ** 12n, 'T'],
    [10n ** 9n, 'B'],
    [10n ** 6n, 'M'],
    [10n ** 3n, 'K'],
  ];
  let tierIndex = tiers.findIndex(([scale]) => whole >= scale);
  if (tierIndex < 0) tierIndex = tiers.length - 1;

  for (;;) {
    const [scale, suffix] = tiers[tierIndex];
    const units = whole / scale;
    const decimals = units >= 100n ? 0 : units >= 10n ? 1 : 2;
    const factor = 10n ** BigInt(decimals);
    const rounded = ((whole * factor) + (scale / 2n)) / scale;
    if (rounded >= (1_000n * factor)) {
      if (tierIndex === 0) return scientific();
      tierIndex -= 1;
      continue;
    }

    const integer = rounded / factor;
    const fraction = decimals === 0
      ? ''
      : (rounded % factor).toString().padStart(decimals, '0').replace(/0+$/, '');
    return `${integer}${fraction ? `.${fraction}` : ''}${suffix}`;
  }
}

function noteReplayApiResponse(response, now = Date.now()) {
  if (Number(response?.status) !== 429) return;
  const raw = response?.headers?.get?.('retry-after');
  const seconds = raw == null || String(raw).trim() === '' ? NaN : Number(raw);
  let delay = Number.isFinite(seconds) && seconds >= 0
    ? seconds * 1_000
    : NaN;
  if (!Number.isFinite(delay) && raw) {
    const date = Date.parse(raw);
    if (Number.isFinite(date)) delay = Math.max(0, date - now);
  }
  if (!Number.isFinite(delay)) delay = RATE_LIMIT_FALLBACK_MS;
  replayApiRetryAfterUntil = Math.max(replayApiRetryAfterUntil, now + delay);
}

function replayApiCooldownRemaining(now = Date.now()) {
  return Math.max(0, replayApiRetryAfterUntil - now);
}

function dayDataRetryDelay(attempt, now = Date.now()) {
  const retryNumber = Math.max(0, Math.trunc(Number(attempt) || 0));
  const backoff = Math.min(
    DAY_DATA_RETRY_MAX_MS,
    DAY_DATA_RETRY_BASE_MS * (2 ** Math.min(retryNumber, 8)),
  );
  return Math.max(backoff, replayApiCooldownRemaining(now));
}

async function replayFetch(path, init) {
  try {
    return await fetchJSON('/replay' + path, {
      signal: init?.signal,
      force: init?.cache === 'no-store',
      cache: init?.cache,
    });
  } catch (error) {
    noteReplayApiResponse(error?.response);
    throw error;
  }
}

// How far above the day's level Roll 2 can still find rolled traits. Mirrors
// app-tickets-inventory.js FAR_FUTURE_OFFSET: past this, a level has not been
// drawn, so its tickets have no traits to match.
const FAR_FUTURE_HORIZON = 4;

// Scratch-cover fill when the viewed player cannot win a quadrant. This is the
// darker locked/unscratched face; the paper beneath is the lighter loser pink
// from `.q-win-impossible` in replay.css.
const NO_WIN_COVER_FILL = 'rgb(218, 104, 104)';
// The settled scratch face uses the original pale-blue ticket color. Actual
// green/pink results stay hidden behind the separate neutral underlay below.
const POSSIBLE_WIN_COVER_FILL = '#b8d4e8';
const GOLD_TRAIT_COVER_FILL = 'rgb(212, 175, 55)';
// Let the eighth lock remain visibly settled before the scratch surface is
// mounted. Without this beat the final reel frame and completed board happened
// in one task, so the last quadrant appeared to snap to its result colour.
const FINAL_LOCK_SETTLE_MS = 260;
// A revealed win has two deliberate vertical zones. The receipt lives against
// the ticket's outer edge (top for TL/TR, bottom for BL/BR); badges stay in the
// remaining art band beside the center diamond instead of being scattered
// underneath the payout copy.
const BUCKET_REVEAL_POSITION_CLASSES = [
  'replay-bucket-reveal--q0',
  'replay-bucket-reveal--q1',
  'replay-bucket-reveal--q2',
  'replay-bucket-reveal--q3',
];
const BUCKET_REVEAL_VARIANT_CLASSES = [
  'replay-bucket-reveal--solo-eth',
  'replay-bucket-reveal--whale',
  'replay-bucket-reveal--main-miss',
];

function isGoldTrait(traitId) {
  const id = Number(traitId);
  return Number.isInteger(id) && id >= 0
    && Math.floor((id % 64) / 8) === 7;
}

/**
 * Decode badge images with bounded parallelism and resolve only when every
 * usable path is ready to paint. Keeping this contract outside the element
 * makes the warm barrier deterministic in tests instead of relying on browser
 * cache timing.
 */
export async function preloadBadgeImages(
  paths,
  cache,
  { concurrency = 8, ImageCtor = globalThis.Image } = {},
) {
  const queue = Array.isArray(paths) ? paths : [];
  if (queue.length === 0 || typeof ImageCtor !== 'function') return;
  const warmed = cache && typeof cache.has === 'function' && typeof cache.set === 'function'
    ? cache
    : new Map();
  const workerCount = Math.max(1, Math.min(
    queue.length,
    Math.trunc(Number(concurrency) || 1),
  ));
  let next = 0;

  const worker = async () => {
    while (next < queue.length) {
      const path = queue[next++];
      if (!path || warmed.has(path)) continue;
      const image = new ImageCtor();
      let finishLoad;
      const loaded = new Promise((resolve) => { finishLoad = resolve; });
      image.onload = () => finishLoad(true);
      image.onerror = () => finishLoad(false);
      image.src = path;

      let ready = false;
      if (typeof image.decode === 'function') {
        try {
          await image.decode();
          ready = true;
        } catch { /* fall back to the load/error signal below */ }
      }
      if (!ready) ready = await loaded;
      image.onload = null;
      image.onerror = null;
      if (ready) warmed.set(path, image);
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
}

// --- Module-level scratch helpers ---

const BRUSH_R = 28;
const REVEAL_THRESHOLD = 0.5;
const KNOWN_LOSER_REVEAL_THRESHOLD = 0.4;
const GRID_RES = 40;
const CENTER_GRID_RES = 20;
const scratchListenersByCanvas = new WeakMap();

/** Replace one canvas's complete gesture-listener set instead of stacking redraw closures. */
export function replaceScratchListeners(canvas, listeners = []) {
  if (!canvas?.addEventListener || !canvas?.removeEventListener) return;
  const prior = scratchListenersByCanvas.get(canvas) || [];
  for (const { type, handler, options } of prior) {
    canvas.removeEventListener(type, handler, options);
  }
  const active = [];
  for (const entry of listeners) {
    const { type, handler, options } = entry || {};
    if (!type || typeof handler !== 'function') continue;
    canvas.addEventListener(type, handler, options);
    active.push({ type, handler, options });
  }
  scratchListenersByCanvas.set(canvas, active);
}

function makeScratchGrid(res) { return new Uint8Array(res * res); }

function markGridCells(grid, res, canvasW, canvasH, cx, cy, brushR) {
  const cellW = canvasW / res, cellH = canvasH / res;
  const gridCX = cx / cellW, gridCY = cy / cellH;
  const gridR = brushR / Math.min(cellW, cellH);
  const minGX = Math.max(0, Math.floor(gridCX - gridR));
  const maxGX = Math.min(res - 1, Math.ceil(gridCX + gridR));
  const minGY = Math.max(0, Math.floor(gridCY - gridR));
  const maxGY = Math.min(res - 1, Math.ceil(gridCY + gridR));
  for (let gy = minGY; gy <= maxGY; gy++) {
    for (let gx = minGX; gx <= maxGX; gx++) {
      const dx = gx + 0.5 - gridCX, dy = gy + 0.5 - gridCY;
      if (dx * dx + dy * dy <= gridR * gridR) {
        grid[gy * res + gx] = 1;
      }
    }
  }
}

function gridCoverage(grid) {
  let cleared = 0;
  for (let i = 0; i < grid.length; i++) { if (grid[i]) cleared++; }
  return cleared / grid.length;
}

/**
 * Format a prize amount for display in overlays.
 * For ETH: uses formatEth (wei string).
 * For FLIP: uses formatFlip (wei string).
 */
function formatPrizeAmount(weiString, currency) {
  if (currency === 'FLIP') return formatFlip(weiString);
  return formatEth(weiString);
}

function positiveBigInt(value) {
  try {
    const parsed = BigInt(value || 0);
    return parsed > 0n ? parsed : 0n;
  } catch (_error) {
    return 0n;
  }
}

/** Compact icon-and-amount rows shown the first time a paid badge is hovered. */
export function winningBadgeRewardLines(win = {}) {
  const rows = [];
  const awardType = String(win.awardType || '').toLowerCase();
  const eth = positiveBigInt(win.ethTotal ?? (awardType === 'eth' ? win.amount : 0));
  const flip = positiveBigInt(win.flipTotal ?? (
    ['flip', 'farfuturecoin'].includes(awardType) ? win.amount : 0
  ));
  const whales = positiveBigInt(win.whalePassCount ?? (awardType === 'whale_pass' ? win.amount : 0));
  const rawTicketEntries = Number(win.ticketTotal ?? (
    ['ticket', 'tickets'].includes(awardType) ? win.amount : 0
  )) + (win.isSolo ? 0 : Number(whalePassTicketEntries(whales)));
  const tickets = Number.isFinite(rawTicketEntries) && rawTicketEntries > 0
    ? joScaledToTickets(rawTicketEntries)
    : 0;
  if (eth > 0n) {
    const amount = win.isSolo
      ? formatEthTruncated(eth.toString())
      : formatEth(eth.toString());
    rows.push({ kind: 'eth', amount, aria: `${amount} ETH` });
  }
  if (flip > 0n) {
    const amount = formatFlip(flip.toString());
    rows.push({ kind: 'flip', amount, aria: `${amount} FLIP` });
  }
  const passes = positiveBigInt(win.crapsPassTotal ?? (awardType === 'craps_pass' ? win.amount : 0));
  if (win.isSolo && whales > 0n) rows.push({ kind: 'whale-pass', amount: `×${formatWhalePassAmount(whales)}`, aria: formatWhalePassAward(whales) });
  const decimator = positiveBigInt(win.decimatorEntryTotal ?? (awardType === 'decimator_entry' ? win.amount : 0));
  if (decimator > 0n) rows.push({ kind: 'decimator-entry', amount: `${decimator} ${decimator === 1n ? 'ENTRY' : 'ENTRIES'}`,
    aria: `${decimator} Decimator ${decimator === 1n ? 'entry' : 'entries'}` });
  if (passes > 0n) {
    rows.push({ kind: 'craps-pass', amount: `${passes} CRAPS ${passes === 1n ? 'COMP' : 'COMPS'}`,
      aria: `${passes} Craps comp${passes === 1n ? '' : 's'}` });
  }
  if (tickets > 0) {
    const amount = tickets.toLocaleString('en-US', { maximumFractionDigits: 2 });
    rows.push({ kind: 'tickets', amount, aria: `${amount} ticket${tickets === 1 ? '' : 's'}` });
  }
  return rows;
}

/**
 * Count the player's still-covered possible-win panels on Roll 1.
 *
 * Blue/gold already tells the player that they owned the offered trait, so
 * every such quadrant must be uncovered before Bonus Spin even when it turns
 * out to be a miss. Red guaranteed-loss quadrants remain optional. The center
 * is included whenever its outcome or a player payout still has a cover.
 */
export function countUnscratchedPotentialWinPanels({
  quadOwned = [],
  scratched = [],
  centerWinCount = 0,
  centerReveal = false,
  centerScratched = false,
} = {}) {
  let remaining = 0;
  for (let i = 0; i < 4; i++) {
    if (Boolean(quadOwned[i]) && !scratched[i]) remaining++;
  }
  if ((centerReveal || Number(centerWinCount) > 0) && !centerScratched) remaining++;
  return remaining;
}

/** Miniature four-trait ticket shared by jackpot receipts and badge popups. */
function createJackpotTicketIcon(extraClass = '') {
  const ticketIcon = document.createElement('span');
  ticketIcon.className = `replay-bucket-ticket-icon ${extraClass}`.trim();
  ticketIcon.setAttribute('aria-hidden', 'true');
  [1, 74, 147, 228].forEach((traitId, miniQ) => {
    const miniBadge = traitToBadge(traitId);
    if (!miniBadge) return;
    const miniBadgeImg = document.createElement('img');
    miniBadgeImg.className = `replay-bucket-ticket-badge replay-bucket-ticket-badge--q${miniQ}`;
    miniBadgeImg.src = miniBadge.path;
    miniBadgeImg.alt = '';
    ticketIcon.appendChild(miniBadgeImg);
  });
  const ticketFlame = document.createElement('img');
  ticketFlame.className = 'replay-bucket-ticket-flame';
  ticketFlame.src = '/whitepaper/flame-center.svg';
  ticketFlame.alt = '';
  ticketIcon.appendChild(ticketFlame);
  return ticketIcon;
}

// --- Component ---

class ReplayPanel extends HTMLElement {
  static get observedAttributes() {
    return ['data-day-loading', 'data-day-warming'];
  }

  // Public toggle: when false, skip the legacy post-pop reveal chime
  // (#sfxGreenReveal/#sfxPinkReveal/#sfxReveal/#sfxSoloEthReveal) so only the
  // new outcome-dependent bubble pop plays. Defaults true so production
  // behaviour is unchanged.
  // A popped bubble's own pop is its reveal sound; the older chime still plays when a reveal
  // skips the pop (instant reveal-all). Set true to layer the chime after every pop.
  revealChimes = false;
  #popSounded = false;

  #nextRngDay = null;
  #rngDays = [];       // [{day, finalWord}]
  #players = [];       // [address, ...]
  #tickets = [];       // [{address, entryCount, totalMintedOnLevel}] — entryCount is ENTRIES (4 = 1 ticket)
  #selectedDay = null;
  #selectedLevel = null;
  #selectedPlayer = null;
  #openingFlipDay = false; // game level 0: two FLIP boards, no normal ETH/ticket Roll 1
  #distributions = []; // raw distributions from replay/day endpoint (used for prize mapping)
  #winners = [];       // winner objects from /game/jackpot/day/:day/winners

  // Per-day roll caches from /game/jackpot/day/:day/roll1 and /roll2
  #dayRoll1 = null;    // full response: { day, level, purchaseLevel, wins: [...] }
  #dayRoll2 = null;    // full response: { day, level, purchaseLevel, wins: [...] }

  // Per-player filtered wins (derived from day caches by filtering on winner address)
  #playerRoll1Wins = [];  // wins[].filter(w => w.winner === selectedAddr)
  #playerRoll2Wins = [];  // wins[].filter(w => w.winner === selectedAddr)
  #hasBonus = false;      // gates the bonus-roll button. True when player won
                          // Roll 2 OR has any future-level ticket holdings
                          // (eligible to roll but possibly miss).
  #playerHasFutureTickets = null;  // Bool|null; cached per (day,player) by
                                   // #refreshPlayerEligibility(). null => unknown.

  // Spin + scratch state
  #playerTraitIds = new Set();  // Set<number> of owned trait IDs (for spin coloring)
  #traitsTicketsReadyAtLoad = false;
  #traitsCacheAddress = null;   // address for which #playerTraitIds was fetched
  #playerTraitsPending = false; // target level/player is known but its holdings are not
  #playerTraitsLoadSeq = 0;     // prevents an older day/player read settling over a newer one
  // Roll 2 draws against the player's FUTURE-level holdings, not the day's
  // level, so the bonus spin colours off its own set. Union of the traits held
  // at every level above the day's, out to the far-future horizon (levels past
  // that cannot have rolled traits yet).
  #futureTraitIds = new Set();
  #futureTraitsCacheKey = null;
  #futureTraitsInflight = null; // shared flight: the warm-up and the bonus press
                                // must await the same hydration, never two.
  #announcedSpinId = null;     // stable across the skip-animation cancellation
  #animId = 0;                  // spin cancellation token (increment to cancel running spin)
  #spinning = false;            // true while spin animation is running
  #bubbleCovers = new Map();
  #quadRevealReady = [false, false, false, false];
  #preparedWinReceipts = [null, null, null, null];
  #scratched = [false, false, false, false];  // per-quadrant scratch completion
  #scratchGrids = [null, null, null, null];   // per-quadrant Uint8Array scratch grids
  #greenRevealed = [false, false, false, false]; // per-quadrant first-badge win surface
  #badgesRevealed = [[], [], [], []];         // per-badge tracking within each quadrant
  #quadBadgeBounds = [null, null, null, null]; // per-quadrant badge hit circles
  #quadOwned = [false, false, false, false];  // per-quadrant win presence (from playerRoll1Wins)
  #quadWinArrays = [[], [], [], []];          // per-quadrant prize arrays (from playerRoll1/2Wins)
  #centerWins = [];                            // far-future coin wins (center diamond)
  #centerScratched = false;                    // center diamond scratch state
  #centerScratchGrid = null;                   // center diamond scratch grid
  // The day's jackpot craps battle, from the roll-2 payload. Kept apart from
  // #dayRoll2 because the opening-level repair rebuilds that object. The centre opens the battle
  // only for a viewed wallet that was drawn into it (see coin-draw-centre.js).
  #coinDrawBattle = null;
  #coinDrawOpening = false;
  #coinDrawPreloadTimer = null;
  #coinDrawPreloadKey = null;
  #coinDrawStatusFor = null;   // the model a status line belongs to; any other model clears it
  #coinDrawStatus = null;      // { message, error } shown on the centre label while opening/failed
  // `${key}:${player}` battles this viewer has opened; the LCD then moves on. Mirrored to
  // localStorage (craps-battle-seen:*, swept per deployment) so a reload does not ask again.
  #coinDrawSeen = new Set();
  #seenReplayListener = () => { this.#syncCoinDrawCentre(); this.#syncSpinControlState(); };
  #coinDrawResults = new Map();
  // False on a run-56 purchase day (its DailyWinningTraits carries bonusTargetLevel 0) and on every
  // run-57+ day (one board a day): no bonus-trait draw, so no Bonus Spin, and the battle's FLIP
  // lands in the main centre.
  #dayBonusTraitDraw = true;

  // Bonus Spin (Roll 2) state — reuses the main widget
  #bonusPhase = false;          // true while bonus roll is active (Roll 2 reveal)
  #mainScratchComplete = false; // full Roll 1 board; remains the spoiler/persistence boundary
  #mainPotentialScratchComplete = false; // durable Bonus Spin gate: every blue/gold Roll 1 panel uncovered
  #bonusScratchComplete = false;// both boards can be revisited once Roll 2 is uncovered
  #drawViewSwitching = false;   // coalesces rapid center-flame view toggles
  #bonusTraitIds = new Set();   // traitIds the player won in Roll 2 (unused — kept for compat)
  #bonusQuadrants = new Set();  // contract quadrant numbers with roll2.future wins

  // Single-button mode (`single-button` attribute, set by /app/): the main
  // Spin button is the ONLY roll trigger — after Roll 1 it becomes the Bonus
  // Spin trigger, and the day ends with no button rather than a replay spin.
  #btnMode = 'reveal';          // 'reveal' | 'bonus' — what reveal-btn fires
  #mainSpinComplete = false;    // durable spin-complete flags; scratch state is separate
  #bonusSpinComplete = false;
  #coinflipHandoff = { day: null, available: false, revealed: false };
  #coinflipHandoffStarting = false;
  // Public main-draw result under each scratch cover. When the viewed player
  // has no winning entry, the quadrant still reveals its badge, ETH per win,
  // and the number of winning entries.
  #quadPublicSummaries = [null, null, null, null];

  #audioCtx = null;             // Web Audio context for SFX
  #sfxBus = null;               // dry, compressed slot-cabinet output
  #reelVoices = [];             // two persistent voices shared by main + bonus reel frames
  #soloEthCuePlayed = false;    // replaces the generic roll fanfare for this reveal
  #scratchNode = null;          // active scratch noise node
  #scratchNoiseBuffer = null;   // immutable two-second loop, shared by scratch gestures
  #mouseIsDown = false;         // global mouse button state
  #badgeCache = new Map();      // path → warmed Image (preloaded badge SVG cache)
  #badgeWarmPromise = Promise.resolve(); // live reels wait for render-ready badge art
  #rewardPopSequence = 0;       // earlier concurrent reward callouts stay in front
  #rewardDirectionPhase = Math.random(); // random starting side; sequence fans a burst around
  #daysRefreshPromise = null;   // coalesce initial/new-day option reloads
  #lastDaysRefreshAt = 0;       // retry throttle while the indexer catches up
  // /app/ owns the persisted "already scratched" bit.  The replay component
  // accepts that state through setPersistedRevealState(): a cleared draw is
  // reconstructed immediately, while an uncleared draw idles its four reels
  // slowly and silently until the player starts the real spin.
  #hostRevealCleared = null;
  #hostAllRollsCleared = false;
  #hostRevealSeq = 0;
  // The app shell publishes the same persisted state on every data poll. Keep
  // the latest requested key as well as the applied key so duplicate polls are
  // inert even while the first async restore is still loading.
  #hostRevealRequestKey = null;
  #hostRevealAppliedKey = null;
  #loadedDay = null;
  #dayLoadSeq = 0;
  #dayLoadInFlight = null;
  #dayReloadTimer = null;
  #dayReloadAttempt = 0;
  #dayReloadTarget = null;
  // Which day already reported a missing roll endpoint. Around the jackpot the
  // retry loop can poll a not-yet-resolved day for minutes; without this it
  // logged two warnings per attempt and buried every other console message.
  #resolvedDayPayload = null;
  #rollsMissingWarnedDay = null;
  #unavailableReads = new Set();
  // First failure time per unavailable read key, for the loading grace period.
  #unavailableSince = new Map();
  #unavailableGraceTimer = null;
  #idleSpinTimer = null;
  #lightweightUnsubscribe = null;
  #optionalTicketReads = new Set();
  #idleDecorationKey = null;
  #futureOptional = false;
  #futureReadController = null;
  #idleInViewport = true;
  #idleVisibilityCleanup = null;
  // Once the player starts this selection's reveal, polling may update the
  // persisted flags but must not rebuild/cancel the live main or bonus board.
  #interactiveRevealKey = null;
  // Only a direct center-flame click is allowed to turn cancellation into an
  // immediate final frame. Day/player changes and refreshes abort silently.
  #skipSpinId = null;
  #advanceSpin = null; // wakes the current reel immediately on a flame press
  // Deterministic, off-chain data used only by /learn/tutorial/. The tutorial
  // still runs this component's real buttons, reel timing, scratch canvases,
  // sounds, and result renderers; this replaces network reads, not gameplay UI.
  #tutorialFixture = null;
  // A due Decimator temporarily owns the app's primary jackpot action. Keep a
  // complete snapshot of the ordinary spin control so closing the draw returns
  // to exactly the prior day-summary/spin state.
  #primaryDecimatorAction = null;
  #primaryDecimatorBusy = false;
  #primaryDecimatorError = '';
  // The permissionless Mine FLIP crank, mirrored off the same pending-actions
  // feed the bottom tray renders. It is never re-derived here: this holds the
  // resolver's own published row and calls the resolver's own `run`.
  #mineFlipAction = null;
  #mineFlipBusy = false;
  #mineFlipArmed = false;
  #pendingActionUnsubscribe = null;
  #revealStateBeforeDecimator = null;
  // Highest processing-milestone count seen for the day being handed off, so a
  // retry that re-requests a roll cannot slide the progress bar backwards.
  #jpProgressLatch = null;
  #jpProcessingSignals = null;
  #jpPresentationState = null;
  #jpPresentationTimer = null;
  #jpPresentationArmed = false;

  #mounted = false;
  #degenesisMode = null;
  #mountedDegenesis = false;

  setDegenesisMode(active) {
    this.#degenesisMode = Boolean(active);
    this.toggleAttribute('data-degenesis', this.#degenesisMode);
    if (this.#mounted && this.#mountedDegenesis !== this.#degenesisMode) {
      this.disconnectedCallback();
      this.#spinning = false;
    }
    if (this.isConnected && !this.#mounted) this.connectedCallback();
  }

  connectedCallback() {
    if (this.#mounted) return;
    const tutorial = this.ownerDocument?.documentElement?.hasAttribute('data-degenerus-tutorial');
    const degenesis = this.#degenesisMode ?? (this.hasAttribute('data-degenesis')
      || (!tutorial && this.closest?.('.jackpot-hero__machine') && isDegenesisActive()));
    this.#mounted = true;
    this.#mountedDegenesis = Boolean(degenesis);
    if (degenesis) this.setAttribute('data-degenesis', '');
    this.innerHTML = `
      <div class="panel replay-panel">
        <div class="panel-header">
          <h2>JACKPOT REPLAY</h2>
        </div>

        <div class="replay-controls">
          <div class="replay-control-group">
            <label class="replay-label">Day</label>
            <select class="replay-select" data-bind="day-select">
              <option value="">Loading days...</option>
            </select>
          </div>
          <div class="replay-control-group">
            <label class="replay-label">Player</label>
            <select class="replay-select" data-bind="player-select">
              <option value="">Select a day first</option>
            </select>
          </div>
          <button class="btn-primary replay-reveal-btn" data-bind="reveal-btn" disabled>
            SPIN JACKPOT
          </button>
          <div class="jackpot-chainlink jackpot-chainlink--right" aria-hidden="true">
            <span class="jackpot-chainlink__lead jackpot-chainlink__lead--top"></span>
            <span class="jackpot-chainlink__lead jackpot-chainlink__lead--bottom"></span>
            <span class="jackpot-chainlink__pins"></span>
            <span class="jackpot-chainlink__cell jackpot-chainlink__cell--1"></span>
            <span class="jackpot-chainlink__cell jackpot-chainlink__cell--2"></span>
            <span class="jackpot-chainlink__cell jackpot-chainlink__cell--3"></span>
            <span class="jackpot-chainlink__cell jackpot-chainlink__cell--4"></span>
            <span class="jackpot-chainlink__cell jackpot-chainlink__cell--5"></span>
            <span class="jackpot-chainlink__cell jackpot-chainlink__cell--6"></span>
            <span class="jackpot-chainlink__core">VRF</span>
          </div>
        </div>

        <div class="replay-ticket-bar" data-bind="ticket-info" hidden>
          <span class="replay-ticket-count" data-bind="ticket-count"></span>
          <span class="replay-ticket-detail" data-bind="ticket-detail"></span>
        </div>

        <div class="replay-ticket" data-bind="card-grid">
          <div class="replay-tq" data-pos="tl">
            <img class="badge-img" src="" alt="">
            <div class="replay-prize-reveal" data-pos="tl"></div>
            <canvas class="replay-scratch-canvas" data-pos="tl"></canvas>
          </div>
          <div class="replay-tq" data-pos="tr">
            <img class="badge-img" src="" alt="">
            <div class="replay-prize-reveal" data-pos="tr"></div>
            <canvas class="replay-scratch-canvas" data-pos="tr"></canvas>
          </div>
          <div class="replay-tq" data-pos="bl">
            <img class="badge-img" src="" alt="">
            <div class="replay-prize-reveal" data-pos="bl"></div>
            <canvas class="replay-scratch-canvas" data-pos="bl"></canvas>
          </div>
          <div class="replay-tq" data-pos="br">
            <img class="badge-img" src="" alt="">
            <div class="replay-prize-reveal" data-pos="br"></div>
            <canvas class="replay-scratch-canvas" data-pos="br"></canvas>
          </div>
          <div class="replay-ticket-center" data-bind="center">
            <img src="/specials/special_none.svg" alt="Flame" class="replay-flame">
            <span class="replay-center-dice" aria-hidden="true"><img src="${CRAPS_CENTRE_DICE[0]}" alt=""><img src="${CRAPS_CENTRE_DICE[1]}" alt=""></span>
            <div class="replay-center-prize" data-bind="center-prize"></div>
            <canvas class="replay-center-canvas" data-bind="center-canvas"></canvas>
          </div>
        </div>

        <p class="replay-hint" aria-hidden="true"></p>

        <div class="replay-bonus-section" data-bind="bonus-section" hidden>
          <button class="btn-primary replay-bonus-btn" data-bind="bonus-btn">
            BONUS SPIN
          </button>
        </div>

        <!-- Plan 39-10: compact day summary mounted between card grid and winners list -->
        <day-jackpot-summary></day-jackpot-summary>

        <div class="replay-player-decimator" data-bind="player-decimator" hidden>
          <h3 class="replay-dist-title">Player Decimator Claims</h3>
          <div class="replay-player-decimator-list" data-bind="player-decimator-list"></div>
        </div>

        <div class="replay-distributions" data-bind="distributions" hidden>
          <h3 class="replay-dist-title">Jackpot Winners</h3>
          <div class="replay-dist-list" data-bind="dist-list"></div>
        </div>

        <div class="replay-empty" data-bind="empty-state">
          Select a day to replay a jackpot draw
        </div>
      </div>
    `;

    // Degenesis uses the exact cabinet, board, center and control socket.
    // The hub supplies the quadrant contents; no draw reads, listeners,
    // attract reels or badge preloads start until the main game launches.
    if (degenesis) {
      for (const quadrant of this.querySelectorAll('.replay-tq')) quadrant.replaceChildren();
      const control = this.querySelector('[data-bind="reveal-btn"]');
      control.textContent = 'DEGENESIS';
      control.disabled = true;
      return;
    }

    this.#lightweightUnsubscribe = subscribeUiPreferences(({ name, value }) => {
      if (name !== 'lightweightMode') return;
      if (value) {
        for (const controller of this.#optionalTicketReads) controller.abort();
        this.#optionalTicketReads.clear();
        if (this.#futureOptional) this.#futureTraitsInflight = null;
      }
      this.#idleDecorationKey = null;
      this.#stopIdleSpin();
      this.#startIdleSpin();
      if (!value) void this.#loadPlayerTraits();
    });
    this.querySelector('[data-bind="day-select"]').addEventListener('change', (e) => this.#onDayChange(e));
    this.querySelector('[data-bind="player-select"]').addEventListener('change', (e) => this.#onPlayerChange(e));
    document.addEventListener('craps:replay-viewed', this.#seenReplayListener);
    const revealBtn = this.querySelector('[data-bind="reveal-btn"]');
    revealBtn.addEventListener('click', () => {
      if (revealBtn.dataset?.replayAction === 'decimator') {
        void this.#triggerPrimaryDecimator();
        return;
      }
      // While the day's results are still being ground out, this key feeds the
      // crank that grinds them. It takes many mineRun calls to walk ticket and
      // jackpot processing to a resolved day, and the player staring at this
      // LCD is the one who wants it finished. The face and click route share
      // one explicit action token, so a key rendered as Mine FLIP cannot fall
      // through into an ordinary jackpot spin. #triggerMineFlip still performs
      // the final callable check, including its in-flight/double-fire guard.
      if (revealBtn.dataset?.replayAction === 'mine-flip') {
        void this.#triggerMineFlip();
        return;
      }
      // Read the owned control directly. Synthetic/custom click dispatchers do
      // not all preserve Event.currentTarget, and a missing value here would
      // incorrectly replay the jackpot instead of handing off to the coin.
      if (revealBtn.dataset?.replayAction === 'coinflip') {
        this.#triggerCoinflipHandoff();
        return;
      }
      if (revealBtn.dataset?.replayAction === 'craps-battle') {
        void this.#openCoinDrawBattle(revealBtn);
        return;
      }
      if (this.#btnMode === 'bonus') this.#triggerBonusRoll();
      else this.#triggerReveal();
    });
    this.querySelector('[data-bind="bonus-btn"]').addEventListener('click', () => this.#triggerBonusRoll());

    // Global mouse button tracking for scratch stop on mouseup
    this._onMouseDown = () => {
      this.#mouseIsDown = true;
      if (this.#audioCtx && this.#audioCtx.state === 'suspended') this.#audioCtx.resume();
    };
    this._onMouseUp = () => {
      this.#mouseIsDown = false;
      this.#sfxScratchStop();
    };
    document.addEventListener('mousedown', this._onMouseDown);
    document.addEventListener('mouseup', this._onMouseUp);

    // During a spin the center skips to the result. Once both draws have been
    // uncovered, the same flame switches between their saved final states.
    const centerEl = this.querySelector('[data-bind="center"]');
    if (centerEl) {
      centerEl.addEventListener('click', (event) => {
        if (this.#spinning) {
          this.#skipSpinId = this.#animId;
          this.#animId++;
          this.#spinning = false;
          this.#advanceSpin?.();
          return;
        }
        // Finishing a center scratch can synthesize a click on its canvas;
        // don't immediately switch away from the result the player just won.
        if (event?.target?.classList?.contains('replay-center-canvas')) return;
        if (this.#bubbleCovers.has('center')) {
          this.#revealCenter({ event });
          return;
        }
        // The main/bonus toggle keeps the centre when both draws exist; otherwise a purchase
        // day's craps battle owns it for a wallet that was drawn in.
        if (!this.#drawToggleReady() && this.#coinDrawCentre()) {
          void this.#openCoinDrawBattle(centerEl, { replay: true });
          return;
        }
        void this.#toggleRevealedDraw();
      });
      centerEl.addEventListener('keydown', (event) => {
        if (event?.key !== 'Enter' && event?.key !== ' ') return;
        if (this.#spinning) {
          event.preventDefault?.();
          centerEl.click();
          return;
        }
        if (this.#bubbleCovers.has('center')) {
          event.preventDefault?.();
          this.#revealCenter({ event });
          return;
        }
        const toggle = this.#mainReadyForBonus() && this.#bonusScratchComplete;
        if (!toggle && !this.#coinDrawCentre()) return;
        try { event.preventDefault?.(); } catch { /* fake DOM */ }
        if (toggle) void this.#toggleRevealedDraw();
        else void this.#openCoinDrawBattle(centerEl, { replay: true });
      });
    }

    this.#pendingActionUnsubscribe = subscribePendingActions((items) => {
      this.#setPrimaryDecimatorAction(items);
      this.#setMineFlipAction(items);
    });
    this.#syncSpinControlState();
    // Paint the first attract frame synchronously. The replay/day and persisted
    // reveal reads arrive independently, so waiting for either one used to
    // expose the neutral grey reset board during a cold load.
    this.#watchIdleVisibility();
    this.#startIdleSpin();
    if (this.#resolvedDayPayload) this.setResolvedDayPayload(this.#resolvedDayPayload);
    this.refreshDays();
    // A transient failure here used to stay cached: every later spin awaited
    // the same rejected promise for the rest of the mount.
    this.#badgeWarmPromise = this.#preloadBadges().catch((error) => {
      console.warn?.('[replay-panel] badge warm failed', error);
      this.#badgeWarmPromise = null;
    });
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === 'data-day-loading' || name === 'data-day-warming') {
      if (newValue !== null && this.hasAttribute(name)) this.#startIdleSpin();
      else if (!this.#idleSpinWanted()) this.#stopIdleSpin();
      this.#syncSpinControlState();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('craps:replay-viewed', this.#seenReplayListener);
    this.#mounted = false;
    this.#cancelCoinDrawPreload();
    this.#clearBubbleCovers();
    for (const quad of this.querySelectorAll('.replay-tq')) clearWinFlights(quad);
    this.#lightweightUnsubscribe?.();
    this.#lightweightUnsubscribe = null;
    for (const controller of this.#optionalTicketReads) controller.abort();
    this.#optionalTicketReads.clear();
    this.#idleVisibilityCleanup?.();
    this.#idleVisibilityCleanup = null;
    this.#animId++;  // cancel any running spin
    this.#dayLoadSeq++;
    this.#dayLoadInFlight = null;
    if (this.#dayReloadTimer != null) {
      try { clearTimeout(this.#dayReloadTimer); } catch { /* defensive */ }
      this.#dayReloadTimer = null;
    }
    if (this.#jpPresentationTimer != null) {
      try { clearTimeout(this.#jpPresentationTimer); } catch { /* defensive */ }
      this.#jpPresentationTimer = null;
    }
    if (this.#unavailableGraceTimer != null) {
      try { clearTimeout(this.#unavailableGraceTimer); } catch { /* defensive */ }
      this.#unavailableGraceTimer = null;
    }
    this.#dayReloadAttempt = 0;
    this.#dayReloadTarget = null;
    this.#skipSpinId = null;
    this.#interactiveRevealKey = null;
    try { this.#pendingActionUnsubscribe?.(); } catch { /* defensive */ }
    this.#pendingActionUnsubscribe = null;
    this.#primaryDecimatorAction = null;
    this.#primaryDecimatorBusy = false;
    this.#primaryDecimatorError = '';
    this.#mineFlipAction = null;
    this.#mineFlipBusy = false;
    this.#mineFlipArmed = false;
    this.#revealStateBeforeDecimator = null;
    this.removeAttribute?.('data-primary-action');
    this.#stopIdleSpin();
    this.#sfxScratchStop();
    this.#stopReelVoices();
    document.removeEventListener('mousedown', this._onMouseDown);
    document.removeEventListener('mouseup', this._onMouseUp);
  }

  /**
   * Install one deterministic draw for the interactive tutorial.
   *
   * The hook is intentionally unavailable on ordinary app pages. It supplies
   * the same exact-day records the component normally receives from the replay
   * API, then enters the normal quiet attract state. Every interaction after
   * this call travels through #triggerReveal/#triggerBonusRoll/#runSpin and the
   * production scratch engine.
   */
  setTutorialFixture(fixture) {
    if (typeof document === 'undefined'
      || !document.documentElement?.hasAttribute?.('data-degenerus-tutorial')
      || !fixture || typeof fixture !== 'object') return false;

    const day = Number(fixture.day);
    const level = Number(fixture.level);
    const purchaseLevel = Number(fixture.purchaseLevel ?? level);
    const player = String(fixture.player || '').toLowerCase();
    const normalizeTraits = (value) => {
      if (!Array.isArray(value) || value.length !== 4) return null;
      const traits = value.map(Number);
      return traits.every((trait, quadrant) => (
        Number.isInteger(trait) && trait >= quadrant * 64 && trait < (quadrant + 1) * 64
      )) ? traits : null;
    };
    const mainTraits = normalizeTraits(fixture.mainTraits);
    const bonusTraits = normalizeTraits(fixture.bonusTraits);
    const validWins = (wins) => Array.isArray(wins) && wins.every((win) => {
      if (!win || !String(win.winner || '') || !String(win.awardType || '') || win.amount == null) {
        return false;
      }
      try { BigInt(win.amount); } catch { return false; }
      return win.traitId == null
        || (Number.isInteger(Number(win.traitId))
          && Number(win.traitId) >= 0 && Number(win.traitId) <= 255);
    });
    if (!Number.isInteger(day) || day <= 0
      || !Number.isInteger(level) || level < 0
      || !Number.isInteger(purchaseLevel) || purchaseLevel < 0
      || !player || !mainTraits || !bonusTraits
      || !validWins(fixture.roll1Wins) || !validWins(fixture.roll2Wins)) return false;

    const packed = (traits) => (
      (traits[0]
        | (traits[1] << 8)
        | (traits[2] << 16)
        | (traits[3] << 24)) >>> 0
    );
    const playerTraits = new Set((fixture.playerTraits || []).map(Number).filter(Number.isInteger));
    const futureTraits = new Set((fixture.futureTraits || []).map(Number).filter(Number.isInteger));
    this.#tutorialFixture = {
      day,
      level,
      purchaseLevel,
      player,
      mainTraits,
      bonusTraits,
      playerTraits,
      futureTraits,
      roll1Wins: fixture.roll1Wins.map((win) => ({ ...win, amount: String(win.amount) })),
      roll2Wins: fixture.roll2Wins.map((win) => ({ ...win, amount: String(win.amount) })),
    };

    this.#dayLoadSeq += 1;
    this.#dayLoadInFlight = null;
    if (this.#dayReloadTimer != null) {
      try { clearTimeout(this.#dayReloadTimer); } catch { /* defensive */ }
      this.#dayReloadTimer = null;
    }
    this.removeAttribute('data-day-warming');
    this.removeAttribute('data-day-loading');
    this.removeAttribute('aria-busy');
    this.#rngDays = [{
      day,
      level,
      phase: 'J',
      dayInPhase: fixture.dayInPhase ?? 5,
      finalWord: String(fixture.finalWord || '1'),
      mainTraitsPacked: packed(mainTraits),
      bonusTraitsPacked: packed(bonusTraits),
    }];
    this.#selectedDay = day;
    this.#loadedDay = day;
    this.#selectedLevel = level;
    this.#selectedPlayer = player;
    this.#openingFlipDay = false;
    this.#coinDrawBattle = fixture.coinDrawBattle ?? null;
    this.#dayBonusTraitDraw = fixture.bonusTraitDraw !== false;
    this.#dayRoll1 = {
      day,
      level,
      purchaseLevel,
      wins: this.#tutorialFixture.roll1Wins,
      decimatorEntries: fixture.decimatorEntries ?? [],
    };
    this.#dayRoll2 = {
      day,
      level,
      purchaseLevel,
      wins: this.#tutorialFixture.roll2Wins,
    };
    this.#tickets = [{
      address: player,
      entryCount: Number(fixture.entryCount) || 8,
      totalMintedOnLevel: Number(fixture.entryCount) || 8,
    }];
    this.#players = [player];
    this.#winners = [];
    this.#distributions = [];
    this.#playerTraitIds = new Set(playerTraits);
    this.#traitsCacheAddress = `${player}|${purchaseLevel}`;
    this.#playerTraitsPending = false;
    this.#playerTraitsLoadSeq += 1;
    this.#futureTraitIds = new Set(futureTraits);
    this.#futureTraitsCacheKey = `${player}|${purchaseLevel}`;
    this.#playerHasFutureTickets = futureTraits.size > 0;
    this.#hostRevealCleared = false;
    this.#hostAllRollsCleared = false;
    this.#hostRevealRequestKey = null;
    this.#hostRevealAppliedKey = null;
    this.#interactiveRevealKey = null;
    this.#resetCards();
    this.#filterPlayerWins(player);

    const daySelect = this.querySelector('[data-bind="day-select"]');
    if (daySelect) {
      daySelect.innerHTML = `<option value="${day}">Day ${day} — L${level} J${fixture.dayInPhase ?? 5}</option>`;
      daySelect.value = String(day);
    }
    const playerSelect = this.querySelector('[data-bind="player-select"]');
    if (playerSelect) {
      playerSelect.innerHTML = `<option value="${player}">${truncateAddress(player)} (${Number(fixture.entryCount) || 8} entries)</option>`;
      playerSelect.value = player;
    }
    const empty = this.querySelector('[data-bind="empty-state"]');
    if (empty) empty.hidden = true;
    const distributions = this.querySelector('[data-bind="distributions"]');
    if (distributions) distributions.hidden = true;
    const ticketInfo = this.querySelector('[data-bind="ticket-info"]');
    if (ticketInfo) ticketInfo.hidden = false;
    const reveal = this.querySelector('[data-bind="reveal-btn"]');
    if (reveal) {
      reveal.hidden = false;
      reveal.disabled = false;
      reveal.textContent = MAIN_SPIN_LABEL;
      reveal.title = '';
    }
    this.#startIdleSpin();
    return true;
  }

  /** Restore a tutorial page using the normal draw and scratch renderers. */
  async restoreTutorialProgress(progress) {
    if (!this.#tutorialFixture
      || !this.ownerDocument?.documentElement?.hasAttribute('data-degenerus-tutorial')) return false;
    if (!progress?.mainSpinComplete) return true;
    const scratchedQuadrants = [];
    if (progress.commonScratched) scratchedQuadrants.push(0);
    if (progress.silverScratched) scratchedQuadrants.push(2);
    return this.#triggerReveal({ instant: true, persisted: true, scratchedQuadrants });
  }

  #hasExactDayRolls(day = this.#selectedDay) {
    const target = Number(day);
    if (!Number.isInteger(target) || target <= 0) return false;
    const rng = this.#rngDays.find((entry) => Number(entry?.day) === target);
    let hasFinalWord = false;
    try { hasFinalWord = BigInt(rng?.finalWord ?? 0) > 0n; } catch { /* malformed row */ }
    if (!hasFinalWord || !hasDayBoards(rng)) {
      return false;
    }

    const roll1Wins = this.#dayRoll1?.wins;
    const roll2Wins = this.#dayRoll2?.wins;
    if (Number(this.#dayRoll1?.day) !== target || !Array.isArray(roll1Wins)
      || Number(this.#dayRoll2?.day) !== target || !Array.isArray(roll2Wins)) {
      return false;
    }
    const validWin = (win) => {
      if (!win || typeof win !== 'object') return false;
      if (!String(win.winner || '').trim() || !String(win.awardType || '').trim()) return false;
      if (win.amount == null) return false;
      try { BigInt(win.amount); } catch { return false; }
      const traitId = win.traitId;
      return traitId == null
        || (Number.isInteger(Number(traitId)) && Number(traitId) >= 0 && Number(traitId) <= 255);
    };
    if (!roll1Wins.every(validWin) || !roll2Wins.every(validWin)) return false;

    // DailyWinningTraits is emitted before the remaining winner rows in the
    // same block. Even non-empty arrays can therefore be incomplete. The app
    // shell keeps this flag set until PrizePoolDailySnapshot seals the day;
    // never cache a pre-seal response as complete. Once it clears, genuinely
    // empty draws are valid and can be shown.
    if (this.hasAttribute('data-day-warming')) return false;
    return true;
  }

  #dayDataReady(day = this.#selectedDay) {
    const target = Number(day);
    return Number.isInteger(target)
      && target > 0
      && Number(this.#selectedDay) === target
      && Number(this.#loadedDay) === target
      && !this.#playerTraitsPending
      && this.#hasExactDayRolls(target);
  }

  /** App-shell readiness hook used during the exact-day handoff. */
  isDayReady(day = this.#selectedDay) {
    return this.#dayDataReady(day);
  }

  /** App-shell hook: publish the exact-day coinflip's reveal state to the LCD. */
  setCoinflipHandoff({ day = null, available = false, revealed = false } = {}) {
    const parsedDay = Number(day);
    const nextDay = Number.isInteger(parsedDay) && parsedDay > 0 ? parsedDay : null;
    if (nextDay !== this.#coinflipHandoff.day || revealed) {
      this.#coinflipHandoffStarting = false;
    }
    this.#coinflipHandoff = {
      day: nextDay,
      available: Boolean(available),
      revealed: Boolean(revealed),
    };
    this.#syncSpinControlState();
  }

  /** App-shell hook: exact contract/indexer phases for the incoming live day. */
  setJackpotProcessingState(signals = null) {
    if (this.hasAttribute('data-degenesis')) return;
    const day = Number(signals?.day);
    const normalizedDay = Number.isInteger(day) && day > 0 ? day : null;
    const priorDay = Number(this.#jpProcessingSignals?.day);
    const priorPurchaseLevel = Number(this.#jpProcessingSignals?.purchaseLevel);
    const rawPurchaseLevel = Number(signals?.purchaseLevel);
    const purchaseLevel = Number.isInteger(rawPurchaseLevel) && rawPurchaseLevel > 0
      ? rawPurchaseLevel
      : null;
    if (normalizedDay !== (Number.isInteger(priorDay) ? priorDay : null)) {
      if (this.#jpPresentationTimer != null) {
        try { clearTimeout(this.#jpPresentationTimer); } catch { /* defensive */ }
        this.#jpPresentationTimer = null;
      }
      this.#jpProgressLatch = null;
      this.#jpPresentationState = null;
      this.#jpPresentationArmed = false;
    }
    this.#jpProcessingSignals = normalizedDay == null ? null : {
      day: normalizedDay,
      active: signals?.active === true,
      requested: signals?.requested === true,
      rngReady: signals?.rngReady === true,
      // The direct isRngFulfilled() contract witness, kept distinct from the
      // inferred rngReady so the milestone can consume authoritative proof.
      rngFulfilled: signals?.rngFulfilled === true,
      coinflipReady: signals?.coinflipReady === true,
      ticketsReady: signals?.ticketsReady === true,
      jackpotReady: signals?.jackpotReady === true,
      purchaseLevel,
    };
    // Before RNG settles there is no Roll 1 payload to identify the ticket
    // cohort. When the app shell supplies (or corrects) that live level, load
    // its traits immediately; the already-running slow reel reads this Set on
    // every frame and will switch each face to the proper pink/blue state.
    const purchaseLevelChanged = purchaseLevel != null
      && purchaseLevel !== (Number.isInteger(priorPurchaseLevel) ? priorPurchaseLevel : null);
    const selectedDayIsLive = normalizedDay != null
      && normalizedDay === Number(this.#selectedDay);
    const hasExactPurchaseLevel = Number.isInteger(Number(this.#dayRoll1?.purchaseLevel))
      && Number(this.#dayRoll1?.purchaseLevel) > 0;
    if (purchaseLevelChanged && selectedDayIsLive && this.#selectedPlayer
      && !hasExactPurchaseLevel) {
      this.#playerTraitIds = new Set();
      this.#traitsCacheAddress = null;
      this.#playerTraitsPending = true;
      void this.#loadPlayerTraits();
    }
    // Only a day observed while genuinely incomplete replays the full visual
    // pipeline. Opening an already-finished historical day starts at its real
    // PREPARING SPIN fetch instead of pretending to request Chainlink again.
    if (this.#jpProcessingSignals?.active && !this.#jpProcessingSignals.jackpotReady) {
      this.#jpPresentationArmed = true;
    }
    this.#syncSpinControlState();
  }

  #setDayDataLoading(day, loading) {
    const target = Number(day);
    const btn = this.querySelector('[data-bind="reveal-btn"]');
    if (loading) {
      if (btn) btn.disabled = true;
      this.setAttribute('data-day-loading', Number.isInteger(target) ? String(target) : '');
      this.setAttribute('aria-busy', 'true');
      this.#syncSpinControlState();
      return;
    }
    if (Number(this.getAttribute('data-day-loading')) === target) {
      this.removeAttribute('data-day-loading');
    }
    if (!this.hasAttribute('data-day-warming') && !this.hasAttribute('data-day-loading')) {
      this.removeAttribute('aria-busy');
    }
    this.#syncSpinControlState();
  }

  /** Every confirmed contract and indexed step needed before Spin is enabled. */
  #jackpotProcessingMilestones(day = this.#selectedDay) {
    const target = Number(day);
    if (!Number.isInteger(target) || target <= 0) {
      return {
        rng: false, coinflip: false, packs: false, jackpot: false,
        draw: false, rolls: false, sealed: false,
      };
    }
    const live = Number(this.#jpProcessingSignals?.day) === target
      && this.#jpProcessingSignals?.active === true
      ? this.#jpProcessingSignals
      : null;
    const rng = this.#rngDays.find((entry) => Number(entry?.day) === target);
    let hasFinalWord = false;
    try { hasFinalWord = BigInt(rng?.finalWord ?? 0) > 0n; } catch { /* malformed row */ }
    return {
      // Two witnesses, because the live feed has a blind spot in exactly the
      // window the player is watching. `hasFinalWord` is the indexed row this
      // method already loads for `draw`; see rngMilestoneSatisfied.
      rng: rngMilestoneSatisfied({ live, hasIndexedFinalWord: hasFinalWord }),
      coinflip: live ? live.coinflipReady : true,
      packs: live ? live.ticketsReady : true,
      jackpot: live ? live.jackpotReady : true,
      draw: Boolean(hasFinalWord && hasDayBoards(rng)),
      // One milestone: #loadDayRolls resolves both endpoints together and the
      // caller assigns both fields in one block, so a half-loaded pair is a
      // failed fetch awaiting retry, never an intermediate state to render.
      rolls: Number(this.#dayRoll1?.day) === target && Array.isArray(this.#dayRoll1?.wins)
        && Number(this.#dayRoll2?.day) === target && Array.isArray(this.#dayRoll2?.wins),
      // The seal is what makes a non-empty winner array trustworthy; see the
      // DailyWinningTraits note in #hasExactDayRolls.
      sealed: !this.hasAttribute('data-day-warming'),
    };
  }

  #jackpotProcessingStage() {
    const day = Number(this.#selectedDay);
    const { stage, latch } = latchedJackpotProcessingStage({
      day,
      milestones: this.#jackpotProcessingMilestones(day),
      latch: this.#jpProgressLatch,
    });
    this.#jpProgressLatch = latch;
    return stage;
  }

  #presentJackpotProcessingStage(target) {
    const out = jackpotProcessingPresentationStep({
      target,
      state: this.#jpPresentationState,
      day: this.#selectedDay,
    });
    this.#jpPresentationState = out.state;
    if (out.pending && this.#jpPresentationTimer == null && typeof setTimeout === 'function') {
      this.#jpPresentationTimer = setTimeout(() => {
        this.#jpPresentationTimer = null;
        this.#syncSpinControlState();
      }, Math.max(1, Number(out.delay) || 1));
      try { this.#jpPresentationTimer?.unref?.(); } catch { /* browser timer */ }
    } else if (!out.pending && this.#jpPresentationTimer != null) {
      try { clearTimeout(this.#jpPresentationTimer); } catch { /* defensive */ }
      this.#jpPresentationTimer = null;
    }
    return out;
  }

  #syncSpinControlState() {
    // Disarmed by default, re-armed only by the processing branch below. Every
    // other path through this method — spinning, decimator, day summary, spin
    // ready, coinflip handoff — is a state where the key has its own live
    // action, and the crank must not be able to eat that press.
    this.#mineFlipArmed = false;
    const btn = this.querySelector?.('[data-bind="reveal-btn"]');
    if (!btn || this.hasAttribute('data-degenesis')) return;
    this.#syncCoinDrawCentre();
    this.#publishCoinDrawDue();
    btn.classList?.remove('is-data-unavailable');
    // Cleared here for the same reason as #mineFlipArmed above: only the
    // processing branch may set it, so no other path can inherit a stale
    // pickaxe from a state the key has already left.
    btn.removeAttribute?.('data-jp-action');
    // `data-jp-stage="rng"` begins at the day boundary so the LCD can describe
    // what the pipeline is waiting for. It is NOT proof that a request exists.
    // Keep that proof on its own short-lived hook; the Chainlink instrument and
    // its current layer use it to remain dormant until the exact-day watcher has
    // seen the request (or a later state that proves it happened).
    btn.removeAttribute?.('data-jp-rng-requested');
    const decimator = this.#primaryDecimatorDue() ? this.#primaryDecimatorAction : null;
    if (decimator) this.setAttribute?.('data-primary-action', 'decimator');
    else this.removeAttribute?.('data-primary-action');
    // Contract/indexer polling continues while the reels animate. Those
    // refreshes own the processing labels, but they must never repaint an
    // already-running main or bonus action back to its idle CTA. The class is
    // set before the async trait reads begin, while #spinning covers the reel
    // loop itself, so together they close both sides of that race.
    if (this.#spinning || btn.classList?.contains('is-spinning')) {
      btn.disabled = true;
      btn.setAttribute?.('aria-busy', 'true');
      return;
    }
    if (decimator) {
      const busy = this.#primaryDecimatorBusy || decimator.state === 'busy';
      const ready = decimator.state === 'ready' && typeof decimator.run === 'function';
      btn.hidden = false;
      btn.classList?.add('is-decimator');
      btn.classList?.remove('is-bonus', 'is-coinflip');
      if (btn.dataset) btn.dataset.replayAction = 'decimator';
      btn.classList?.toggle('is-processing', busy);
      btn.disabled = busy || this.#spinning || !ready;
      btn.textContent = this.#primaryDecimatorError
        ? 'DECIMATOR RESULTS · TRY AGAIN'
        : busy
          ? (decimator.write === true ? 'RESOLVING DECIMATOR…' : 'LOADING DECIMATOR…')
          : (decimator.write === true ? 'RESOLVE + RUN DECIMATOR' : 'DECIMATOR RESULTS');
      btn.setAttribute?.('aria-label', btn.textContent);
      if (busy) btn.setAttribute?.('aria-busy', 'true');
      else btn.removeAttribute?.('aria-busy');
      btn.title = this.#primaryDecimatorError
        ? 'The results could not be loaded. Press to try again.'
        : 'Open the Decimator results';
      return;
    }
    btn.classList?.remove('is-decimator', 'is-coinflip');
    // DAY SUMMARY is another owner of this exact LCD face. On reload its
    // durable completion gates can resolve while the replay fetch is still
    // marked loading; give the already-valid summary precedence so a later
    // loading repaint cannot put two controls in the one hardware socket.
    const resultsCta = this.querySelector?.('.ldj-results-cta');
    // An unopened craps battle outranks it: the host holds DAY SUMMARY back while
    // data-craps-battle-due is set, and the battle step below claims the key.
    if (resultsCta && resultsCta.hidden === false && !this.#coinDrawStepDue()) {
      btn.hidden = true;
      btn.classList?.remove('is-processing', 'is-bonus');
      if (btn.dataset) delete btn.dataset.replayAction;
      btn.style?.removeProperty?.('--jp-progress');
      btn.removeAttribute?.('data-jp-stage');
      btn.removeAttribute?.('aria-busy');
      btn.removeAttribute?.('aria-label');
      return;
    }
    if (!this.#dayDataReady(this.#selectedDay) && (
      this.#readUnavailable('days')
      || this.#readUnavailable(`rolls:${this.#selectedDay}`)
    )) {
      btn.hidden = false;
      btn.disabled = true;
      btn.classList?.remove('is-processing', 'is-bonus');
      btn.classList?.add('is-data-unavailable');
      if (btn.dataset) btn.dataset.replayAction = 'unavailable';
      btn.style?.removeProperty?.('--jp-progress');
      btn.removeAttribute?.('data-jp-stage');
      btn.removeAttribute?.('aria-busy');
      btn.textContent = 'RECONNECTING…';
      btn.title = 'Jackpot results are temporarily unavailable. Retrying automatically.';
      btn.setAttribute?.('aria-label', btn.title);
      return;
    }
    const sourceProcessing = this.hasAttribute('data-day-warming')
      || this.hasAttribute('data-day-loading');
    let stage = null;
    let presentationPending = false;
    if (sourceProcessing || this.#jpPresentationArmed) {
      const target = this.#jackpotProcessingStage();
      if (this.#jpPresentationArmed) {
        const presentation = this.#presentJackpotProcessingStage(target);
        stage = presentation.stage;
        presentationPending = presentation.pending;
        if (!presentationPending && stage.key === 'ready') {
          this.#jpPresentationArmed = false;
        }
      } else {
        stage = target;
      }
    }
    const exactDayReady = this.#dayDataReady(this.#selectedDay);
    const control = jackpotSpinControlState({
      sourceProcessing,
      presentationPending,
      presentationArmed: this.#jpPresentationArmed,
      stage,
      dayReady: exactDayReady,
    });
    stage = control.stage;
    const processing = control.processing;
    btn.classList?.toggle('is-processing', processing);
    if (processing) {
      // The processing LCD and a scratched result can never describe the same
      // selected day. A late persisted restore used to win this render race at
      // the Mine FLIP step, replacing the live attract reel with yesterday's
      // pink NO HIT board until the exact roll payload arrived. Retire that
      // stale result synchronously before painting the processing action.
      if (sourceProcessing) this.#keepProcessingReelLive();
      btn.hidden = false;
      stage ||= this.#jackpotProcessingStage();
      const rngRequestObserved = this.#jpProcessingSignals?.active === true
        && Number(this.#jpProcessingSignals?.day) === Number(this.#selectedDay)
        && this.#jpProcessingSignals?.requested === true;
      if (rngRequestObserved) {
        btn.setAttribute?.('data-jp-rng-requested', 'true');
      }
      // The one thing a player can DO while this window is open is turn the
      // crank. Availability is the resolver's, not ours: it publishes the row
      // only when a simulated mineRun actually succeeds, so an armed key means
      // the chain really does have pending work for this wallet right now. An
      // in-flight call disarms it, which is what stops a double-fire; the
      // resolver re-probes and republishes after each receipt, and that
      // re-arms the key for the next call.
      const mineFlipKeyActive = this.#mineFlipKeyPhaseActive();
      this.#mineFlipArmed = mineFlipKeyActive && this.#mineFlipCallable();
      if (btn.dataset) {
        btn.dataset.replayAction = this.#mineFlipArmed ? 'mine-flip' : 'processing';
      }
      btn.disabled = !this.#mineFlipArmed;
      // The LABEL follows the resolver-owned PHASE, not the momentary armed
      // flag or a second RNG witness: a press
      // disarms the key for the length of its transaction, and the key must
      // keep saying what it is doing across that whole window. With no crank
      // published at all it still falls back to the stage, so a wallet-less or
      // finished board never advertises an action nobody can take.
      const crankLabel = mineFlipKeyActive;
      btn.textContent = crankLabel
        ? (this.#mineFlipBusy ? MINE_FLIP_MINING_LABEL : MINE_FLIP_CRANK_LABEL)
        : stage.label;
      // The LCD's indicator glyph is keyed off data-jp-stage, so while the key
      // is naming the crank it would still be showing the pipeline stage's own
      // mark — the coinflip stage paints a full-colour ETH coin face there.
      // This attribute is the hook that swaps in the pickaxe for exactly this
      // state; every other state keeps whatever its stage puts there.
      if (crankLabel) btn.setAttribute?.('data-jp-action', 'mine-flip');
      // The fill and the flame cadence are both driven off this one number, so
      // there is exactly one place where progress can disagree with itself.
      btn.style?.setProperty?.('--jp-progress', String(stage.progress));
      btn.setAttribute?.('data-jp-stage', stage.key);
      btn.setAttribute?.('aria-busy', 'true');
      btn.setAttribute?.(
        'aria-label',
        crankLabel
          ? `${btn.textContent}. ${stage.label}. Step ${stage.done} of ${stage.total}.`
          : `${stage.label}. Step ${stage.done} of ${stage.total}.`,
      );
      btn.title = stage.label;
      return;
    }
    btn.style?.removeProperty?.('--jp-progress');
    btn.removeAttribute?.('data-jp-stage');
    btn.removeAttribute?.('aria-busy');
    btn.removeAttribute?.('aria-label');
    btn.classList?.remove('is-craps');
    // A stopped reel still has concealed outcomes. Keep the shared key on the
    // same face for wins and owned misses until every possible-win cover is
    // open, before handing it to Craps, the coin, or maintenance.
    if (this.#singleButton() && this.#finalDrawRevealPending()) {
      btn.hidden = false;
      btn.classList?.remove('is-bonus', 'is-coinflip');
      if (btn.dataset) btn.dataset.replayAction = 'reveal-results';
      btn.disabled = true;
      btn.textContent = 'REVEAL RESULTS';
      btn.title = 'Pop the blue panels and covered center to continue';
      return;
    }
    // A purchase day's craps battle comes after every spin the day has (a level-0 purchase day
    // also has a Bonus Spin) and before the Community Coinflip, until the viewer has opened it.
    const crapsBattle = this.#coinDrawStepDue();
    if (crapsBattle) {
      btn.hidden = false;
      btn.classList?.remove('is-bonus', 'is-coinflip');
      btn.classList?.add('is-craps');
      if (btn.dataset) btn.dataset.replayAction = 'craps-battle';
      btn.disabled = this.#coinDrawOpening;
      btn.textContent = this.#coinDrawOpening ? 'LOADING BATTLE…'
        : this.#coinDrawStatus?.error ? 'RETRY CRAPS BATTLE' : CRAPS_BATTLE_LABEL;
      btn.title = this.#coinDrawStatus?.error ? this.#coinDrawStatus.message : crapsBattle.ariaLabel;
      btn.setAttribute?.('aria-label', crapsBattle.ariaLabel);
      return;
    }
    if (this.#coinflipHandoffReady()) {
      btn.hidden = false;
      btn.classList?.remove('is-bonus');
      btn.classList?.add('is-coinflip');
      if (btn.dataset) btn.dataset.replayAction = 'coinflip';
      btn.disabled = this.#coinflipHandoffStarting;
      btn.textContent = this.#coinflipHandoffStarting ? 'COIN FLIPPING…' : COINFLIP_LABEL;
      btn.title = this.#coinflipHandoffStarting
        ? 'The Community Coinflip is playing'
        : 'Reveal the Community Coinflip';
      btn.setAttribute?.('aria-label', btn.textContent);
      return;
    }
    if (this.#singleButton() && this.#jackpotSpinsComplete()) {
      // Once the player's draw actions are exhausted this LCD would otherwise
      // be an empty hardware socket. Let the resolver-owned permissionless
      // crank occupy that fallback, but only after Decimator, Day Summary,
      // processing state, and the Community Coinflip have all had priority.
      if (this.#paintMineFlipFallback(btn)) return;
      btn.hidden = true;
      if (btn.dataset) delete btn.dataset.replayAction;
      return;
    }
    btn.hidden = false;
    if (btn.dataset) btn.dataset.replayAction = this.#btnMode;
    btn.classList?.toggle('is-bonus', this.#btnMode === 'bonus');
    if (this.#btnMode === 'bonus') {
      const ready = this.#mainReadyForBonus();
      btn.textContent = ready ? BONUS_SPIN_LABEL : BONUS_SPIN_LOCKED_LABEL;
      btn.disabled = !ready || !this.#dayDataReady(this.#selectedDay);
      btn.title = ready ? '' : 'Pop the blue panels first';
    } else {
      btn.textContent = MAIN_SPIN_LABEL;
      btn.disabled = !exactDayReady;
      btn.title = '';
    }
  }

  #setPrimaryDecimatorAction(items) {
    const next = (Array.isArray(items) ? items : []).find((item) => (
      item?.kind === 'decimator' && item?.primarySurface === 'jackpot'
    )) || null;
    const btn = this.querySelector?.('[data-bind="reveal-btn"]');

    if (next && !this.#primaryDecimatorAction && btn) {
      this.#revealStateBeforeDecimator = {
        hidden: Boolean(btn.hidden),
        disabled: Boolean(btn.disabled),
        textContent: btn.textContent,
        title: btn.title || '',
        ariaBusy: btn.getAttribute?.('aria-busy'),
        ariaLabel: btn.getAttribute?.('aria-label'),
        isBonus: Boolean(btn.classList?.contains('is-bonus')),
        isProcessing: Boolean(btn.classList?.contains('is-processing')),
        isSpinning: Boolean(btn.classList?.contains('is-spinning')),
        jpProgress: btn.style?.getPropertyValue?.('--jp-progress') || '',
        jpStage: btn.getAttribute?.('data-jp-stage'),
      };
    }

    const hadAction = Boolean(this.#primaryDecimatorAction);
    this.#primaryDecimatorAction = next;
    if (next) {
      this.#primaryDecimatorError = '';
      this.#syncSpinControlState();
      return;
    }
    if (!hadAction) return;

    this.removeAttribute?.('data-primary-action');
    this.#primaryDecimatorBusy = false;
    this.#primaryDecimatorError = '';
    if (btn && this.#revealStateBeforeDecimator) {
      const saved = this.#revealStateBeforeDecimator;
      const resultsCta = this.querySelector?.('.ldj-results-cta');
      btn.hidden = resultsCta && resultsCta.hidden === false ? true : saved.hidden;
      btn.disabled = saved.disabled;
      btn.textContent = saved.textContent;
      btn.title = saved.title;
      btn.classList?.remove('is-decimator');
      btn.classList?.toggle('is-bonus', saved.isBonus);
      btn.classList?.toggle('is-processing', saved.isProcessing);
      // Only re-apply the spin latch over a reel that is still turning. The
      // snapshot can outlive its spin, and #syncSpinControlState() refuses to
      // repaint a button carrying `is-spinning`, so restoring a stale one
      // locks the control for the rest of the day. A latch dropped a beat
      // early is cosmetic; the owning flow relabels when it lands.
      btn.classList?.toggle('is-spinning', saved.isSpinning && this.#spinning);
      if (saved.jpProgress) btn.style?.setProperty?.('--jp-progress', saved.jpProgress);
      else btn.style?.removeProperty?.('--jp-progress');
      if (saved.jpStage == null) btn.removeAttribute?.('data-jp-stage');
      else btn.setAttribute?.('data-jp-stage', saved.jpStage);
      if (saved.ariaBusy == null) btn.removeAttribute?.('aria-busy');
      else btn.setAttribute?.('aria-busy', saved.ariaBusy);
      if (saved.ariaLabel == null) btn.removeAttribute?.('aria-label');
      else btn.setAttribute?.('aria-label', saved.ariaLabel);
    }
    this.#revealStateBeforeDecimator = null;
    // Recompute the next action from the completed jackpot and current day
    // instead of restoring the button's old loading or spin label.
    this.#syncSpinControlState();
  }

  /**
   * Mirror the resolver's published Mine FLIP row. Nothing about availability
   * is decided here: app-mine-flip.js publishes the row only when a simulated
   * `mineRun()` succeeds for the connected wallet, and clears it when another
   * keeper wins the race. Reading that row is how this key stays honest.
   */
  #setMineFlipAction(items) {
    const next = (Array.isArray(items) ? items : []).find((item) => (
      typeof item?.id === 'string' && item.id.startsWith('mine-flip:')
    )) || null;
    const had = Boolean(this.#mineFlipAction);
    this.#mineFlipAction = next;
    if (next || had) this.#syncSpinControlState();
  }

  /** Test-only seam: the exact payload the pending-actions subscription hands us. */
  __setPendingActionsForTest(items) {
    this.#setPrimaryDecimatorAction(items);
    this.#setMineFlipAction(items);
  }

  /** Test-only seam: the press the armed LCD key performs. */
  __triggerMineFlipForTest() {
    return this.#triggerMineFlip();
  }

  /** Test-only seam: exercise one complete scratch-audio gesture. */
  __cycleScratchAudioForTest() {
    this.#sfxScratchStart();
    this.#sfxScratchStop();
  }

  /** Test-only seam: model the ordinary audio-frame load of consecutive spins. */
  __playSpinFrameSfxForTest(frameCount = 1) {
    const count = Math.max(0, Math.min(200, Math.trunc(Number(frameCount) || 0)));
    for (let frame = 0; frame < count; frame++) {
      this.#sfxSpinFrame(frame % 5, frame % 13 === 0, frame % 9);
    }
  }

  /**
   * Test-only seam: pin the day the key is reporting on, without the network
   * load `#loadDay` would otherwise perform to set it.
   */
  __setSelectedDayForTest(day) {
    const target = Number(day);
    this.#selectedDay = Number.isInteger(target) && target > 0 ? target : null;
  }

  /** Test-only seam for the post-spin empty-LCD action priority. */
  __setCompletedSpinsForTest({ hasBonus = false, bonusComplete = true, revealed = true } = {}) {
    this.#hasBonus = Boolean(hasBonus);
    this.#mainSpinComplete = true;
    this.#bonusSpinComplete = !this.#hasBonus || Boolean(bonusComplete);
    this.#mainPotentialScratchComplete = Boolean(revealed);
    this.#bonusScratchComplete = Boolean(revealed);
    this.#btnMode = 'reveal';
    this.#syncSpinControlState();
  }

  /**
   * Lowest-priority owner of the shared LCD: permissionless maintenance after
   * every player-facing draw action has vacated it.
   */
  #paintMineFlipFallback(btn) {
    if (!btn || !this.#mineFlipPhaseActive()) return false;
    this.#mineFlipArmed = this.#mineFlipCallable();
    btn.hidden = false;
    btn.classList?.remove('is-bonus', 'is-coinflip');
    btn.classList?.add('is-processing');
    if (btn.dataset) {
      btn.dataset.replayAction = this.#mineFlipArmed ? 'mine-flip' : 'processing';
    }
    btn.disabled = !this.#mineFlipArmed;
    btn.textContent = this.#mineFlipBusy ? MINE_FLIP_MINING_LABEL : MINE_FLIP_CRANK_LABEL;
    btn.title = 'Process pending jackpot work';
    btn.setAttribute?.('data-jp-action', 'mine-flip');
    btn.setAttribute?.('aria-label', btn.textContent);
    if (this.#mineFlipBusy) btn.setAttribute?.('aria-busy', 'true');
    else btn.removeAttribute?.('aria-busy');
    return true;
  }

  /**
   * Does the resolver-owned crank belong on this selected-day LCD?
   *
   * The resolver row is authoritative for whether Mine FLIP work exists, but
   * it is global and can briefly outlive this panel's exact-day processing
   * context. With live context present, keep both the label and click route
   * scoped to the selected active unresolved day. A warming panel without the
   * live feed yet may still trust the resolver row; its click re-probes before
   * opening the wallet.
   */
  #mineFlipKeyPhaseActive() {
    if (!this.#mineFlipPhaseActive()) return false;
    const live = this.#jpProcessingSignals;
    if (!live) return true;
    const target = Number(this.#selectedDay);
    return Number.isInteger(target)
      && target > 0
      && Number(live.day) === target
      && live.active === true
      && live.jackpotReady !== true;
  }

  /**
   * Is the crank the thing this key is FOR right now?
   *
   * Deliberately not the same question as #mineFlipCallable. Callability is
   * momentary — it drops the instant a press puts a transaction in flight, and
   * the resolver itself republishes the row as `state: 'busy'` while it runs —
   * so a label driven off it flipped back to the pipeline stage the moment the
   * player pressed the key, which is exactly when they most needed it to still
   * say what they had just started.
   *
   * The row EXISTING is the resolver's statement that this wallet has crank
   * work; its `state` only says whether it can be pressed this instant. So the
   * phase runs from the first published row until the resolver clears it —
   * which it does when the chain reports no work left, or the wallet goes
   * away. That makes the label a function of the phase: press, in flight,
   * confirm, re-arm and press again all sit inside it, and it ends only when
   * the processing branch yields to results/another action or the resolver
   * reports that mining is genuinely done.
   */
  #mineFlipPhaseActive() {
    return Boolean(this.#mineFlipAction) || this.#mineFlipBusy;
  }

  /** Callable right now: a ready row, a real runner, and nothing in flight. */
  #mineFlipCallable() {
    const action = this.#mineFlipAction;
    return Boolean(
      action
      && action.state === 'ready'
      && typeof action.run === 'function'
      && !this.#mineFlipBusy
      && !this.#spinning,
    );
  }

  /**
   * One deliberate press, one crank call. The resolver's own `run` re-probes at
   * intent time, opens the wallet, and refreshes the day feeds on a receipt, so
   * there is no transaction logic here and no loop: the player presses again
   * when the key re-arms.
   */
  async #triggerMineFlip() {
    if (!this.#mineFlipCallable()) return false;
    const action = this.#mineFlipAction;
    this.#mineFlipBusy = true;
    this.#syncSpinControlState();
    try {
      await action.run();
      return true;
    } catch (error) {
      // The tray owns the visible error surface for this action; this key just
      // stops claiming to be armed.
      console.warn('[replay-panel] Mine FLIP crank failed', error);
      return false;
    } finally {
      this.#mineFlipBusy = false;
      this.#syncSpinControlState();
    }
  }

  async #triggerPrimaryDecimator() {
    const action = this.#primaryDecimatorAction;
    if (!this.#primaryDecimatorDue() || this.#primaryDecimatorBusy || this.#spinning
      || action.state !== 'ready' || typeof action.run !== 'function') return false;
    this.#primaryDecimatorBusy = true;
    this.#primaryDecimatorError = '';
    this.#syncSpinControlState();
    try {
      await action.run();
      return true;
    } catch (error) {
      console.warn('[replay-panel] Decimator draw failed', error);
      this.#primaryDecimatorError = 'retry';
      return false;
    } finally {
      this.#primaryDecimatorBusy = false;
      if (this.#primaryDecimatorAction) this.#syncSpinControlState();
    }
  }

  #scheduleDayDataReload(day) {
    const target = Number(day);
    if (!Number.isInteger(target) || target <= 0 || this.#dayReloadTimer != null) return;
    if (this.#dayReloadTarget !== target) {
      this.#dayReloadTarget = target;
      this.#dayReloadAttempt = 0;
    }
    const delay = dayDataRetryDelay(this.#dayReloadAttempt);
    this.#dayReloadAttempt += 1;
    this.#dayReloadTimer = setTimeout(async () => {
      this.#dayReloadTimer = null;
      if (Number(this.#selectedDay) !== target || this.#dayDataReady(target)) return;
      const expectedLoadSeq = this.#dayLoadSeq;
      // Refresh the RNG metadata as well as the roll endpoints. The day option
      // itself can arrive before its packed winning traits on an indexing edge.
      try { await this.refreshDays({ force: true }); } catch { /* retry rolls anyway */ }
      if (this.#dayLoadSeq !== expectedLoadSeq
        || Number(this.#selectedDay) !== target
        || this.#dayDataReady(target)) return;
      const select = this.querySelector('[data-bind="day-select"]');
      if (!select) return;
      select.value = String(target);
      void this.#onDayChange({ target: select, retry: true });
    }, delay);
    try { this.#dayReloadTimer?.unref?.(); } catch { /* browser timer */ }
  }

  /**
   * App-shell hook. `cleared` comes from the chain/day-scoped spun_day key.
   * Beta/play consumers never call this, so their replay behaviour is unchanged.
   */
  setPersistedRevealState(cleared, allRollsCleared = false) {
    if (this.hasAttribute('data-degenesis')) return;
    if (!this.#singleButton()) return;
    const nextCleared = Boolean(cleared);
    const nextAllRollsCleared = Boolean(nextCleared && allRollsCleared);
    const requestKey = this.#persistedRevealKey(nextCleared, nextAllRollsCleared);
    // Routine same-day polling must not rebuild the visible draw. In
    // particular, #startIdleSpin() resets every quadrant before painting its
    // next frame, which looked like a random mid-day jackpot reset.
    if (this.#hostRevealRequestKey === requestKey) return;
    this.#hostRevealCleared = nextCleared;
    this.#hostAllRollsCleared = nextAllRollsCleared;
    this.#hostRevealRequestKey = requestKey;
    this.#hostRevealSeq += 1;
    // A polling/bridge update for the draw the player is actively uncovering
    // must not put the attract reel over that live board. This guard has to run
    // before the eager idle paint below, not only before the async restore.
    if (this.#selectionKey() === this.#interactiveRevealKey) return;
    // The host can learn that a brand-new day is uncleared before the replay
    // endpoints finish loading it. Start the quiet attract reels immediately;
    // #applyPersistedRevealState restarts them with the exact level holdings
    // once that asynchronous selection is fully pinned.
    if (!nextCleared && !this.#spinning) {
      this.#startIdleSpin();
    }
    void this.#applyPersistedRevealState(this.#hostRevealSeq);
  }

  #selectionKey() {
    if (this.#selectedDay == null || !this.#selectedPlayer) return null;
    return `${Number(this.#selectedDay)}|${String(this.#selectedPlayer).toLowerCase()}`;
  }

  #interactiveSelectionActive(selectionKey = this.#selectionKey()) {
    return selectionKey != null && selectionKey === this.#interactiveRevealKey;
  }

  #claimInteractiveReveal(selectionKey) {
    if (selectionKey == null) return;
    this.#interactiveRevealKey = selectionKey;
    // Invalidate a persisted-state restore that began before the press. Its
    // network read may finish after the real reel starts; that older task must
    // not put the attract reel or an instant restored board over the player's
    // live result.
    this.#hostRevealSeq += 1;
  }

  #persistedRevealKey(cleared, allRollsCleared) {
    const selection = this.#selectionKey() || 'selection-pending';
    return `${selection}|${cleared ? 'cleared' : 'waiting'}`
      + `|${allRollsCleared ? 'all-rolls' : 'main-only'}`;
  }

  async #applyPersistedRevealState(seq = this.#hostRevealSeq) {
    if (this.#hostRevealCleared == null
      || this.#selectedDay == null
      || !this.#selectedPlayer
      || this.#loadedDay !== this.#selectedDay) return false;
    if (this.#interactiveSelectionActive()) return false;

    const rngEntry = this.#rngDays.find(d => d.day === this.#selectedDay);
    if (!rngEntry || !rngEntry.finalWord || rngEntry.finalWord === '0') {
      return false;
    }

    const day = this.#selectedDay;
    const player = this.#selectedPlayer;
    const cleared = this.#hostRevealCleared;
    const key = this.#persistedRevealKey(cleared, this.#hostAllRollsCleared);
    // Day/player changes can invoke this method directly after their data
    // finishes loading. Record that request too, so the next host poll cannot
    // eagerly repaint while this restore is in flight.
    this.#hostRevealRequestKey = key;
    if (this.#hostRevealAppliedKey === key) return true;

    if (cleared) {
      this.#stopIdleSpin();
      const restored = await this.#triggerReveal({ instant: true, persisted: true });
      if (!restored) return false;
      if (seq !== this.#hostRevealSeq
        || day !== this.#selectedDay
        || player !== this.#selectedPlayer) return false;
      this.#restoreCompletedDrawViews();
    } else {
      await this.#loadPlayerTraits();
      if (seq !== this.#hostRevealSeq
        || day !== this.#selectedDay
        || player !== this.#selectedPlayer
        || this.#hostRevealCleared !== false
        || this.#interactiveSelectionActive()) return false;
      this.#startIdleSpin();
    }

    if (seq !== this.#hostRevealSeq
      || day !== this.#selectedDay
      || player !== this.#selectedPlayer) return false;
    this.#hostRevealAppliedKey = key;
    return true;
  }

  /**
   * A completed two-roll day always reloads onto the cleared main draw. The
   * bonus result remains available through the center flame, but its purchase-
   * style button must not come back after refresh.
   */
  #restoreCompletedDrawViews() {
    if (!this.#hostAllRollsCleared || !this.#hasBonus) return;
    this.#bonusPhase = false;
    this.#mainScratchComplete = true;
    this.#mainPotentialScratchComplete = true;
    this.#bonusScratchComplete = true;
    this.#mainSpinComplete = true;
    this.#bonusSpinComplete = true;
    this.#btnMode = 'reveal';
    const btn = this.querySelector('[data-bind="reveal-btn"]');
    if (btn) {
      btn.hidden = true;
      btn.disabled = true;
      btn.title = '';
    }
    const bonusSection = this.querySelector('[data-bind="bonus-section"]');
    if (bonusSection) bonusSection.hidden = true;
    this.#syncDrawToggleAffordance();
    this.#syncSpinControlState();
  }

  #watchIdleVisibility() {
    this.#idleVisibilityCleanup?.();
    this.#idleInViewport = true;
    let active = true;
    const refresh = () => {
      if (!active) return;
      if (this.#idleSpinWanted()) this.#startIdleSpin();
      else this.#stopIdleSpin();
    };
    // Observe the stable ticket, not an image that changes size as badges swap.
    const ticket = this.querySelector('[data-bind="card-grid"]');
    const observer = ticket && typeof IntersectionObserver === 'function'
      ? new IntersectionObserver((records) => {
          if (!active) return;
          for (const record of records) {
            if (record.target === ticket) this.#idleInViewport = record.isIntersecting;
          }
          refresh();
        }, { rootMargin: '50px' })
      : null;
    observer?.observe(ticket);
    document.addEventListener('visibilitychange', refresh);
    this.#idleVisibilityCleanup = () => {
      active = false;
      observer?.disconnect();
      document.removeEventListener('visibilitychange', refresh);
    };
  }

  #idleSpinWanted() {
    if (this.hasAttribute('data-degenesis')) return false;
    return replayAttractShouldRun({
      revealCleared: this.#hostRevealCleared,
      dayLoading: this.hasAttribute('data-day-loading'),
      dayWarming: this.hasAttribute('data-day-warming'),
      spinning: this.#spinning,
      interactiveReveal: this.#interactiveSelectionActive(),
      inViewport: this.#idleInViewport,
      tabVisible: typeof document === 'undefined' || document.visibilityState !== 'hidden',
    });
  }

  #keepProcessingReelLive() {
    if (this.#spinning || !this.#idleSpinWanted()) return;
    const processingDay = Number(this.#jpProcessingSignals?.day);
    const selectedDay = Number(this.#selectedDay);
    if (!Number.isInteger(processingDay) || processingDay <= 0
      || processingDay !== selectedDay) return;

    const staleResult = Array.from(this.querySelectorAll('.replay-tq')).some((quad) => (
      quad.classList?.contains('q-result-revealed')
      || quad.classList?.contains('q-public-result')
      || quad.classList?.contains('q-scratch-underlay')
      || quad.classList?.contains('q-result-pending')
      || quad.querySelector?.('.replay-prize-reveal')?.classList?.contains('visible')
    ));
    if (!staleResult) return;

    // Force one clean restart even if the attract timer itself survived. The
    // ordinary #startIdleSpin guard intentionally treats a running timer as a
    // no-op; here the timer is alive but its visible layers were overwritten.
    this.#stopIdleSpin();
    this.#startIdleSpin();
  }

  #startIdleSpin() {
    const lightweight = readLightweightModePreference();
    const decorationKey = `${this.#selectedDay}:${this.#selectedPlayer}`;
    if (lightweight && this.#idleDecorationKey === decorationKey
      && this.querySelector('.q-decorative') && this.#idleSpinWanted()) return;
    // Starting an attract reel that is already running is a no-op. This is the
    // final safety boundary around the visible ticket: even if a future host
    // refresh produces a different request key, it cannot clear four painted
    // badges merely to restart the same timer loop.
    if (this.#idleSpinTimer != null && this.#idleSpinWanted()) return;
    this.#stopIdleSpin();
    if (!this.#idleSpinWanted()) return;
    if (this.querySelectorAll('.replay-tq').length < 4) return;

    // Always enter the attract loop from one clean main-draw face. A previous
    // bonus scratch can leave transparent canvases, revealed prize paper, and
    // result classes mounted; random badge swaps on top of those layers produce
    // the mixed front/underside frame reported during bonus clearing.
    this.#bonusPhase = false;
    this.#mainScratchComplete = false;
    this.#mainPotentialScratchComplete = false;
    this.#bonusScratchComplete = false;
    this.#drawViewSwitching = false;
    this.#resetMainWidget();
    this.#syncDrawToggleAffordance();

    const tick = () => {
      this.#idleSpinTimer = null;
      if (!this.#idleSpinWanted()) {
        this.querySelector('[data-bind="center"]')?.classList.remove('spinning');
        return;
      }
      const quads = this.querySelectorAll('.replay-tq');
      for (let i = 0; i < 4; i++) {
        const contractQ = DISPLAY_ORDER[i];
        const { sym, col } = previewSpinTrait(contractQ,
          Math.floor(Math.random() * 8), Math.floor(Math.random() * 8));
        const category = BADGE_QUADRANTS[contractQ];
        const img = quads[i]?.querySelector('.badge-img');
        if (img) {
          img.src = badgeCircularPath(category, sym, col);
          img.style.display = '';
          img.style.opacity = '1';
        }
        quads[i]?.classList.remove(
          'q-decorative',
          'q-has-trait', 'q-no-tickets', 'q-traits-pending', 'q-scratchable', 'q-has-tickets',
          'q-public-result', 'q-win-impossible', 'q-win-impossible-lock',
          'q-owned-miss', 'q-player-win', 'q-solo-eth-win', 'q-craps-comp-win',
          'q-gold-trait', 'q-scratch-underlay', 'q-result-pending', 'q-result-revealed',
        );
        const shownTrait = contractQ * 64 + col * 8 + sym;
        const ownsShown = !this.#playerTraitsPending && this.#playerTraitIds.has(shownTrait);
        quads[i]?.classList.add(
          lightweight ? 'q-decorative' : this.#playerTraitsPending
            ? 'q-traits-pending'
            : ownsShown ? 'q-has-trait' : 'q-no-tickets',
        );
        if (!lightweight && !this.#bonusPhase && !this.#playerTraitsPending && ownsShown && col === 7) {
          quads[i]?.classList.add('q-gold-trait');
        }
      }
      if (lightweight) {
        this.#idleDecorationKey = decorationKey;
        this.querySelector('[data-bind="center"]')?.classList.remove('spinning');
        return;
      }
      this.#syncOwnedGoldState(quads);
      this.querySelector('[data-bind="center"]')?.classList.add('spinning');

      // Deliberately much slower than the reveal animation. This is a quiet
      // attract loop, not a second outcome animation.
      this.#idleSpinTimer = setTimeout(tick, 620);
      if (this.#idleSpinTimer && typeof this.#idleSpinTimer.unref === 'function') {
        this.#idleSpinTimer.unref();
      }
    };
    tick();
  }

  #stopIdleSpin() {
    if (this.#idleSpinTimer != null) {
      try { clearTimeout(this.#idleSpinTimer); } catch { /* defensive */ }
      this.#idleSpinTimer = null;
    }
    if (!this.#spinning) {
      this.querySelector('[data-bind="center"]')?.classList.remove('spinning');
    }
  }

  // Decode-warm all 256 badges so spin src-swaps render instantly. Awaits the
  // badge bundle FIRST — generating paths before it lands resolves them to
  // /badges-circular/ file URLs and re-creates the 256-request cold-load storm
  // this preloader used to be. Once the bundle is in, every path below is a
  // local blob URL and the whole warm costs zero network.
  async #preloadBadges() {
    if (readLightweightModePreference()) return;
    await warmBadgeStore();
    if (readLightweightModePreference()) return;
    if (typeof Image !== 'function') return;
    const BADGE_CATEGORIES = ['crypto', 'zodiac', 'cards', 'dice'];
    let paths = [];
    for (const cat of BADGE_CATEGORIES) {
      for (let sym = 0; sym < 8; sym++) {
        for (let col = 0; col < 8; col++) {
          if (readLightweightModePreference() || !this.isConnected) return;
          paths.push(badgeCircularPath(cat, sym, col));
          if (paths.length === 8) {
            await preloadBadgeImages(paths, this.#badgeCache, { concurrency: 8 });
            paths = [];
            // Blob creation and SVG decode both cost main-thread time. Yield
            // between small groups instead of minting all 256 URLs in one task.
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }
      }
    }
    // Live spins still await the same completion barrier for every badge.
    paths.push(GOLD_SIX_MONKEY_BADGE);
    await preloadBadgeImages(paths, this.#badgeCache, { concurrency: 8 });
  }

  // --- Data Loading ---

  #recordJackpotRead(key, error = null) {
    if (error?.name === 'AbortError') return;
    if (jackpotReadIsUnavailable(error)) {
      this.#unavailableReads.add(key);
      if (!this.#unavailableSince.has(key)) this.#unavailableSince.set(key, Date.now());
    } else {
      this.#unavailableReads.delete(key);
      this.#unavailableSince.delete(key);
    }
    this.#syncSpinControlState();
  }

  /**
   * True once `key` has failed continuously past the grace period. Inside the
   * grace the button keeps its loading face, and one timer repaints it when
   * the grace runs out in case no retry lands first.
   */
  #readUnavailable(key) {
    if (!this.#unavailableReads.has(key)) return false;
    const remaining = (this.#unavailableSince.get(key) ?? 0) + JACKPOT_UNAVAILABLE_GRACE_MS - Date.now();
    if (remaining <= 0) return true;
    if (this.#unavailableGraceTimer == null && typeof setTimeout === 'function') {
      this.#unavailableGraceTimer = setTimeout(() => {
        this.#unavailableGraceTimer = null;
        this.#syncSpinControlState();
      }, remaining + 50);
    }
    return false;
  }

  /**
   * Refresh the day option source. The app's last-day bridge calls this when a
   * newly resolved day is not in the once-loaded list yet. Calls coalesce, and
   * a short throttle lets the bridge retry without hammering the replay API.
   * @returns {Promise<boolean>}
   */
  refreshDays({ force = false } = {}) {
    if (this.hasAttribute('data-degenesis')) return Promise.resolve(false);
    if (this.#tutorialFixture) return Promise.resolve(true);
    if (this.#daysRefreshPromise) return this.#daysRefreshPromise;
    const now = Date.now();
    // A 429 applies to this whole API origin. Forced rollover retries must not
    // bypass Retry-After, and even a cold/empty selector gets the ordinary
    // short throttle so the host's 500ms bridge cannot create a request loop.
    if (replayApiCooldownRemaining(now) > 0
      || (!force && now - this.#lastDaysRefreshAt < 2_000)) {
      return Promise.resolve(false);
    }
    this.#lastDaysRefreshAt = now;
    const pending = this.#loadDays();
    const tracked = pending.finally(() => {
      if (this.#daysRefreshPromise === tracked) this.#daysRefreshPromise = null;
    });
    this.#daysRefreshPromise = tracked;
    return this.#daysRefreshPromise;
  }

  async #loadDays(beforeDay = null) {
    const select = this.querySelector('[data-bind="day-select"]');
    try {
      const query = beforeDay ? '?beforeDay=' + beforeDay
        : CHAIN.readMode === 'chain' && this.#singleButton() ? '?limit=7' : '';
      const data = await replayFetch('/rng' + query, { cache: 'no-store' });
      if (this.#tutorialFixture) return true;
      // Read the selection AFTER the request. Rollover may have mounted today
      // while this request was fetching yesterday's list.
      // Rebuilding a <select> clears its value before the app shell can pin it
      // again. Preserve the logical selection as well as the DOM value: during
      // indexer catch-up the latter can already be empty even though this panel
      // is still displaying a perfectly valid day.
      const previous = String(Number(select?.value) > 0 ? select.value : this.#selectedDay || '');
      const previousOption = select?.options
        ? Array.from(select.options).find((option) => String(option.value) === previous)
        : null;
      this.#recordJackpotRead('days');
      const incoming = Array.isArray(data?.days) ? data.days : [];
      const days = beforeDay ? [...incoming, ...this.#rngDays].sort((a,b) => a.day-b.day) : incoming;
      const retained = this.#rngDays.find(row => String(row.day) === previous);
      if (retained && !days.some(row => String(row.day) === previous)) days.push(retained);
      this.#nextRngDay = data.nextDay ?? null;
      this.#rngDays = days;
      if (!select) return false;
      select.innerHTML = '<option value="">Pick a jackpot day</option>' +
        days.map(d => `<option value="${d.day}">Day ${d.day} — L${d.level} ${d.phase}${d.dayInPhase}</option>`).join('') +
        (this.#nextRngDay ? '<option value="older">Load older days…</option>' : '');
      if (previous) {
        let matching = Array.from(select.options || [])
          .find((option) => String(option.value) === previous);
        // A resolved day cannot legitimately disappear from the replay feed.
        // Keep its option through a transient partial response so the bridge
        // cannot manufacture an empty -> same-day change and blank the board.
        if (!matching && typeof document !== 'undefined'
          && typeof document.createElement === 'function') {
          matching = document.createElement('option');
          matching.value = previous;
          matching.textContent = previousOption?.textContent || `Day ${previous}`;
          matching.dataset.retainedSelection = 'true';
          select.appendChild(matching);
        }
        if (matching) select.value = matching.value;
      }
      // The app nav mirrors this selector. Notify it when an older page arrives.
      select.dispatchEvent(new CustomEvent('replay:days-loaded', { bubbles: true }));
      return true;
    } catch (err) {
      // Cache invalidation and tab/account lifecycle intentionally abort the
      // old replay read. Keep the last usable selector without reporting that
      // expected cancellation as a failed load.
      if (err?.name !== 'AbortError') {
        this.#recordJackpotRead('days', err);
        console.warn('[ReplayPanel] Failed to load days:', err);
      }
      // A network blip is not a day change. Keep the last usable options and
      // visible board; only show the failure placeholder on a true cold load.
      const hasUsableDay = select?.options
        && Array.from(select.options).some((option) => Number(option.value) > 0);
      if (select && !hasUsableDay) {
        select.innerHTML = '<option value="">Failed to load</option>';
      }
      return false;
    }
  }

  async #loadTickets(level) {
    // The single-player cabinet already knows the viewed wallet. A full
    // participant list is only needed by the explicit replay browser.
    if (this.#singleButton()) return;
    try {
      const data = await replayFetch(`/tickets/${level}${this.#selectedDay ? '?day=' + this.#selectedDay : ''}`);
      const players = Array.isArray(data?.players) ? data.players : [];
      this.#tickets = players;

      // Compute winnings per player from distributions (ETH vs FLIP)
      const ethByAddr = {};
      const flipByAddr = {};
      let ethCount = 0, flipCount = 0;
      for (const dist of this.#distributions) {
        const addr = dist.winner.toLowerCase();
        const t = dist.awardType || '';
        const isFlip = dist.currency === 'FLIP' || t === 'flip' || t === 'farFutureCoin';
        const isEth = t === 'eth';
        if (isFlip) {
          flipByAddr[addr] = (flipByAddr[addr] || 0n) + BigInt(dist.amount || '0');
        } else if (isEth) {
          ethByAddr[addr] = (ethByAddr[addr] || 0n) + BigInt(dist.amount || '0');
        }
      }

      const select = this.querySelector('[data-bind="player-select"]');
      const previous = String(this.#selectedPlayer || select?.value || '');
      const previousOption = select?.options
        ? Array.from(select.options).find(
            (option) => String(option.value).toLowerCase() === previous.toLowerCase(),
          )
        : null;
      select.innerHTML = '<option value="">' + (data.scope === 'prize-recipients' ? 'Prize recipients' : 'All players') + ' (' + players.length + ')</option>' +
        players.map(p => {
          const addr = p.address.toLowerCase();
          const eth = ethByAddr[addr];
          const flip = flipByAddr[addr];
          const parts = [];
          if (eth) parts.push(`${formatEth(eth.toString())} ETH`);
          if (flip) parts.push(`${formatFlip(flip.toString())} FLIP`);
          const wonLabel = parts.length > 0 ? ` | Won ${parts.join(' + ')}` : '';
          return `<option value="${p.address}">${truncateAddress(p.address)} (${p.entryCount == null ? 'prize recipient' : p.entryCount + ' entries'}${wonLabel})</option>`;
        }).join('');

      if (previous) {
        let matching = Array.from(select.options || []).find(
          (option) => String(option.value).toLowerCase() === previous.toLowerCase(),
        );
        // The app intentionally supports viewing a wallet with zero entries.
        // Retain that synthetic selection when the level's ticket list reloads
        // instead of briefly selecting "All players" and resetting the draw.
        if (!matching && typeof document !== 'undefined'
          && typeof document.createElement === 'function') {
          matching = document.createElement('option');
          matching.value = previous;
          matching.textContent = previousOption?.textContent
            || `${truncateAddress(previous)} (0 entries)`;
          matching.dataset.zeroEntry = 'true';
          select.appendChild(matching);
        }
        if (matching) select.value = matching.value;
      }

      this.#players = players.map(p => p.address);
    } catch (err) {
      console.warn('[ReplayPanel] Failed to load tickets:', err);
    }
  }

  // The host already fetched these authoritative results to announce readiness.
  // Share them with the replay rather than reconstructing the same day again.
  setResolvedDayPayload(payload) {
    this.#resolvedDayPayload = payload;
    rememberCompletedJackpot(payload);
    // A complete saved day must not wait for the history selector's RPC scan.
    const row = readCompletedJackpotRng(payload?.day);
    if (!row) return;
    if (!this.#rngDays.some(entry => Number(entry.day) === Number(row.day))) this.#rngDays.push(row);
    const select = this.querySelector('[data-bind="day-select"]');
    if (!select || Array.from(select.options).some(option => Number(option.value) === Number(row.day))) return;
    const option = document.createElement('option');
    option.value = String(row.day);
    option.textContent = `Day ${row.day} — L${row.level} ${row.phase}${row.dayInPhase}`;
    select.appendChild(option);
  }

  async #loadDayDetail(day) {
    const shared = reusableJackpotPayload(this.#resolvedDayPayload, day) ?? readCompletedJackpot(day);
    if (shared && shared.winners.every(winner => Array.isArray(winner.breakdown))) {
      return { day, distributions: shared.winners.flatMap(winner => winner.breakdown) };
    }
    try {
      const data = await replayFetch(`/day/${day}`, { cache: 'no-store' });
      return data;
    } catch (err) {
      console.warn('[ReplayPanel] Failed to load day detail:', err);
      return null;
    }
  }

  async #loadDayRolls(day) {
    const shared = reusableJackpotPayload(this.#resolvedDayPayload, day) ?? readCompletedJackpot(day);
    if (shared) {
      this.#recordJackpotRead(`rolls:${day}`);
      return { roll1: shared.roll1, roll2: shared.roll2 };
    }
    let roll1 = null;
    let roll2 = null;
    const [r1Res, r2Res] = await Promise.allSettled([
      fetchJSON(`/game/jackpot/day/${day}/roll1`, { force: true }),
      fetchJSON(`/game/jackpot/day/${day}/roll2`, { force: true }),
    ]);
    if (r1Res.status === 'fulfilled') roll1 = r1Res.value;
    else noteReplayApiResponse(r1Res.reason?.response);
    if (r2Res.status === 'fulfilled') roll2 = r2Res.value;
    else noteReplayApiResponse(r2Res.reason?.response);
    const failures = [r1Res, r2Res].filter((result) => result.status === 'rejected');
    const unavailable = failures.find((result) => jackpotReadIsUnavailable(result.reason));
    const aborted = failures.find((result) => result.reason?.name === 'AbortError');
    this.#recordJackpotRead(`rolls:${day}`, unavailable?.reason ?? aborted?.reason);
    if (roll1 && roll2) {
      if (this.#rollsMissingWarnedDay === day) this.#rollsMissingWarnedDay = null;
    } else if (this.#rollsMissingWarnedDay !== day) {
      this.#rollsMissingWarnedDay = day;
      const missing = [!roll1 && 'roll1', !roll2 && 'roll2'].filter(Boolean).join(' + ');
      console.warn(
        `[ReplayPanel] ${missing} not indexed yet for day ${day} — retrying quietly`,
      );
    }
    return { roll1, roll2 };
  }

  #isOpeningFlipDraw(day) {
    const row = this.#rngDays.find((entry) => Number(entry.day) === Number(day));
    // During game level 0 the replay clock correctly advertises the tickets on
    // sale (L1), so L1 purchase-phase rows are the opening double-FLIP path.
    return Number(row?.level) === 1 && String(row?.phase || '').toUpperCase() === 'P';
  }

  #packedTraits(packed) {
    if (packed == null) return [];
    const value = Number(packed) >>> 0;
    return [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff];
  }

  #repairOpeningFlipRolls(day) {
    this.#openingFlipDay = this.#isOpeningFlipDraw(day);
    if (!this.#openingFlipDay) return;
    const rng = this.#rngDays.find((entry) => Number(entry.day) === Number(day));
    const { mainWins, bonusWins } = splitOpeningFlipDraw(
      this.#distributions,
      this.#packedTraits(rng?.mainTraitsPacked),
      this.#packedTraits(rng?.bonusTraitsPacked),
    );
    this.#dayRoll1 = { day: Number(day), level: 0, purchaseLevel: 1, wins: mainWins };
    this.#dayRoll2 = { day: Number(day), level: 0, purchaseLevel: 1, wins: bonusWins };
  }

  #filterPlayerWins(addr) {
    const norm = addr.toLowerCase();
    this.#playerRoll1Wins = (this.#dayRoll1?.wins || [])
      .filter(w => String(w?.winner || '').toLowerCase() === norm);
    this.#playerRoll2Wins = (this.#dayRoll2?.wins || [])
      .filter(w => String(w?.winner || '').toLowerCase() === norm);
    // The bonus draw is public just like Roll 1. An exact Roll 2 payload is the
    // authoritative witness even when its valid public result has zero winners;
    // player holdings control only cover colour and personal payout.
    const hasResolvedRoll2 = Number(this.#dayRoll2?.day) === Number(this.#selectedDay)
      && Array.isArray(this.#dayRoll2?.wins);
    // A day with no bonus-trait draw (a run-56 purchase day, any run-57+ day) has nothing to spin:
    // its only roll-2 rows are the craps battle's, which #distributePrizesFromRoll1 lands in the main centre.
    this.#hasBonus = hasResolvedRoll2 && this.#dayBonusTraitDraw;
    this.#syncCoinDrawCentre();
  }

  // Pulled by #onDayChange/#onPlayerChange after fetching player.tickets.
  // True when the player owns >0 tickets at any level > the day's level
  // (near-future) OR any level the contract has not yet drawn (far-future,
  // already covered by the same > dayLevel comparison since draws are level-
  // ordered).
  async #refreshPlayerEligibility() {
    if (this.#tutorialFixture) {
      this.#playerHasFutureTickets = this.#tutorialFixture.futureTraits.size > 0;
      return;
    }
    this.#playerHasFutureTickets = null;
    if (!this.#selectedPlayer || !this.#selectedDay) return;
    const dayLevel = Number(this.#dayRoll1?.purchaseLevel ?? this.#selectedLevel);
    if (!Number.isFinite(dayLevel)) return;
    try {
      const data = await fetchJSON(`/player/${encodeURIComponent(this.#selectedPlayer)}?day=${encodeURIComponent(this.#selectedDay)}`);
      const tickets = Array.isArray(data?.tickets) ? data.tickets : [];
      this.#playerHasFutureTickets = tickets.some((t) => Number(t.level) > dayLevel && Number(t.entryCount ?? 0) > 0);
    } catch {
      this.#playerHasFutureTickets = null;
    }
  }

  async #loadDistributionsForLevel(level) {
    // Try new endpoint first (has traitId), fall back to history endpoint
    try {
      const data = await replayFetch('/distributions/' + level);
      this.#distributions = data.distributions || [];
      return;
    } catch {}
    try {
      const data = await fetchJSON('/history/jackpots?level=' + level + '&limit=100');
      this.#distributions = (data.items || []).map(d => ({
        level: d.level, winner: d.winner, amount: d.amount,
        traitId: d.traitId ?? null, ticketIndex: d.ticketIndex ?? null,
        awardType: d.awardType,
      }));
    } catch (err) {
      console.warn('[ReplayPanel] Failed to load distributions:', err);
    }
  }

  // Traits the player holds ABOVE the day's level — what Roll 2 grades against.
  // Bounded by FAR_FUTURE_HORIZON: levels past it have no rolled traits, so
  // fetching them would cost a request per level to learn nothing.
  //
  // Shared flight: the warm-up the main spin starts and the bonus press itself
  // await one hydration instead of racing two identical waves through the
  // transport's three-slot personalized lane. If the player reaches Bonus Spin
  // before that background warm finishes, attach interaction-priority consumers
  // to the same URL-keyed requests so queued levels are promoted, not duplicated.
  #promoteFutureTraitReads() {
    const dayLevel = Number(this.#dayRoll1?.purchaseLevel ?? this.#selectedLevel);
    if (!this.#selectedPlayer || !Number.isFinite(dayLevel)) return;
    for (let level = dayLevel + 1; level <= dayLevel + FAR_FUTURE_HORIZON; level += 1) {
      void fetchJSON(
        `/player/${encodeURIComponent(this.#selectedPlayer)}/tickets/by-trait?level=${level}`,
        { priority: 'interaction' },
      ).catch(() => null);
    }
  }

  #loadFutureTraits({ priority = 'background' } = {}) {
    if (readLightweightModePreference() && priority === 'background') return Promise.resolve();
    if (this.#futureTraitsInflight) {
      if (priority === 'interaction') {
        this.#futureOptional = false;
        this.#optionalTicketReads.delete(this.#futureReadController);
        this.#promoteFutureTraitReads();
      }
      return this.#futureTraitsInflight;
    }
    this.#futureOptional = priority === 'background';
    const flight = this.#hydrateFutureTraits(priority).finally(() => {
      if (this.#futureTraitsInflight === flight) this.#futureTraitsInflight = null;
    });
    this.#futureTraitsInflight = flight;
    return flight;
  }

  async #hydrateFutureTraits(priority = 'background') {
    if (readLightweightModePreference() && priority === 'background') return;
    if (this.#tutorialFixture) {
      this.#futureTraitIds = new Set(this.#tutorialFixture.futureTraits);
      this.#futureTraitsCacheKey = `${this.#tutorialFixture.player}|${this.#tutorialFixture.purchaseLevel}`;
      return;
    }
    const dayLevel = Number(this.#dayRoll1?.purchaseLevel ?? this.#selectedLevel);
    if (!this.#selectedPlayer || !Number.isFinite(dayLevel)) {
      this.#futureTraitIds = new Set();
      this.#futureTraitsCacheKey = null;
      return;
    }
    const selectedDay = this.#selectedDay;
    const cacheKey = `${this.#selectedPlayer.toLowerCase()}|${dayLevel}|${selectedDay}`;
    if (this.#futureTraitsCacheKey === cacheKey) return;
    const savedTraits = readJackpotTraits(this.#selectedDay, this.#selectedPlayer, dayLevel, 'future');
    if (savedTraits) {
      this.#futureTraitIds = savedTraits;
      this.#futureTraitsCacheKey = cacheKey;
      return;
    }

    const levels = [];
    for (let l = dayLevel + 1; l <= dayLevel + FAR_FUTURE_HORIZON; l++) levels.push(l);
    const owned = new Set();
    const controller = new AbortController();
    this.#futureReadController = controller;
    if (priority === 'background') this.#optionalTicketReads.add(controller);
    try {
      const payloads = await Promise.all(levels.map(async (l) => {
        // No &day= param — same 404 gotcha as #loadPlayerTraits.
        return fetchJSON(
          `/player/${encodeURIComponent(this.#selectedPlayer)}/tickets/by-trait?level=${l}`,
          { priority, signal: controller.signal },
        )
          .catch(() => null);
      }));
      if (controller.signal.aborted
        || cacheKey !== `${String(this.#selectedPlayer || '').toLowerCase()}|${Number(this.#dayRoll1?.purchaseLevel ?? this.#selectedLevel)}|${this.#selectedDay}`) return;
      for (const data of payloads) {
        for (const card of (Array.isArray(data?.cards) ? data.cards : [])) {
          for (const entry of (Array.isArray(card?.entries) ? card.entries : [])) {
            if (entry && entry.traitId != null) owned.add(Number(entry.traitId));
          }
        }
      }
      this.#futureTraitIds = owned;
      // Each level catches its own rejection, so a shed/cooldown burst that
      // failed every request still arrives here as an empty set. Caching that
      // as the answer painted every bonus quadrant red for the rest of the
      // session with no retry. An answer needs at least one real payload.
      this.#futureTraitsCacheKey = payloads.some((data) => data != null)
        ? cacheKey
        : null;
      if (payloads.every(data => data != null)) {
        rememberJackpotTraits(this.#selectedDay, this.#selectedPlayer, dayLevel, owned, 'future');
      }
    } catch (err) {
      console.warn('[ReplayPanel] Failed to load future-level traits:', err);
      // Fail-closed, like #loadPlayerTraits: an empty set spins red rather than
      // promising a match the player does not hold.
      this.#futureTraitIds = new Set();
      this.#futureTraitsCacheKey = null;
    } finally {
      this.#optionalTicketReads.delete(controller);
      if (this.#futureReadController === controller) this.#futureReadController = null;
    }
  }

  async #loadPlayerTraits({ required = false } = {}) {
    if (readLightweightModePreference() && !required) return;
    // An early processing signal can still carry the bootstrap purchase level.
    // Exact rolls start the required read; avoid speculative history scans.
    if (!required && !this.#tutorialFixture && this.#dayRoll1?.purchaseLevel == null) return;
    const loadSeq = ++this.#playerTraitsLoadSeq;
    if (this.#tutorialFixture) {
      this.#playerTraitIds = new Set(this.#tutorialFixture.playerTraits);
      this.#traitsCacheAddress = `${this.#tutorialFixture.player}|${this.#tutorialFixture.purchaseLevel}`;
      this.#playerTraitsPending = false;
      return;
    }
    if (!this.#selectedPlayer) {
      this.#playerTraitIds = new Set();
      this.#traitsCacheAddress = null;
      this.#playerTraitsPending = false;
      return;
    }
    // Roll 1 samples its winners from tickets at the day's PURCHASE level, so
    // scratch eligibility must be scoped to the traits the player holds at
    // that level. The old /replay/player-traits endpoint aggregated ALL
    // levels, which lit quadrants scratchable for players who could never
    // win the day's draw (user bug report: "should be red and unscratchable").
    const level = replayHoldingsLevel({
      exactPurchaseLevel: this.#dayRoll1?.purchaseLevel,
      processingPurchaseLevel: this.#jpProcessingSignals?.purchaseLevel,
      processingDay: this.#jpProcessingSignals?.day,
      selectedDay: this.#selectedDay,
      selectedLevel: this.#selectedLevel,
    });
    if (level == null) {
      this.#playerTraitIds = new Set();
      this.#traitsCacheAddress = null;
      // The selected day is still resolving its purchase level. This is an
      // unknown cohort, not proof that the player owns no traits.
      this.#playerTraitsPending = true;
      return;
    }
    const player = this.#selectedPlayer;
    const ticketsReadyAtLoad = this.#jpProcessingSignals?.ticketsReady === true;
    const cacheKey = `${player.toLowerCase()}|${level}`;
    if (this.#traitsCacheAddress === cacheKey) {
      this.#playerTraitsPending = false;
      return;
    }
    const savedTraits = readJackpotTraits(this.#selectedDay, player, level);
    if (savedTraits) {
      this.#playerTraitIds = savedTraits;
      this.#traitsCacheAddress = cacheKey;
      this.#traitsTicketsReadyAtLoad = true;
      this.#playerTraitsPending = false;
      return;
    }
    this.#playerTraitsPending = true;
    const controller = new AbortController();
    if (!required) this.#optionalTicketReads.add(controller);
    try {
      // No &day= param — the endpoint 404s on days without an indexed
      // daily_rng row (same gotcha as app-tickets-inventory).
      const data = await fetchJSON(
        `/player/${encodeURIComponent(player)}/tickets/by-trait?level=${level}`,
        { priority: 'interaction', signal: controller.signal },
      );
      if (controller.signal.aborted || loadSeq !== this.#playerTraitsLoadSeq
        || String(this.#selectedPlayer || '').toLowerCase() !== player.toLowerCase()) return;
      const owned = new Set();
      for (const card of (Array.isArray(data?.cards) ? data.cards : [])) {
        for (const entry of (Array.isArray(card?.entries) ? card.entries : [])) {
          if (entry && entry.traitId != null) owned.add(Number(entry.traitId));
        }
      }
      this.#playerTraitIds = owned;
      this.#traitsCacheAddress = cacheKey;
      this.#traitsTicketsReadyAtLoad = ticketsReadyAtLoad;
      this.#playerTraitsPending = false;
      rememberJackpotTraits(this.#selectedDay, player, level, owned);
    } catch (err) {
      if (controller.signal.aborted || loadSeq !== this.#playerTraitsLoadSeq) return;
      console.warn('[ReplayPanel] Failed to load player traits:', err);
      // A failed preload is still unknown ownership. Keep preparing and retry
      // before enabling Spin, rather than repeating this slow read on click.
      this.#playerTraitIds = new Set();
      this.#traitsCacheAddress = null;
      this.#playerTraitsPending = true;
      this.#scheduleDayDataReload(this.#selectedDay);
    } finally {
      this.#optionalTicketReads.delete(controller);
      if (loadSeq === this.#playerTraitsLoadSeq) this.#syncSpinControlState();
    }
  }

  // --- Event Handlers ---

  async #onDayChange(e) {
    if (e?.target?.value === 'older') { await this.#loadDays(this.#nextRngDay); return false; }
    const dayNum = Number.parseInt(e?.target?.value, 10);
    const validDay = Number.isInteger(dayNum) && dayNum > 0;
    const sameDay = validDay && Number(this.#selectedDay) === dayNum;
    // A same-day bridge/poll event is never a new reveal. Once the player has
    // started this exact day/address, preserve its landed or in-flight board
    // even if a transient warming flag makes #dayDataReady() false. The player
    // could not have started this reveal without complete exact-day data.
    if (sameDay && this.#interactiveSelectionActive()) return true;
    // Same-day selector refreshes are normally inert. A day whose exact roll
    // payloads failed during rollover is the exception: keep it masked and
    // retry instead of declaring a half-loaded scratch board ready.
    if (sameDay && this.#dayDataReady(dayNum)) return true;
    if (sameDay && Number(this.#dayLoadInFlight?.day) === dayNum) return false;
    // In app/single-button mode an empty value can only be a transient option
    // rebuild. There is no user-facing "no day" selection there, so retain the
    // current board until the hidden selector is repopulated.
    if (!validDay && this.#singleButton() && this.#selectedDay != null) {
      const matching = e?.target?.options
        ? Array.from(e.target.options).find(
            (option) => Number(option.value) === Number(this.#selectedDay),
          )
        : null;
      if (matching) e.target.value = matching.value;
      return;
    }

    const keepLoadingFace = sameDay && this.#idleSpinWanted()
      && (this.#idleSpinTimer != null || Boolean(this.querySelector('.q-decorative')));
    this.#hostRevealSeq += 1;
    this.#interactiveRevealKey = null;
    this.#skipSpinId = null;
    this.#loadedDay = null;
    this.#cancelCoinDrawPreload();
    if (!validDay) {
      this.#playerTraitsLoadSeq += 1;
      this.#playerTraitsPending = false;
      this.#dayLoadSeq++;
      this.#dayLoadInFlight = null;
      this.#dayReloadAttempt = 0;
      this.#dayReloadTarget = null;
      if (this.#dayReloadTimer != null) {
        try { clearTimeout(this.#dayReloadTimer); } catch { /* defensive */ }
        this.#dayReloadTimer = null;
      }
      this.removeAttribute('data-day-loading');
      if (!this.hasAttribute('data-day-warming')) this.removeAttribute('aria-busy');
      this.#selectedDay = null;
      this.#resetCards();
      this.querySelector('[data-bind="empty-state"]').hidden = false;
      this.querySelector('[data-bind="distributions"]').hidden = true;
      this.querySelector('[data-bind="ticket-info"]').hidden = true;
      this.querySelector('[data-bind="reveal-btn"]').disabled = true;
      batch([['replay.day', null], ['replay.level', null]]);
      return false;
    }

    if (this.#dayReloadTimer != null) {
      try { clearTimeout(this.#dayReloadTimer); } catch { /* defensive */ }
      this.#dayReloadTimer = null;
    }
    if (!sameDay || this.#dayReloadTarget !== dayNum) {
      this.#dayReloadAttempt = 0;
      this.#dayReloadTarget = dayNum;
    }
    const loadSeq = ++this.#dayLoadSeq;
    this.#dayLoadInFlight = { day: dayNum, seq: loadSeq };
    const loadIsCurrent = () => (
      this.#dayLoadSeq === loadSeq && Number(this.#selectedDay) === dayNum
    );
    const finishStaleLoad = () => {
      if (this.#dayLoadInFlight?.seq === loadSeq) this.#dayLoadInFlight = null;
      return false;
    };

    this.#selectedDay = dayNum;
    this.#openingFlipDay = false;
    // A new day can route to a different purchase level. Never color its
    // refresh animation from the prior day's cached trait set.
    this.#distributions = [];
    this.#winners = [];
    this.#selectedLevel = null;
    this.#dayRoll1 = null;
    this.#dayRoll2 = null;
    this.#coinDrawBattle = null;
    this.#dayBonusTraitDraw = true;
    this.#playerTraitsLoadSeq += 1;
    // A retry of the same draw must not throw away traits warmed after ticket
    // processing completed. New days and pre-processing holdings still refresh.
    if (!sameDay || !this.#traitsTicketsReadyAtLoad) {
      this.#playerTraitIds = new Set();
      this.#traitsCacheAddress = null;
      this.#traitsTicketsReadyAtLoad = false;
    }
    this.#playerTraitsPending = Boolean(this.#selectedPlayer);
    if (!keepLoadingFace) this.#resetCards();
    this.#setDayDataLoading(dayNum, true);
    // Loading is itself enough reason to run the reel. The persisted reveal
    // bit commonly lands later than these exact-day requests.
    this.#startIdleSpin();

    const rngEntry = this.#rngDays.find(d => d.day === dayNum);
    const hasRng = rngEntry && rngEntry.finalWord && rngEntry.finalWord !== '0';

    this.querySelector('[data-bind="empty-state"]').hidden = true;

    // Both exact roll payloads are mandatory scratch-underlay data. A transient
    // 404 here used to enable Reveal anyway, producing an empty or prior-day
    // underside; retain the loading gate and retry instead.
    const rolls = await this.#loadDayRolls(dayNum);
    if (!loadIsCurrent()) return finishStaleLoad();
    this.#dayRoll1 = rolls.roll1;
    this.#dayRoll2 = rolls.roll2;
    this.#coinDrawBattle = rolls.roll2?.coinDrawBattle ?? null;
    this.#dayBonusTraitDraw = rolls.roll2?.bonusTraitDraw !== false;
    this.#scheduleCoinDrawPreload();
    this.#repairOpeningFlipRolls(dayNum);

    // Do not request the potentially large distribution feed while the two
    // exact roll endpoints still say this processing day is not ready. The
    // retry loop only needs those small readiness payloads; once both exist we
    // load day detail exactly as before for player labels and prize mapping.
    const rollPayloadsReady = Number(this.#dayRoll1?.day) === dayNum
      && Array.isArray(this.#dayRoll1?.wins)
      && Number(this.#dayRoll2?.day) === dayNum
      && Array.isArray(this.#dayRoll2?.wins);
    // These reads are independent once the exact roll cohort is known. Start
    // them together rather than adding three network round trips to readiness.
    this.#selectedLevel = this.#openingFlipDay ? 0 : (this.#dayRoll1?.level ?? null);
    const traitsPromise = rollPayloadsReady ? this.#loadPlayerTraits({ required: true }) : Promise.resolve();
    const sharedDay = reusableJackpotPayload(this.#resolvedDayPayload, dayNum) ?? readCompletedJackpot(dayNum);
    const winnersPromise = (sharedDay
      ? Promise.resolve(sharedDay)
      : fetchJSON(`/game/jackpot/day/${dayNum}/winners`, { force: true }))
      .catch((error) => {
        noteReplayApiResponse(error?.response);
        return null;
      });
    if (rollPayloadsReady) {
      const detail = await this.#loadDayDetail(dayNum);
      if (!loadIsCurrent()) return finishStaleLoad();
      this.#distributions = Array.isArray(detail?.distributions) ? detail.distributions : [];
      // The opening level has no Roll 1 endpoint, so its main FLIP board is
      // synthesized from this distribution feed. #repairOpeningFlipRolls()
      // also runs before the readiness check above to let that 404 path load
      // the detail payload; rebuild once more now that the awards actually
      // exist or every public quadrant is permanently rendered as 0 x 0.
      this.#repairOpeningFlipRolls(dayNum);
      const rng = this.#rngDays.find((entry) => Number(entry?.day) === dayNum);
      this.#dayRoll2 = includeEarlyBirdTicketWins({
        roll1: this.#dayRoll1,
        roll2: this.#dayRoll2,
        distributions: this.#distributions,
        bonusTraits: this.#packedTraits(rng?.bonusTraitsPacked),
      });
    }

    // Load winners from the authoritative day/winners endpoint.
    // This gives us level, winner list with breakdown, and hasBonus flags.
    let nextWinners = [];
    let nextSelectedLevel = null;
    try {
      const wJson = await winnersPromise;
      if (!loadIsCurrent()) return finishStaleLoad();
      nextSelectedLevel = this.#openingFlipDay
        ? 0
        : (wJson.level ?? (this.#distributions[0]?.level ?? null));
      nextWinners = wJson.winners || [];
    } catch (error) {
      noteReplayApiResponse(error?.response);
      if (this.#distributions.length > 0) {
        nextSelectedLevel = this.#distributions[0].level;
      }
    }
    if (!loadIsCurrent()) return finishStaleLoad();
    this.#winners = Array.isArray(nextWinners) ? nextWinners : [];
    this.#selectedLevel = nextSelectedLevel
      ?? (this.#openingFlipDay ? 0 : (this.#dayRoll1?.level ?? null));

    // Roll 1 grades the purchase level itself. Prefer the endpoint's explicit
    // source level; modal winner level can be the level receiving ticket prizes
    // (or an opening bonus target), which is not the ownership cohort.
    await Promise.all([
      this.#selectedLevel != null
        ? this.#loadTickets(this.#dayRoll1?.purchaseLevel ?? (this.#selectedLevel + 1))
        : Promise.resolve(),
      traitsPromise,
    ]);
    if (!loadIsCurrent()) return finishStaleLoad();
    // Older payloads may omit the purchase cohort. Retry after winners resolve
    // the level; normally the concurrent trait read above has already hydrated it.
    if (this.#playerTraitsPending) await this.#loadPlayerTraits({ required: true });
    if (!loadIsCurrent()) return finishStaleLoad();

    // A valid draw can have zero winners. RNG + a selected player are enough
    // to run the public board, but only after both exact roll payloads exist.
    const rollDataReady = this.#hasExactDayRolls(dayNum) && !this.#playerTraitsPending;
    const canReveal = rollDataReady && hasRng && this.#selectedPlayer;
    this.querySelector('[data-bind="reveal-btn"]').disabled = !canReveal;

    if (this.#winners.length > 0) {
      this.#showDistributions(this.#winners);
    } else {
      this.querySelector('[data-bind="distributions"]').hidden = true;
    }

    // Publish selected day + level to store so game-status-bar can display them.
    batch([
      ['replay.day', dayNum],
      ['replay.level', this.#selectedLevel],
    ]);
    if (loadIsCurrent() && rollDataReady) {
      this.#loadedDay = dayNum;
      this.#dayReloadAttempt = 0;
      this.#dayReloadTarget = null;
      this.#setDayDataLoading(dayNum, false);
      try {
        this.dispatchEvent(new CustomEvent('replay:day-ready', {
          detail: { day: dayNum },
          bubbles: true,
        }));
      } catch { /* headless / CustomEvent shim absent */ }
      void this.#applyPersistedRevealState(this.#hostRevealSeq);
    } else if (loadIsCurrent()) {
      this.#loadedDay = null;
      this.#setDayDataLoading(dayNum, true);
      this.#scheduleDayDataReload(dayNum);
    }
    if (this.#dayLoadInFlight?.seq === loadSeq) this.#dayLoadInFlight = null;
    if (rollDataReady) this.#scheduleCoinDrawPreload();
    return rollDataReady;
  }

  #onPlayerChange(e) {
    const addr = String(e?.target?.value || '').trim();
    const nextPlayer = addr || null;
    const currentPlayer = this.#selectedPlayer == null
      ? null
      : String(this.#selectedPlayer);
    if ((nextPlayer == null && currentPlayer == null)
      || (nextPlayer != null && currentPlayer != null
        && nextPlayer.toLowerCase() === currentPlayer.toLowerCase())) return;
    // The app always has a concrete viewed address. If a ticket-list rebuild
    // momentarily drops its option, keep the rendered player's draw instead of
    // clearing all four quadrants while the bridge restores the same address.
    if (nextPlayer == null && this.#singleButton() && currentPlayer != null) {
      const matching = e?.target?.options
        ? Array.from(e.target.options).find(
            (option) => String(option.value).toLowerCase() === currentPlayer.toLowerCase(),
          )
        : null;
      if (matching) e.target.value = matching.value;
      return;
    }

    this.#hostRevealSeq += 1;
    this.#interactiveRevealKey = null;
    this.#skipSpinId = null;
    this.#selectedPlayer = nextPlayer;
    this.#cancelCoinDrawPreload();
    this.#scheduleCoinDrawPreload();
    this.#playerTraitsLoadSeq += 1;
    this.#playerTraitIds = new Set();
    this.#traitsCacheAddress = null;
    this.#playerTraitsPending = Boolean(nextPlayer);
    this.#resetCards();
    this.#startIdleSpin();
    // Publish replay-player selection so sibling widgets (status-bar activity
    // score) can react without coupling to this panel.
    update('replay.player', this.#selectedPlayer);
    this.#updateTicketInfo();
    const revealSeq = this.#hostRevealSeq;
    void this.#loadPlayerTraits({ required: this.#hasExactDayRolls(this.#selectedDay) }).then(() => {
      if (revealSeq === this.#hostRevealSeq) {
        void this.#applyPersistedRevealState(revealSeq);
        this.#scheduleCoinDrawPreload();
      }
    });
    this.#loadPlayerDecimator();
    // Re-render distributions to update (YOU) labels
    if (this.#winners.length > 0) {
      this.#showDistributions(this.#winners);
    }
    // Update reveal button state
    const rngEntry = this.#rngDays.find(d => d.day === this.#selectedDay);
    const hasRng = rngEntry && rngEntry.finalWord && rngEntry.finalWord !== '0';
    this.querySelector('[data-bind="reveal-btn"]').disabled = !hasRng
      || !this.#selectedPlayer
      || !this.#dayDataReady(this.#selectedDay);
  }

  async #loadPlayerDecimator() {
    const container = this.querySelector('[data-bind="player-decimator"]');
    const list = this.querySelector('[data-bind="player-decimator-list"]');
    if (!container || !list) return;
    if (!this.#selectedPlayer) {
      container.hidden = true;
      list.innerHTML = '';
      return;
    }
    // /app/ basic mode hides this block outright (app.css: `.replay-player-decimator
    // { display: none }` — decimator deferred per product call), so the request below
    // is pure waste there. Skipping it also silences a 404 on every page load.
    if (getComputedStyle(container).display === 'none') return;
    list.innerHTML = '<div class="jp-summary-note">Loading…</div>';
    container.hidden = false;
    try {
      const data = await fetchJSON(`/game/decimator/player/${this.#selectedPlayer}`);
      const rounds = Array.isArray(data.rounds) ? data.rounds : [];
      if (rounds.length === 0) {
        list.innerHTML = '<div class="jp-summary-note">Player has no decimator burns.</div>';
        return;
      }
      // Render one row per round.  Status priority: claimed > pending winner > not eligible.
      list.innerHTML = rounds.map((r) => {
        let status, amountText;
        if (r.claimed) {
          const ethStr = formatEth(r.claimedEthAmount || '0');
          const lbStr  = formatEth(r.claimedLootboxAmount || '0');
          const lbPart = (r.claimedLootboxAmount && BigInt(r.claimedLootboxAmount) > 0n) ? ` + ${lbStr} ETH luckbox` : '';
          status = '<span class="replay-dec-status replay-dec-claimed">Claimed</span>';
          amountText = `${ethStr} ETH${lbPart}`;
        } else if (r.isWinner && BigInt(r.claimableEth || '0') > 0n) {
          status = '<span class="replay-dec-status replay-dec-pending">Claimable (pending)</span>';
          amountText = `${formatEth(r.claimableEth)} ETH`;
        } else if (!r.resolved) {
          status = '<span class="replay-dec-status replay-dec-unresolved">Not yet resolved</span>';
          amountText = '—';
        } else {
          status = '<span class="replay-dec-status replay-dec-loser">Not eligible</span>';
          amountText = `bucket ${r.bucket}/sub ${r.playerSubBucket} (winner sub ${r.winningSubBucket ?? '—'})`;
        }
        return `<div class="replay-dec-row">
          <span class="replay-dec-level"><strong>L${r.level}</strong></span>
          ${status}
          <span class="replay-dec-amount">${amountText}</span>
        </div>`;
      }).join('');
    } catch (err) {
      console.warn('[ReplayPanel] decimator fetch failed', err);
      list.innerHTML = '<div class="jp-summary-note">Could not load decimator state.</div>';
    }
  }

  #updateTicketInfo() {
    const infoEl = this.querySelector('[data-bind="ticket-info"]');
    const countEl = this.querySelector('[data-bind="ticket-count"]');
    const detailEl = this.querySelector('[data-bind="ticket-detail"]');

    if (!this.#selectedPlayer) {
      const total = this.#tickets.reduce((sum, t) => sum + t.entryCount, 0);
      countEl.textContent = `${total.toLocaleString()} total entries`;
      detailEl.textContent = `across ${this.#tickets.length} players`;
      infoEl.hidden = false;
      return;
    }

    const player = this.#tickets.find(t => t.address === this.#selectedPlayer);
    if (player) {
      countEl.textContent = `${player.entryCount.toLocaleString()} entries`;
      detailEl.textContent = `${player.totalMintedOnLevel.toLocaleString()} minted on level`;
      infoEl.hidden = false;

      const won = this.#winners.find(w => w.address.toLowerCase() === this.#selectedPlayer.toLowerCase());
      if (won) {
        countEl.textContent += ' · WINNER';
        countEl.classList.add('replay-winner-text');
      } else {
        countEl.classList.remove('replay-winner-text');
      }
    } else {
      infoEl.hidden = true;
    }
  }

  #showDistributions(winners) {
    // winners: array from /game/jackpot/day/:day/winners response
    const container = this.querySelector('[data-bind="distributions"]');
    const list = this.querySelector('[data-bind="dist-list"]');

    if (!winners || !winners.length) {
      container.hidden = true;
      return;
    }

    const myAddr = this.#selectedPlayer?.toLowerCase();
    const ethCounts = new Map();
    for (const row of this.#dayRoll1?.wins || []) {
      if (row.awardType !== 'eth' || row.traitId == null) continue;
      const trait = Number(row.traitId);
      ethCounts.set(trait, (ethCounts.get(trait) || 0) + 1);
    }
    const soloTraits = new Set([...ethCounts].filter(([, count]) => count === 1).map(([trait]) => trait));

    list.innerHTML = winners.map(w => {
      const addr = w.address.toLowerCase();
      const isMe = myAddr && addr === myAddr;

      // Build trait-grouped tooltip from breakdown entries, partitioned by roll phase
      const tipHtml = this.#buildWinnerTooltip(w.breakdown || [], w.hasBonus);
      const passRows = (w.breakdown || []).filter(row => row.awardType === 'whale_pass');
      const soloPass = row => row.traitId != null && soloTraits.has(Number(row.traitId));
      const countHalves = rows => rows.reduce((total, row) => total + positiveBigInt(row.amount) * positiveBigInt(row.count ?? 1), 0n);
      const whaleHalves = countHalves(passRows.filter(soloPass));
      const ticketHalves = countHalves(passRows.filter(row => !soloPass(row)));
      const ticketEntries = (w.breakdown || []).filter(row => ['ticket','tickets'].includes(row.awardType))
        .reduce((total, row) => total + positiveBigInt(row.amount) * positiveBigInt(row.count ?? 1), 0n);
      const ticketLabel = joScaledToTickets(ticketEntries + whalePassTicketEntries(ticketHalves)).toLocaleString('en-US', { maximumFractionDigits: 2 });
      const whaleBadge = (whaleHalves > 0n
        ? `<span class="replay-dist-whale" title="${formatWhalePassAward(whaleHalves)}" aria-label="${formatWhalePassAward(whaleHalves)}"><img src="/app/assets/whale-pass-mark.svg" alt="">×${formatWhalePassAmount(whaleHalves)}</span>` : '')
        + (ticketHalves > 0n ? `<span class="replay-dist-tickets" aria-label="${ticketLabel} tickets">${createJackpotTicketIcon('replay-dist-ticket-icon').outerHTML}×${ticketLabel}</span>` : '');

      return `
      <div class="replay-dist-item${isMe ? ' replay-dist-mine' : ''}" style="position:relative">
        <span class="replay-dist-winner">${truncateAddress(w.address)}${isMe ? ' (YOU)' : ''}</span>${whaleBadge}
        ${w.hasBonus ? '<span class="replay-dist-bonus-badge">+BONUS</span>' : ''}
        <div class="winner-tip">${tipHtml}</div>
      </div>`;
    }).join('');

    container.hidden = false;
  }

  /**
   * Build HTML for the hover tooltip grouped by trait, partitioned by roll phase.
   * breakdown: [{awardType, amount, count, traitId}]
   * hasBonus: if true, split entries into Roll 1 (eth/tickets) vs Bonus Roll (flip non-null traitId)
   *           vs Bonus Center (null-traitId flip). This matches exactly what the widget renders
   *           across Roll 1 quadrants + Roll 2 bonus quadrants + center diamond.
   */
  #buildWinnerTooltip(breakdown, hasBonus = false) {
    if (!breakdown || breakdown.length === 0) return '<em>No detail available</em>';

    /**
     * Render a set of entries grouped by traitId into tooltip HTML.
     * entries: [{awardType, amount, count, traitId}]
     */
    const renderEntryGroup = (entries) => {
      // Solo-bucket detection: whale_pass (traitId=null) always goes to the
      // single solo-bucket winner. We identify that bucket as the trait with
      // the largest single ETH slice in this player's entries, and fold the
      // whale pass row INTO that trait group instead of showing a separate
      // "Solo Winner" section.
      let soloTraitKey = null;
      let soloMaxEth = 0n;
      for (const e of entries) {
        if ((e.awardType || '') === 'eth' && e.traitId != null) {
          const v = BigInt(e.amount || '0');
          if (v > soloMaxEth) { soloMaxEth = v; soloTraitKey = e.traitId; }
        }
      }

      const byTrait = new Map(); // traitId|'bonus'|'solo' -> entries[]
      for (const entry of entries) {
        let key;
        if (entry.traitId != null) {
          key = entry.traitId;
        } else if ((entry.awardType || '') === 'whale_pass') {
          // Fold whale pass into the solo-bucket trait group when we can
          // identify it; otherwise fall back to a labeled Solo Winner section.
          key = soloTraitKey != null ? soloTraitKey : 'solo';
        } else {
          key = 'bonus';
        }
        if (!byTrait.has(key)) byTrait.set(key, []);
        byTrait.get(key).push(entry);
      }
      const sections = [];
      for (const [key, ents] of byTrait) {
        let headerHtml;
        if (key === 'solo') {
          headerHtml = '<span class="tip-trait-name">Solo Winner</span>';
        } else if (key === 'bonus') {
          headerHtml = '<span class="tip-trait-name">Bonus Center</span>';
        } else {
          const traitId = Number(key);
          const badge = traitToBadge(traitId);
          const quadrant = Math.floor(traitId / 64);
          const quadrantName = BADGE_QUADRANTS[quadrant] || 'Unknown';
          const label = badge ? `${badge.item} (${quadrantName} Q${quadrant + 1})` : `Trait ${traitId}`;
          headerHtml = `<span class="tip-trait-name">${label}</span>`;
        }
        const rows = ents.map(e => {
          const at = e.awardType || '';
          let formatted;
          if (at === 'eth') {
            formatted = `${formatEth(e.amount)} ETH`;
          } else if (at === 'eth_baf') {
            formatted = `${formatEth(e.amount)} ETH (BAF)`;
          } else if (at === 'craps_pass') {
            formatted = `${escapeHtml(String(e.amount))} Craps comp${String(e.amount) === '1' ? '' : 's'}`;
          } else if (at === 'decimator_entry') {
            formatted = `${escapeHtml(String(e.amount))} Decimator ${String(e.amount) === '1' ? 'entry' : 'entries'}`;
          } else if (at === 'afking_seat_flip') {
            formatted = `${formatFlip(e.amount)} FLIP (AFKing Seat Draw)`;
          } else if (at === 'flip' || at === 'farFutureCoin' || at.includes('flip')) {
            formatted = `${formatFlip(e.amount)} FLIP`;
          } else if (at === 'tickets' || at === 'ticket') {
            // Amounts are in ENTRIES (4 = 1 whole ticket); joScaledToTickets /4 for display.
            const n = joScaledToTickets(e.amount);
            formatted = `${n} ticket${n !== 1 ? 's' : ''}`;
          } else if (at === 'tickets_baf') {
            const n = joScaledToTickets(e.amount);
            formatted = `${n} ticket${n !== 1 ? 's' : ''} (BAF)`;
          } else if (at === 'whale_pass') {
            formatted = formatWhalePassAward(e.amount);
          } else {
            formatted = `${e.amount} ${at}`;
          }
          const countStr = e.count > 1 ? ` ×${e.count}` : '';
          return `<span class="tip-row${at === 'craps_pass' ? ' tip-row--craps-comp' : ''}">${formatted}${countStr}</span>`;
        }).join('');
        sections.push(`<div class="tip-trait-group">${headerHtml}${rows}</div>`);
      }
      return sections.join('');
    };

    if (!hasBonus) {
      // No bonus roll — render entries flat, grouped by traitId.  BAF entries
      // (traitId=420 sentinel) must still be partitioned out so they don't
      // render under a phantom "horseshoe Q7" badge.
      const nonBaf = breakdown.filter(e => e.awardType !== 'eth_baf' && e.awardType !== 'tickets_baf');
      const baf = breakdown.filter(e => e.awardType === 'eth_baf' || e.awardType === 'tickets_baf');
      const renderBafFlat = (entries) =>
        entries.map(e => {
          const at = e.awardType || '';
          let formatted;
          if (at === 'eth_baf') {
            formatted = `${formatEth(e.amount)} ETH`;
          } else {
            const n = joScaledToTickets(e.amount);
            formatted = `${n} ticket${n !== 1 ? 's' : ''}`;
          }
          const countStr = e.count > 1 ? ` ×${e.count}` : '';
          return `<span class="tip-row">${formatted}${countStr}</span>`;
        }).join('');
      const parts = [];
      if (nonBaf.length > 0) parts.push(renderEntryGroup(nonBaf));
      if (baf.length > 0) parts.push('<div class="tip-phase-header">BAF</div><div class="tip-trait-group">' + renderBafFlat(baf) + '</div>');
      return parts.length > 0 ? parts.join('') : '<em>No detail available</em>';
    }

    // Partition: Roll 1 entries (eth / tickets / whale_pass) vs Bonus Roll entries (flip with
    // non-null traitId) vs Bonus Center (null-traitId flip / farFutureCoin) vs BAF (eth_baf /
    // tickets_baf — traitId=420 sentinel, must be partitioned out so the trait grouper doesn't
    // render them under a phantom "horseshoe Q7" badge).
    const roll1Entries = [];
    const bonusQuadEntries = [];
    const bonusCenterEntries = [];
    const bafEntries = [];

    for (const entry of breakdown) {
      const at = entry.awardType || '';
      const isFlip = at === 'flip' || at === 'farFutureCoin' || at.includes('flip');
      if (at === 'eth_baf' || at === 'tickets_baf') {
        bafEntries.push(entry);
      } else if (this.#openingFlipDay
        && isFlip
        && entry.traitId != null
        && Number(entry.level) === 1) {
        roll1Entries.push(entry);
      } else if (isFlip && entry.traitId == null) {
        bonusCenterEntries.push(entry);
      } else if ((isFlip || at === 'craps_pass') && entry.traitId != null) {
        bonusQuadEntries.push(entry);
      } else {
        roll1Entries.push(entry);
      }
    }

    // BAF entries are not trait-keyed (traitId=420 is the sentinel, not a real
    // trait), so render them flat without the per-trait grouping.
    const renderBafFlat = (entries) =>
      entries.map(e => {
        const at = e.awardType || '';
        let formatted;
        if (at === 'eth_baf') {
          formatted = `${formatEth(e.amount)} ETH`;
        } else {
          const n = joScaledToTickets(e.amount);
          formatted = `${n} ticket${n !== 1 ? 's' : ''}`;
        }
        const countStr = e.count > 1 ? ` ×${e.count}` : '';
        return `<span class="tip-row">${formatted}${countStr}</span>`;
      }).join('');

    const parts = [];
    if (roll1Entries.length > 0) {
      parts.push('<div class="tip-phase-header">Main Spin</div>' + renderEntryGroup(roll1Entries));
    }
    if (bonusQuadEntries.length > 0) {
      parts.push('<div class="tip-phase-header">Bonus Spin</div>' + renderEntryGroup(bonusQuadEntries));
    }
    if (bonusCenterEntries.length > 0) {
      parts.push('<div class="tip-phase-header">Bonus Center</div>' + renderEntryGroup(bonusCenterEntries));
    }
    if (bafEntries.length > 0) {
      parts.push('<div class="tip-phase-header">BAF</div><div class="tip-trait-group">' + renderBafFlat(bafEntries) + '</div>');
    }
    return parts.length > 0 ? parts.join('') : '<em>No detail available</em>';
  }

  // --- Reveal / Spin ---

  /**
   * The main reveal carries the same hard latch as the bonus roll: it adds
   * `is-spinning` before two network reads, and #syncSpinControlState() will
   * not repaint a button that carries one. Clear it on any path that never
   * reaches the reel — #resetCards() only clears it once the flow gets that
   * far, which the selection-changed bail above it does not.
   */
  #releaseMainSpinLatch() {
    const btn = this.querySelector('[data-bind="reveal-btn"]');
    if (!btn) return;
    btn.classList?.remove('is-spinning');
    btn.removeAttribute?.('aria-busy');
    this.#syncSpinControlState();
  }

  // A reveal that threw used to release its latch and say nothing: the player
  // could not tell whether they had won, and the next press failed the same
  // silent way. The finally blocks repaint the control from state, so paint
  // the failure one macrotask later and only over an idle control; the next
  // repaint (a poll or the next press) clears it.
  #paintSpinFailure() {
    globalThis.setTimeout?.(() => {
      const btn = this.querySelector?.('[data-bind="reveal-btn"]');
      if (!btn || btn.hidden) return;
      if (btn.classList?.contains?.('is-spinning') || btn.classList?.contains?.('is-processing')) return;
      btn.disabled = false;
      btn.textContent = SPIN_FAILED_LABEL;
    }, 0);
  }

  async #triggerReveal({ instant = false, persisted = false, scratchedQuadrants = null } = {}) {
    const tutorialFixture = this.#tutorialFixture;
    if (!this.#selectedDay || !this.#selectedPlayer) return;
    // The RNG row can arrive before its roll/bucket endpoints. Never construct
    // a scratch board until its exact-day underside is complete.
    if (!this.#dayDataReady(this.#selectedDay)) return false;

    const rngEntry = this.#rngDays.find(d => d.day === this.#selectedDay);
    if (!rngEntry || !rngEntry.finalWord || rngEntry.finalWord === '0') return;

    const selectionKey = this.#selectionKey();
    if (persisted) {
      if (this.#interactiveSelectionActive(selectionKey)) return false;
    } else {
      this.#claimInteractiveReveal(selectionKey);
    }

    // Create/resume WebAudio while the reveal click still owns user activation.
    // Jackpot cues are synthesized locally, so the spin never waits on an
    // audio download/decode path.
    if (!instant) {
      try { this.#getAudio(); } catch (_error) { /* visuals remain authoritative */ }
    }

    const btn = this.querySelector('[data-bind="reveal-btn"]');
    btn.disabled = true;
    if (!instant) {
      btn.classList?.add('is-spinning');
      btn.textContent = 'PREPARING SPIN…';
    }

    let settled = false;
    try {
      // Bonus traits are already knowable here and stay on the shared flight
      // consumed by the later button. DAY SUMMARY deliberately waits until
      // the final scratch is over: its response processing must not compete
      // with reel or scratch presentation on the main thread.
      const playerTraitsPromise = this.#loadPlayerTraits({ required: true });
      if (this.#hasBonus) void this.#loadFutureTraits();

      await playerTraitsPromise; // ensure traits loaded for spin coloring
      if (tutorialFixture !== this.#tutorialFixture) return false;
      if (this.#selectionKey() !== selectionKey) return false;
      // A persisted restore can begin before the player's click and finish its
      // trait read afterward. At that point the click owns the selection; do
      // not let the older restore clear or instantly replace the live reel.
      if (persisted && this.#interactiveSelectionActive(selectionKey)) return false;

      // Keep the existing attract/result face painted during the asynchronous
      // trait reads above. Clear it only when the derived player result is
      // ready to be rebuilt for the very next spin frame.
      this.#resetCards();

      // Filter the pre-cached day roll1/roll2 responses down to this player's wins.
      this.#filterPlayerWins(this.#selectedPlayer);

      const displayTraits = this.#displayTraitsForRoll(false);

      // Map per-player roll1 wins to quadrant prize arrays.
      this.#distributePrizesFromRoll1();
      if (!instant) btn.textContent = 'SPINNING…';

      const completed = await this.#runSpin(displayTraits, { instant, announce: !persisted, scratchedQuadrants });
      if (!completed || this.#selectionKey() !== selectionKey) return false;
      this.#mainSpinComplete = true;
      this.#syncCoinDrawCentre();
      settled = true;
      btn.classList?.remove('is-spinning');
      btn.removeAttribute?.('aria-busy');

      if (this.#singleButton()) {
        // Same button carries Roll 2; with no bonus ahead the day is played out.
        if (this.#hasBonus) {
          this.#btnMode = 'bonus';
          btn.classList?.add('is-bonus');
          btn.disabled = !this.#mainReadyForBonus();
          btn.textContent = this.#mainReadyForBonus() ? BONUS_SPIN_LABEL : BONUS_SPIN_LOCKED_LABEL;
          btn.title = this.#mainReadyForBonus()
            ? ''
            : 'Pop the blue panels first';
        } else {
          this.#syncSpinControlState();
        }
      } else {
        btn.disabled = false;
        btn.textContent = SPIN_AGAIN_LABEL;
      }

      // After Roll 1 spin: show bonus section
      this.#showBonusSection();
      if (this.#primaryDecimatorAction) this.#syncSpinControlState();
      return true;
    } catch (error) {
      console.warn('[replay-panel] reveal failed', error);
      if (!instant) this.#paintSpinFailure();
      return false;
    } finally {
      // A bail between the latch and the reel would otherwise leave the button
      // stuck at "PREPARING SPIN…" with nothing able to repaint it.
      if (!instant && !settled) this.#releaseMainSpinLatch();
    }
  }

  /**
   * Build a per-traitId lookup from the winner's breakdown array.
   * breakdown entries have { awardType, amount, count, traitId }.
   * Returns Map<traitId, { ethTotal: bigint, flipTotal: bigint }>.
   * Also returns { centerFlip: bigint } for null-traitId flip/farFutureCoin entries.
   */
  #buildBreakdownLookup(breakdown) {
    const byTrait = new Map();
    let centerFlip = 0n;
    for (const entry of (breakdown || [])) {
      const at = entry.awardType || '';
      const amt = BigInt(entry.amount || '0');
      const cnt = BigInt(entry.count || 1);
      const total = amt * cnt;
      if (entry.traitId == null) {
        // null-traitId flip = farFutureCoin center wins
        if (at === 'flip' || at === 'farFutureCoin') centerFlip += total;
        continue;
      }
      if (!byTrait.has(entry.traitId)) byTrait.set(entry.traitId, { ethTotal: 0n, flipTotal: 0n });
      const rec = byTrait.get(entry.traitId);
      if (at === 'eth') rec.ethTotal += total;
      else if (at === 'flip' || at === 'farFutureCoin') rec.flipTotal += total;
      // tickets are read from row.ticketsPerWinner (already aggregated correctly)
    }
    return { byTrait, centerFlip };
  }

  /**
   * Map #playerRoll1Wins (already filtered to this player, one row per discrete payout)
   * to quadrant prize arrays. Each win row is exactly one badge/emission — no expansion.
   * whale_pass / dgnrs rows land under a "Solo Winner" quadrant entry (no traitId quadrant).
   */
  #distributePrizesFromRoll1() {
    this.#quadWinArrays = [[], [], [], []];
    this.#centerWins = [];
    // No Bonus Spin without a bonus draw, so the day's craps-battle FLIP (roll 2's trait-less
    // rows) would be revealed under the main board's centre instead. A wallet drawn into the
    // battle never scratches it there: the centre opens the battle, which pays it on the table.
    if (!this.#dayBonusTraitDraw) {
      const onTable = this.#coinDrawDrawnIn();
      for (const win of this.#playerRoll2Wins || []) {
        if (win?.traitId == null && BigInt(win.amount || '0') > 0n
          && !(onTable && win.awardType === 'farFutureCoin')) this.#centerWins.push({ ...win });
      }
    }

    if (!this.#playerRoll1Wins || this.#playerRoll1Wins.length === 0) return;

    const MAX_VISUAL_BADGES = 20;
    // Track total wins per display pos for overflow sentinel
    const totalPerPos = [0, 0, 0, 0];

    const awardPriority = win => win.awardType === 'decimator_entry' ? 2 : win.awardType === 'whale_pass' ? 1 : 0;
    const badgeWins = [...this.#playerRoll1Wins].sort((left, right) => awardPriority(right) - awardPriority(left));
    for (const win of badgeWins) {
      const at = win.awardType || '';

      // whale_pass / dgnrs: no quadrant from traitId — place in quadrant with most ETH wins
      // (handled after loop below). Skip here.
      if (win.traitId == null) continue;

      const contractQ = Math.floor(win.traitId / 64);
      const displayPos = DISPLAY_ORDER.indexOf(contractQ);
      if (displayPos < 0 || displayPos > 3) continue;

      // A wallet may receive many generated entries in one trait. Keep their full
      // count on one award badge so the visual badge cap cannot discard entries.
      if (at === 'decimator_entry') {
        const bundle = this.#quadWinArrays[displayPos].find(row => row.traitId === win.traitId
          && positiveBigInt(row.decimatorEntryTotal) > 0n);
        if (bundle) {
          bundle.decimatorEntryTotal = String(positiveBigInt(bundle.decimatorEntryTotal) + positiveBigInt(win.amount));
          continue;
        }
      }
      totalPerPos[displayPos]++;
      const currentCount = this.#quadWinArrays[displayPos].length;
      if (currentCount >= MAX_VISUAL_BADGES) continue;

      this.#quadWinArrays[displayPos].push({
        awardType: 'aggregated',
        ethTotal: at === 'eth' ? (win.amount || '0') : '0',
        flipTotal: (at === 'flip' || at === 'farFutureCoin') ? (win.amount || '0') : '0',
        ticketTotal: (at === 'tickets' || at === 'ticket') ? Number(win.amount || 0) : 0,
        crapsPassTotal: at === 'craps_pass' ? (win.amount || '0') : '0',
        decimatorEntryTotal: at === 'decimator_entry' ? (win.amount || '0') : '0',
        whalePassCount: at === 'whale_pass' ? Number(win.amount || 0) : 0,
        traitId: win.traitId,
        ticketIndex: win.ticketIndex ?? null,
        level: win.level ?? null,
        sourceLevel: win.sourceLevel ?? null,
      });
    }

    // whale_pass / dgnrs wins (no traitId) → merge INTO the solo-bucket ETH
    // entry.  The solo bucket is where this player is the only winner, which
    // on the contract side pays the biggest single ETH slice (60% on final
    // day, 20% otherwise — still larger than the per-winner share in
    // multi-winner buckets), and whale pass / dgnrs ride along with that
    // same payout — they aren't distinct prizes.  Picking the entry with the
    // largest *single* ETH win lands on that bucket, whereas summing totals
    // can tip toward a multi-winner quadrant whose cumulative payout exceeds
    // the solo share.
    const noTraitWins = this.#playerRoll1Wins.filter(w => w.traitId == null);
    if (noTraitWins.length > 0) {
      let bestEntry = null, bestSingle = 0n;
      for (let i = 0; i < 4; i++) {
        for (const d of this.#quadWinArrays[i]) {
          const amt = BigInt(d.ethTotal || '0');
          if (amt > bestSingle) { bestSingle = amt; bestEntry = d; }
        }
      }
      if (bestEntry) {
        bestEntry.isSolo = true;
        for (const win of noTraitWins) {
          const at = win.awardType || '';
          if (at === 'whale_pass') {
            bestEntry.whalePassCount = (bestEntry.whalePassCount || 0) + Number(win.amount || 1);
          } else if (at === 'dgnrs') {
            const prev = BigInt(bestEntry.dgnrsTotal || '0');
            bestEntry.dgnrsTotal = (prev + BigInt(win.amount || '0')).toString();
          }
        }
      }
    }

    // Overflow sentinels
    for (let pos = 0; pos < 4; pos++) {
      const rendered = this.#quadWinArrays[pos].length;
      const total = totalPerPos[pos];
      if (total > rendered) {
        const lastEntry = this.#quadWinArrays[pos][rendered - 1];
        this.#quadWinArrays[pos].push({
          awardType: 'overflow',
          overflowCount: total - rendered,
          traitId: lastEntry ? lastEntry.traitId : null,
          ethTotal: '0',
          flipTotal: '0',
          ticketTotal: 0,
        });
      }
    }
  }

  // Legacy method — kept so existing #checkAllScratched / farFutureCoin center logic
  // still has a reference point. Not called from #triggerReveal anymore.
  #distributePrizes(displayTraits) {
    this.#quadWinArrays = [[], [], [], []];
    this.#centerWins = [];
    const addr = this.#selectedPlayer?.toLowerCase();
    if (!addr) return;

    const todaysTraits = new Set(displayTraits.filter(t => t != null));
    const playerDists = this.#distributions.filter(d => d.winner.toLowerCase() === addr);
    if (playerDists.length === 0) return;

    const quadDists = [];
    for (const dist of playerDists) {
      if (dist.awardType === 'farFutureCoin') {
        this.#centerWins.push(dist);
      } else if (dist.traitId == null || todaysTraits.has(dist.traitId)) {
        quadDists.push(dist);
      }
    }

    const noTraitDists = [];
    for (const dist of quadDists) {
      if (dist.traitId != null) {
        const contractQ = Math.floor(dist.traitId / 64);
        const displayPos = DISPLAY_ORDER.indexOf(contractQ);
        if (displayPos >= 0 && displayPos <= 3) this.#quadWinArrays[displayPos].push(dist);
      } else {
        noTraitDists.push(dist);
      }
    }
    if (noTraitDists.length > 0) {
      let bestQ = 0, bestEth = 0n;
      for (let i = 0; i < 4; i++) {
        const qEth = this.#quadWinArrays[i]
          .filter(d => d.awardType === 'eth')
          .reduce((s, d) => s + BigInt(d.amount || '0'), 0n);
        if (qEth > bestEth) { bestEth = qEth; bestQ = i; }
      }
      for (const dist of noTraitDists) this.#quadWinArrays[bestQ].push(dist);
    }
  }

  // --- Bonus Roll (Roll 2) ---

  // Authoritative packed traits for either board. Keeping this in one helper
  // guarantees a post-reveal flame toggle reconstructs the exact same faces as
  // the original main/bonus spins.
  #displayTraitsForRoll(bonus) {
    if (this.#tutorialFixture) {
      return toDisplayOrder(
        bonus ? this.#tutorialFixture.bonusTraits : this.#tutorialFixture.mainTraits,
      );
    }
    const rngEntry = this.#rngDays.find(d => d.day === this.#selectedDay);
    const packed = bonus ? rngEntry?.bonusTraitsPacked : rngEntry?.mainTraitsPacked;
    let traits;
    if (packed != null) {
      const p = Number(packed) >>> 0;
      traits = [p & 0xff, (p >>> 8) & 0xff, (p >>> 16) & 0xff, (p >>> 24) & 0xff];
    } else {
      traits = deriveWinningTraits(rngEntry?.finalWord || '0');
    }
    return toDisplayOrder(traits);
  }

  #prepareRoll2Prizes() {
    const nearFutureWins = this.#playerRoll2Wins.filter(w => w.traitId != null);
    const farFutureWins = this.#playerRoll2Wins.filter(w => w.traitId == null);
    this.#bonusTraitIds = new Set(nearFutureWins.map(w => w.traitId));
    this.#bonusQuadrants = new Set(nearFutureWins.map(w => Math.floor(w.traitId / 64)));
    this.#distributePrizesFromRoll2(nearFutureWins, farFutureWins);
  }

  /** /app/ opts into one shared roll button (see #btnMode). */
  #singleButton() {
    return this.hasAttribute('single-button');
  }

  #jackpotSpinsComplete() {
    return this.#mainSpinComplete && (!this.#hasBonus || this.#bonusSpinComplete);
  }

  #primaryDecimatorDue() {
    // The jackpot can award entries in this Decimator. Finish both spins and
    // uncover their possible rewards before offering the resulting battle.
    return Boolean(this.#primaryDecimatorAction)
      && !this.hasAttribute('data-day-warming')
      && !this.hasAttribute('data-day-loading')
      && this.#dayDataReady(this.#selectedDay)
      && this.#jackpotSpinsComplete()
      && !this.#finalDrawRevealPending();
  }

  #finalDrawRevealPending() {
    if (!this.#jackpotSpinsComplete()) return false;
    if (!this.#hasBonus) return !this.#mainPotentialScratchComplete;
    return !this.#bonusScratchComplete && countUnscratchedPotentialWinPanels({
      quadOwned: this.#quadOwned,
      scratched: this.#scratched,
      centerWinCount: this.#centerWins.length,
      centerScratched: this.#centerScratched,
    }) > 0;
  }

  #coinflipHandoffReady() {
    return this.#singleButton()
      && this.#jackpotSpinsComplete()
      && !this.#finalDrawRevealPending()
      && Number(this.#coinflipHandoff.day) === Number(this.#selectedDay)
      && this.#coinflipHandoff.available
      && !this.#coinflipHandoff.revealed;
  }

  #triggerCoinflipHandoff() {
    if (!this.#coinflipHandoffReady() || this.#coinflipHandoffStarting) return false;
    const target = typeof document !== 'undefined'
      ? document.querySelector?.('app-daily-flip')
      : null;
    if (!target) return false;
    let mobile = false;
    try {
      mobile = typeof matchMedia === 'function'
        ? matchMedia('(max-width: 1099px)').matches
        : Number(globalThis.innerWidth) <= 1099;
    } catch (_error) { /* desktop fallback */ }
    this.#coinflipHandoffStarting = true;
    this.#syncSpinControlState();
    let started = false;
    try {
      started = target.startCoinflipFromJackpot?.({ day: this.#selectedDay, scroll: mobile }) === true;
    } catch (_error) { started = false; }
    if (!started) {
      this.#coinflipHandoffStarting = false;
      this.#syncSpinControlState();
    }
    return started;
  }

  #refreshMainPotentialScratchGate() {
    const remaining = countUnscratchedPotentialWinPanels({
      quadOwned: this.#quadOwned,
      scratched: this.#scratched,
      centerWinCount: this.#centerWins.length,
      centerReveal: !this.#bonusPhase,
      centerScratched: this.#centerScratched,
    });
    // Once the player has exposed every visibly possible Roll 1 result, keep
    // that disclosure durable while Roll 2 replaces this widget's ownership
    // state and while the two completed draws are revisited through the toggle.
    if (remaining === 0) this.#mainPotentialScratchComplete = true;
    return remaining;
  }

  #mainReadyForBonus() {
    return this.#mainSpinComplete
      && this.#mainPotentialScratchComplete;
  }

  #showBonusSection() {
    const section = this.querySelector('[data-bind="bonus-section"]');
    const btn = this.querySelector('[data-bind="bonus-btn"]');
    if (!section) return;

    // Single-button mode keeps every actionable state on the shared LCD.
    if (this.#singleButton()) {
      if (btn) btn.hidden = true;
      section.hidden = true;
      return;
    }

    // Standalone replay needs a second control only when Roll 2 exists.
    section.hidden = !this.#hasBonus;
    if (this.#hasBonus) {
      btn.hidden = false;
      btn.disabled = !this.#mainReadyForBonus();
      btn.textContent = this.#mainReadyForBonus() ? BONUS_SPIN_LABEL : BONUS_SPIN_LOCKED_LABEL;
      btn.title = this.#mainReadyForBonus()
        ? ''
        : 'Pop the blue panels first';
    } else {
      btn.hidden = true;
    }
  }

  /**
   * Drop the bonus press's busy latch on every path that leaves
   * #triggerBonusRoll without a finished reel.
   *
   * `is-spinning` is a hard lock, not a decoration: #syncSpinControlState()
   * returns early on any button carrying it, so that a background processing
   * poll cannot repaint a live spin. Nothing else ever removed it except
   * #resetCards() — day change, player change, a fresh main reveal — so a bail
   * between the latch and the reel stranded the control at "BONUS SPINNING…"
   * over the settled Roll 1 board until the day rolled or the page reloaded.
   */
  async #releaseBonusSpinLatch(selectionKey, boardCleared) {
    const btn = this.querySelector('[data-bind="reveal-btn"]');
    btn?.classList?.remove('is-spinning');
    btn?.removeAttribute?.('aria-busy');
    // A day/player change, or a spin that has since taken the widget over,
    // already owns both the control and the board. Releasing the latch is the
    // only thing this path may still do.
    if (this.#selectionKey() !== selectionKey || this.#spinning) return;

    // Re-open the gate at the top of #triggerBonusRoll so the press retries.
    this.#bonusPhase = false;
    this.#bonusScratchComplete = false;
    this.#syncDrawToggleAffordance();

    // A reel that never ran leaves the blank board #resetMainWidget() made for
    // it. Put the settled main draw back underneath the retry offer.
    if (boardCleared) {
      try {
        this.#distributePrizesFromRoll1();
        this.#resetMainWidget();
        await this.#runSpin(this.#displayTraitsForRoll(false), {
          instant: true,
          announce: false,
        });
      } catch (err) {
        // A board that will not repaint must not also cost the player the
        // button: the release below is the part that has to happen.
        console.warn('[ReplayPanel] Failed to restore the main draw:', err);
      }
    }

    if (this.#singleButton()) {
      this.#btnMode = 'bonus';
      if (btn) {
        btn.hidden = false;
        btn.classList?.add('is-bonus');
        btn.disabled = false;
        btn.textContent = BONUS_SPIN_LABEL;
        btn.title = '';
      }
    } else {
      if (btn) {
        btn.disabled = false;
        btn.textContent = SPIN_AGAIN_LABEL;
      }
      this.#showBonusSection();
    }
  }

  async #triggerBonusRoll() {
    // Public Roll 2 results remain viewable even for a player with no eligible
    // future ticket. The button is hidden only when no bonus draw exists.
    if (!this.#dayDataReady(this.#selectedDay)
      || !this.#hasBonus
      || !this.#mainReadyForBonus()
      || this.#bonusPhase
      || this.#bonusScratchComplete) return;
    // Resume WebAudio while the bonus click still owns user activation.
    try { this.#getAudio(); } catch (_error) { /* visuals remain authoritative */ }
    const selectionKey = this.#selectionKey();
    this.#claimInteractiveReveal(selectionKey);
    const bonusSection = this.querySelector('[data-bind="bonus-section"]');
    if (bonusSection) bonusSection.hidden = true;

    this.#bonusPhase = true;
    this.#bonusScratchComplete = false;
    this.#syncDrawToggleAffordance();

    // Near-future wins (traitId != null) go to quadrants; null-traitId = center diamond FLIP.
    this.#prepareRoll2Prizes();

    // Derive display traits for the spin — Roll 2 uses the *bonus* trait set
    // the contract rolled from the salted RNG (keccak(randWord, BONUS_TRAITS)),
    // NOT the main Roll 1 traits.  The indexer stores it in
    // daily_winning_traits.bonusTraitsPacked, served via /replay/rng.
    // Fallback to the main RNG word derivation when bonusTraitsPacked is
    // unavailable (legacy DB / first day) so the widget still animates.
    const displayTraits = this.#displayTraitsForRoll(true);

    const btn = this.querySelector('[data-bind="reveal-btn"]');
    if (btn) {
      btn.disabled = true;
      btn.classList?.add('is-bonus', 'is-spinning');
      btn.textContent = 'BONUS SPINNING…';
    }

    let boardCleared = false;
    let settled = false;
    try {
      // Colouring for this roll comes from the future-level holdings.
      await this.#loadFutureTraits({ priority: 'interaction' });
      if (this.#selectionKey() !== selectionKey || !this.#bonusPhase) return false;
      // Keep the settled main board painted during an uncached trait request.
      // Once hydration finishes, clear it immediately before #runSpin paints
      // the bonus reel's first frame, leaving no blank intermediate board.
      this.#resetMainWidget();
      boardCleared = true;
      const completed = await this.#runSpin(displayTraits);
      if (!completed || this.#selectionKey() !== selectionKey || !this.#bonusPhase) return false;
      this.#bonusSpinComplete = true;
      settled = true;
      btn?.classList?.remove('is-spinning');
      btn?.removeAttribute?.('aria-busy');

      if (btn) {
        if (this.#singleButton()) {
          // Both rolls are done — nothing left to fire until the day changes.
          this.#btnMode = 'reveal';
          this.#syncSpinControlState();
        } else {
          btn.disabled = false;
          btn.textContent = SPIN_AGAIN_LABEL;
        }
      }
      if (this.#primaryDecimatorAction) this.#syncSpinControlState();
      return true;
    } catch (error) {
      console.warn('[replay-panel] bonus spin failed', error);
      this.#paintSpinFailure();
      return false;
    } finally {
      // Every early return above leaves the latch on, and no other code path
      // in this panel can take it off. Release it here or the control is dead
      // for the rest of the day.
      if (!settled) await this.#releaseBonusSpinLatch(selectionKey, boardCleared);
    }
  }

  #cancelCoinDrawPreload() {
    if (this.#coinDrawPreloadTimer != null) clearTimeout(this.#coinDrawPreloadTimer);
    this.#coinDrawPreloadTimer = null;
    this.#coinDrawPreloadKey = null;
  }

  #scheduleCoinDrawPreload() {
    const model = this.#coinDrawModel();
    if (this.#tutorialFixture || model?.kind !== 'jackpot'
      || (this.#coinDrawWasSeen(model) && this.#coinDrawResultTone(model))) return;
    const key = `${model.key}:${model.viewerBetId}:${model.player}`;
    if (this.#coinDrawPreloadKey === key) return;
    this.#cancelCoinDrawPreload();
    this.#coinDrawPreloadKey = key;
    const current = () => {
      const selected = this.#coinDrawModel();
      return this.isConnected !== false && this.#coinDrawPreloadKey === key
        && `${selected?.key}:${selected?.viewerBetId}:${selected?.player}` === key;
    };
    let attempts = 0;
    const arm = (delay = 0) => {
      this.#coinDrawPreloadTimer = setTimeout(run, delay);
      this.#coinDrawPreloadTimer?.unref?.();
    };
    const retry = () => {
      if (current()) arm(Math.min(30_000, 5_000 * 2 ** Math.min(3, attempts++)));
    };
    const run = async () => {
      this.#coinDrawPreloadTimer = null;
      if (!current()) return;
      if (globalThis.document?.visibilityState === 'hidden') { arm(500); return; }
      // The awarded seat is enough to start. Prepare while the jackpot loads
      // and animates; waiting for every panel's reads or artwork can starve
      // this warmup until the player clicks. CPU reconstruction uses a worker.
      try {
        const { preloadJackpotDrawRun } = await import('../craps/coin-draw-viewer.js');
        if (!current()) return;
        const prepared = await preloadJackpotDrawRun({ battleKey: model.key, viewerBetId: model.viewerBetId });
        if (!prepared?.ready) { retry(); return; }
        if (current() && prepared?.resultTone) {
          this.#rememberCoinDrawResult(model, prepared.resultTone);
          this.#syncCoinDrawCentre();
        }
      } catch (_error) { retry(); }
    };
    arm();
  }

  #drawToggleReady() {
    return this.#hasBonus
      && this.#mainReadyForBonus()
      && this.#bonusScratchComplete
      && !this.#spinning
      && !this.#drawViewSwitching;
  }

  /** The day's craps-battle model for the viewed wallet, whatever the spin state. */
  #coinDrawModel() {
    if (!this.#coinDrawBattle || Number(this.#coinDrawBattle.day) !== Number(this.#selectedDay)) return null;
    return coinDrawCentreModel(this.#coinDrawBattle, this.#selectedPlayer);
  }

  /** True when the viewed wallet was drawn into the day's jackpot battle. */
  #coinDrawDrawnIn() {
    return this.#coinDrawModel() != null;
  }

  /** The craps-battle centre model, or null — see coin-draw-centre.js for who gets one. */
  #coinDrawCentre() {
    if (!this.#mainSpinComplete || this.#spinning) return null;
    return this.#coinDrawModel();
  }

  #coinDrawSeenStorageKey(model) {
    return `craps-battle-seen:${CHAIN.id}:${model.player}:${model.day}`;
  }

  #coinDrawWasSeen(model) {
    const key = `${model.key}:${model.player}`;
    if (this.#coinDrawSeen.has(key) || hasViewedBattle(model.player, { battleKey: model.key, viewerBetId: model.viewerBetId })) return true;
    try {
      if (globalThis.localStorage?.getItem(this.#coinDrawSeenStorageKey(model)) !== '1') return false;
    } catch { return false; }
    markBattleViewed(model.player, { battleKey: model.key, viewerBetId: model.viewerBetId });
    this.#coinDrawSeen.add(key);
    return true;
  }

  #coinDrawResultTone(model) {
    const key = `${model.key}:${model.player}`;
    let tone = this.#coinDrawResults.get(key);
    if (!tone) {
      try { tone = globalThis.localStorage?.getItem(`${this.#coinDrawSeenStorageKey(model)}:result`); }
      catch { /* private browsing */ }
    }
    return ['won', 'profit', 'lost'].includes(tone) ? tone : null;
  }

  #rememberCoinDrawResult(model, tone) {
    if (!['won', 'profit', 'lost'].includes(tone)) return;
    this.#coinDrawResults.set(`${model.key}:${model.player}`, tone);
    try { globalThis.localStorage?.setItem(`${this.#coinDrawSeenStorageKey(model)}:result`, tone); }
    catch { /* this mount still knows its result */ }
  }

  #markCoinDrawSeen(model, tone = null) {
    markBattleViewed(model.player, { battleKey: model.key, viewerBetId: model.viewerBetId });
    this.#rememberCoinDrawResult(model, tone);
    this.#coinDrawSeen.add(`${model.key}:${model.player}`);
    try { globalThis.localStorage?.setItem(this.#coinDrawSeenStorageKey(model), '1'); }
    catch { /* private browsing: this mount still moves on */ }
  }

  /** The craps battle the LCD still owes the viewer: after every spin, until it is opened. */
  #coinDrawStepDue() {
    if (this.#finalDrawRevealPending()) return null;
    if (!this.#bonusPhase && !this.#centerScratched) return null;
    const model = this.#jackpotSpinsComplete() ? this.#coinDrawCentre() : null;
    return model && !this.#coinDrawWasSeen(model) ? model : null;
  }

  // last-day-jackpot owns DAY SUMMARY on the same key. Tell it when a battle is due so
  // it holds the summary back instead of hiding the battle behind it.
  #publishCoinDrawDue() {
    const due = Boolean(this.#coinDrawStepDue());
    if (due === Boolean(this.hasAttribute?.('data-craps-battle-due'))) return;
    if (due) this.setAttribute?.('data-craps-battle-due', '');
    else this.removeAttribute?.('data-craps-battle-due');
    try {
      this.dispatchEvent(new CustomEvent('replay:craps-battle-due', { detail: { due }, bubbles: true }));
    } catch { /* headless / CustomEvent shim absent */ }
  }

  #syncCoinDrawCentre() {
    const center = this.querySelector?.('[data-bind="center"]');
    const model = this.#coinDrawCentre();
    const covered = this.#bubbleCovers.has('center');
    const shown = Boolean(model) && this.#centerScratched && !covered;
    const seen = shown && this.#coinDrawWasSeen(model);
    const tone = seen ? this.#coinDrawResultTone(model) : null;
    const statusFor = model ? `${model.key}:${model.player}` : null;
    if (statusFor !== this.#coinDrawStatusFor) {
      this.#coinDrawStatusFor = statusFor;
      this.#coinDrawStatus = null;
    }
    // The face is only the dice: loading and failure read on the diamond itself, and the words
    // live on the LCD key and in the tooltip.
    center?.classList?.toggle('replay-ticket-center--craps', shown);
    center?.classList?.toggle('replay-ticket-center--craps-seen', seen);
    if (center?.dataset) {
      if (tone) center.dataset.crapsResult = tone;
      else delete center.dataset.crapsResult;
    }
    center?.classList?.toggle('replay-ticket-center--flame', !this.#bonusPhase
      && this.#centerScratched && !covered && !model && this.#centerWins.length === 0);
    center?.classList?.toggle('is-working', Boolean(model) && this.#coinDrawOpening);
    center?.classList?.toggle('is-error', Boolean(model) && Boolean(this.#coinDrawStatus?.error));
    if (center && !this.#drawToggleReady()) {
      if (shown) {
        center.setAttribute('role', 'button');
        center.setAttribute('tabindex', '0');
        center.setAttribute('aria-disabled', String(this.#coinDrawOpening));
        const outcome = { won: 'You won the battle.', profit: 'You made money.', lost: 'You lost.' }[tone];
        const label = seen ? `Replay Craps battle.${outcome ? ` ${outcome}` : ''}` : model.ariaLabel;
        center.setAttribute('aria-label', label);
        center.title = seen ? label : this.#coinDrawStatus?.error ? this.#coinDrawStatus.message : label;
        if (center.dataset) center.dataset.crapsCentre = model.key;
      } else if (covered || center.dataset?.crapsCentre) {
        delete center.dataset.crapsCentre;
        center.removeAttribute('role');
        center.removeAttribute('tabindex');
        center.removeAttribute('aria-label');
        center.removeAttribute('title');
        center.removeAttribute('aria-disabled');
      }
    } else {
      center?.removeAttribute('aria-disabled');
    }
    return model;
  }

  #setCoinDrawStatus(message = '', { error = false } = {}) {
    this.#coinDrawStatus = message ? { message, error } : null;
    this.#syncCoinDrawCentre();
    this.#syncSpinControlState();
  }

  async #openCoinDrawBattle(opener = null, { replay = false } = {}) {
    const model = this.#coinDrawCentre();
    if (!model || this.#coinDrawOpening || (!replay && this.#coinDrawWasSeen(model))) return false;
    this.#coinDrawOpening = true;
    this.#setCoinDrawStatus('LOADING…');
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let deadline = null;
    try {
      const { openJackpotDrawRun } = await import('../craps/coin-draw-viewer.js');
      // The dice spin while this runs, so it must end: past the deadline the open is
      // abandoned (a late result never pops the table) and the player can try again.
      const timedOut = new Promise((resolve) => {
        deadline = setTimeout(() => {
          controller?.abort();
          resolve({ ok: false, message: 'The battle is taking too long to load. Try again.' });
        }, CRAPS_BATTLE_OPEN_DEADLINE_MS);
      });
      const result = await Promise.race([
        openJackpotDrawRun({ battleKey: model.key, viewerBetId: model.viewerBetId, opener, signal: controller?.signal ?? null }),
        timedOut,
      ]);
      clearTimeout(deadline);
      this.#coinDrawOpening = false;
      if (result?.ok) this.#markCoinDrawSeen(model, result.resultTone);
      this.#setCoinDrawStatus(result?.ok ? '' : String(result?.message || 'Battle replay unavailable.'), { error: !result?.ok });
      return Boolean(result?.ok);
    } catch (error) {
      console.warn('[replay-panel] craps battle open failed', error);
      clearTimeout(deadline);
      controller?.abort();
      this.#coinDrawOpening = false;
      this.#setCoinDrawStatus(String(error?.message || 'Battle replay unavailable.'), { error: true });
      return false;
    }
  }

  #syncDrawToggleAffordance() {
    const center = this.querySelector('[data-bind="center"]');
    if (!center) return;
    const ready = this.#drawToggleReady();
    const prize = this.querySelector('[data-bind="center-prize"]');
    const rewardDescription = prize?.style?.display === 'flex' ? (prize.getAttribute?.('aria-label') || '') : '';
    center.classList.toggle('replay-ticket-center--draw-toggle', ready);
    if (!ready) {
      center.removeAttribute('role');
      center.removeAttribute('tabindex');
      center.removeAttribute('aria-label');
      if (rewardDescription) center.setAttribute('title', rewardDescription);
      else center.removeAttribute('title');
      this.#syncCoinDrawCentre();
      return;
    }
    const destination = this.#bonusPhase ? 'main' : 'bonus';
    center.setAttribute('role', 'button');
    center.setAttribute('tabindex', '0');
    center.setAttribute('aria-label', [rewardDescription, `Show ${destination} jackpot draw`].filter(Boolean).join(' · '));
    center.title = [rewardDescription, `Show ${destination} draw`].filter(Boolean).join(' · ');
    this.#syncCoinDrawCentre();
  }

  async #toggleRevealedDraw() {
    if (this.#drawViewSwitching
      || this.#spinning
      || !this.#hasBonus
      || !this.#mainReadyForBonus()
      || !this.#bonusScratchComplete) return;

    this.#drawViewSwitching = true;
    this.#syncDrawToggleAffordance();
    const showBonus = !this.#bonusPhase;
    try {
      this.#bonusPhase = showBonus;
      if (showBonus) {
        this.#prepareRoll2Prizes();
        await this.#loadFutureTraits({ priority: 'interaction' });
      } else {
        this.#distributePrizesFromRoll1();
      }
      this.#resetMainWidget();
      await this.#runSpin(this.#displayTraitsForRoll(showBonus), {
        instant: true,
        announce: false,
      });
    } catch (error) {
      // Unhandled before: the toggle just did nothing, with the rejection in
      // the console and the affordance already reset by the finally below.
      console.warn('[replay-panel] draw view switch failed', error);
    } finally {
      this.#drawViewSwitching = false;
      this.#syncDrawToggleAffordance();
    }
  }

  /**
   * Distribute Roll 2 prizes into quadrant arrays and center wins.
   * nearFutureWins (traitId != null) → one badge per win row per display-position quadrant.
   * farFutureWins (traitId == null) → center diamond FLIP total.
   * Each win row from /roll2 is already one discrete payout — no expansion needed.
   */
  #distributePrizesFromRoll2(nearFutureWins, farFutureWins) {
    this.#quadWinArrays = [[], [], [], []];
    this.#centerWins = [];

    const MAX_VISUAL_BADGES = 20;
    const totalPerPos = [0, 0, 0, 0];

    for (const win of nearFutureWins) {
      const contractQ = Math.floor(win.traitId / 64);
      const displayPos = DISPLAY_ORDER.indexOf(contractQ);
      if (displayPos < 0 || displayPos > 3) continue;

      totalPerPos[displayPos]++;
      if (this.#quadWinArrays[displayPos].length >= MAX_VISUAL_BADGES) continue;

      const at = win.awardType || '';
      this.#quadWinArrays[displayPos].push({
        awardType: 'aggregated',
        ethTotal: '0',
        flipTotal: (at === 'flip' || at === 'farFutureCoin') ? (win.amount || '0') : '0',
        ticketTotal: (at === 'tickets' || at === 'ticket') ? Number(win.amount || 0) : 0,
        crapsPassTotal: at === 'craps_pass' ? (win.amount || '0') : '0',
        traitId: win.traitId,
        ticketIndex: win.ticketIndex ?? null,
        level: win.level ?? null,
        sourceLevel: win.sourceLevel ?? null,
      });
    }

    // Overflow sentinels
    for (let pos = 0; pos < 4; pos++) {
      const rendered = this.#quadWinArrays[pos].length;
      const total = totalPerPos[pos];
      if (total > rendered) {
        const lastEntry = this.#quadWinArrays[pos][rendered - 1];
        this.#quadWinArrays[pos].push({
          awardType: 'overflow',
          overflowCount: total - rendered,
          traitId: lastEntry ? lastEntry.traitId : null,
          ethTotal: '0',
          flipTotal: '0',
          ticketTotal: 0,
        });
      }
    }

    // Preserve center reward sources for the reveal label and accessible breakdown. Battle FLIP
    // for a wallet drawn into the day's battle is paid on the craps table, as on the main board.
    const onTable = this.#coinDrawDrawnIn();
    for (const win of farFutureWins) {
      if (BigInt(win.amount || '0') > 0n && !(onTable && win.awardType === 'farFutureCoin')) this.#centerWins.push({ ...win });
    }
  }

  /**
   * Reset only the scratch/reveal state of the main widget (canvases, prizes, badges)
   * without touching data-loading state. Used before re-running the spin for Roll 2.
   */
  #resetMainWidget() {
    this.#animId++;
    this.#skipSpinId = null;
    this.#spinning = false;
    this.#sfxScratchStop();

    const quads = this.querySelectorAll('.replay-tq');
    quads.forEach(q => {
      q.classList.remove(
        'revealed', 'q-has-trait', 'q-no-tickets', 'q-traits-pending', 'q-scratchable',
        'q-has-tickets', 'q-public-result', 'q-win-impossible', 'q-win-impossible-lock',
        'q-owned-miss', 'q-player-win', 'q-solo-eth-win', 'q-craps-comp-win', 'q-gold-trait', 'q-scratch-underlay',
        'q-result-pending', 'q-result-revealed',
      );
      const img = q.querySelector('.badge-img');
      if (img) { img.src = ''; img.alt = ''; img.style.opacity = '0'; img.style.display = ''; }
      const canvas = q.querySelector('.replay-scratch-canvas');
      if (canvas) { canvas.style.opacity = '0'; canvas.style.pointerEvents = 'none'; }
      const prize = q.querySelector('.replay-prize-reveal');
      if (prize) {
        prize.classList.remove(
          'visible',
          'replay-bucket-reveal',
          'replay-player-win-reveal',
          ...BUCKET_REVEAL_POSITION_CLASSES,
          ...BUCKET_REVEAL_VARIANT_CLASSES,
        );
        prize.innerHTML = '';
        prize.removeAttribute('aria-label');
      }
    });
    this.#clearScatteredBadges();
    this.#hideCenterScratch();
    this.querySelector('[data-bind="card-grid"]')
      ?.classList.remove('replay-ticket--has-owned-gold');

    this.#quadRevealReady = [false, false, false, false];
    this.#scratched = [false, false, false, false];
    this.#scratchGrids = [null, null, null, null];
    this.#greenRevealed = [false, false, false, false];
    this.#badgesRevealed = [[], [], [], []];
    this.#quadBadgeBounds = [null, null, null, null];
    this.#quadOwned = [false, false, false, false];
    this.#quadPublicSummaries = [null, null, null, null];
    this.#centerScratched = false;
    this.#centerScratchGrid = null;

  }

  async #runSpin(displayTraits, { instant = false, announce = true, scratchedQuadrants = null } = {}) {
    const tutorialFixture = this.#tutorialFixture;
    this.#stopIdleSpin();
    // A live reel replaces the same four <img> sources every 80-200ms. Let the
    // cold decode warmer finish first so intermediate swaps cannot be canceled
    // before they become paintable, leaving the prior frame stuck on screen.
    if (!instant) await this.#badgeWarmPromise;
    if (tutorialFixture !== this.#tutorialFixture) return false;
    const myId = ++this.#animId;
    const spinSelectionKey = this.#selectionKey();
    const spinBonusPhase = this.#bonusPhase;
    if (!instant && announce) {
      this.#announcedSpinId = myId;
      const context = this.#rngDays.find(row => Number(row.day) === Number(this.#selectedDay));
      this.dispatchEvent(new CustomEvent('replay:spin-start', { bubbles: true, detail: {
        day: Number(this.#selectedDay), player: this.#selectedPlayer, runId: myId,
        bonusPhase: spinBonusPhase, phase: context?.phase,
      } }));
    }
    this.#skipSpinId = null;
    this.#spinning = true;
    const quads = this.querySelectorAll('.replay-tq');
    // Track the viewed player's result in each quadrant. The main draw now lets
    // every quadrant scratch; this ownership state still drives the reel colours
    // and win sound. Roll 2 keeps its player-eligible-only scratch behavior.
    if (this.#bonusPhase) {
      for (let i = 0; i < 4; i++) {
        const contractQ = displayTraits[i] != null ? Math.floor(displayTraits[i] / 64) : -1;
        const hasPlayerWin = contractQ >= 0 && this.#bonusQuadrants.has(contractQ);
        // Roll 2 uses future-level holdings. A held offered trait that missed
        // its bucket is still a possible-win blue face, just like Roll 1; an
        // actual win is authoritative even if the by-trait endpoint lags.
        this.#quadOwned[i] = hasPlayerWin
          || (displayTraits[i] != null && this.#futureTraitIds.has(displayTraits[i]));
      }
    } else {
      const roll1TraitIds = new Set(this.#playerRoll1Wins.map(r => r.traitId).filter(t => t != null));
      for (let i = 0; i < 4; i++) {
        // Ownership and winning are different states. A player can hold the
        // offered trait and lose the bucket; that face must stay blue/gold
        // ("didn't win"), not flip to the red "win not possible" treatment.
        // A winner row is also authoritative ownership if the trait endpoint
        // happens to lag.
        this.#quadOwned[i] = displayTraits[i] != null
          && (this.#playerTraitIds.has(displayTraits[i])
            || roll1TraitIds.has(displayTraits[i]));
      }
    }

    // The set the spin colours against: the bonus roll grades the player's
    // future-level holdings, the main roll the day's level.
    const spinOwned = this.#bonusPhase ? this.#futureTraitIds : this.#playerTraitIds;

    // Reset state
    this.#quadRevealReady = [false, false, false, false];
    this.#scratched = [false, false, false, false];
    this.#scratchGrids = [null, null, null, null];
    this.#greenRevealed = [false, false, false, false];
    this.#badgesRevealed = [[], [], [], []];
    this.#quadBadgeBounds = [null, null, null, null];
    this.#centerScratched = false;
    this.#centerScratchGrid = null;
    this.#soloEthCuePlayed = false;
    this.#sfxScratchStop();

    // Start flame spinning animation
    const center = this.querySelector('[data-bind="center"]');
    if (center) center.classList.add('spinning');

    // Hide center scratch canvas and prize
    this.#hideCenterScratch();

    // Clear canvases and prizes
    this.#clearScatteredBadges();
    const mainBadges = this.querySelectorAll('.replay-ticket .badge-img');
    for (const mb of mainBadges) {
      mb.style.display = '';
      mb.removeAttribute('width');
      mb.removeAttribute('height');
    }

    for (let i = 0; i < 4; i++) {
      const canvas = quads[i].querySelector('.replay-scratch-canvas');
      if (canvas) {
        canvas.style.transition = 'none';
        canvas.style.opacity = '0';
        canvas.style.pointerEvents = 'none';
      }
      const prize = quads[i].querySelector('.replay-prize-reveal');
      if (prize) {
        prize.classList.remove(
          'visible',
          'replay-bucket-reveal',
          'replay-player-win-reveal',
          ...BUCKET_REVEAL_POSITION_CLASSES,
          ...BUCKET_REVEAL_VARIANT_CLASSES,
        );
        prize.innerHTML = '';
        prize.removeAttribute('aria-label');
      }
    }

    // Compute target for each display position
    const targets = displayTraits.map((traitId, i) => {
      if (traitId == null) return { contractQ: DISPLAY_ORDER[i], sym: 0, col: 0 };
      const contractQ = Math.floor(traitId / 64);
      const within = traitId % 64;
      // Canonical decode: symbol = bits 2:0 (within % 8), color = bits 5:3
      // (within / 8) — matches traitToBadge / foil-match.js. The prior swap
      // transposed symbol and color on every reel badge.
      return { contractQ, sym: within % 8, col: Math.floor(within / 8) };
    });

    // Both draws expose the complete public board. A player with no winning
    // entry uncovers the public bucket result: badge, per-entry payout, and
    // winning-entry count (ETH for Roll 1, FLIP for Roll 2).
    this.#quadPublicSummaries = this.#bonusPhase
      ? buildRoll2BucketSummaries(this.#dayRoll2?.wins, displayTraits)
      : buildRoll1BucketSummaries(
          this.#dayRoll1?.wins,
          displayTraits,
          this.#openingFlipDay ? 'FLIP' : 'ETH',
          this.#dayRoll1?.decimatorEntries ?? [],
        );

    // The result is already known: build totals, badge prizes and flight geometry
    // under the spinning faces. A later pop only exposes and animates these nodes.
    for (let i = 0; i < 4; i++) {
      this.#preparePlayerWinResult(i, displayTraits[i], quads[i], !instant || scratchedQuadrants != null);
    }
    this.#prepareCenterWinResult();

    // Restoration uses the same ownership/prize preparation as a played spin.
    // Ordinary refresh removes every cover; tutorial navigation restores only
    // the quadrants scratched at that page. No sound or celebration is replayed.
    if (instant) {
      this.#spinning = false;
      for (let i = 0; i < 4; i++) {
        const category = BADGE_QUADRANTS[DISPLAY_ORDER[i]];
        const img = quads[i].querySelector('.badge-img');
        if (img) {
          img.src = this.#resultBadge(displayTraits[i])?.path
            || badgeCircularPath(category, targets[i].sym, targets[i].col);
          img.style.opacity = '1';
        }
      }
      // A fully revealed restore/toggle has no cover to show. Avoid building
      // five bubble trees only to remove them again in the same task.
      this.#afterSpin(displayTraits, targets, quads, {
        announce, covered: scratchedQuadrants != null,
      });
      for (let i = 0; i < 4; i++) {
        if (scratchedQuadrants == null || scratchedQuadrants.includes(DISPLAY_ORDER[i])) {
          this.#revealQuadrant(i, { instant: true, silent: true });
        }
      }
      if (scratchedQuadrants == null) this.#revealCenter({ instant: true, silent: true });
      return true;
    }

    // Spin state
    const lockedColors = [false, false, false, false];
    const lockedSymbols = [false, false, false, false];
    let locksDone = 0;
    const totalLocks = 8;
    let idleCount = 2 + Math.floor(Math.random() * 3);
    let finalLockSettling = false;
    // Phase 64 (app embed): publish two separate views of the reel state.
    // `traits` contains durable locks only: a quadrant commits once BOTH its
    // colour and symbol stop. `liveTraits` is the exact badge painted on this
    // frame, including still-cycling reels. Hosts can therefore replace a
    // transient lamp on every frame without losing already-committed locks.
    // The opening emit contains four nulls in both arrays and clears the prior
    // roll before the first new frame is painted.
    const emitSpinProgress = (liveTraits = [null, null, null, null]) => {
      const traits = [null, null, null, null];
      for (let i = 0; i < 4; i++) {
        if (!lockedSymbols[i] || !lockedColors[i]) continue;
        if (displayTraits[i] == null) continue;
        const { contractQ, col, sym } = targets[i];
        if (!(contractQ >= 0 && contractQ < 4)) continue;
        traits[contractQ] = (contractQ * 64) + (col * 8) + sym;
      }
      if (!announce) return;
      try {
        this.dispatchEvent(new CustomEvent('replay:spin-progress', {
          detail: {
            day: this.#selectedDay,
            player: this.#selectedPlayer,
            bonusPhase: this.#bonusPhase,
            traits,
            liveTraits,
          },
          bubbles: true,
        }));
      } catch { /* headless / CustomEvent shim absent */ }
    };

    setMajorDrawActivity('jackpot-replay', true);
    return new Promise(resolve => {
      let activitySettled = false;
      let frameTimer = null;
      const advance = () => {
        // The flame skips now, including during the final lock's hold. Cancel
        // that timer so it cannot repaint or publish completion a second time.
        clearTimeout(frameTimer);
        step();
      };
      this.#advanceSpin = advance;
      const settle = (completed) => {
        clearTimeout(frameTimer);
        if (this.#advanceSpin === advance) this.#advanceSpin = null;
        if (!activitySettled) {
          activitySettled = true;
          setMajorDrawActivity('jackpot-replay', false);
        }
        resolve(completed);
      };
      const step = () => {
        if (myId !== this.#animId) {
          const intentionalSkip = this.#skipSpinId === myId
            && this.#selectionKey() === spinSelectionKey
            && this.#bonusPhase === spinBonusPhase;
          if (!intentionalSkip) {
            settle(false);
            return;
          }
          this.#skipSpinId = null;
          this.#spinning = false;
          // A center-flame click is an explicit request to skip this exact
          // spin. Paint and reveal one coherent captured main/bonus result.
          for (let i = 0; i < 4; i++) {
            if (this.#quadRevealReady[i]) continue;
            const contractQ = DISPLAY_ORDER[i];
            const category = BADGE_QUADRANTS[contractQ];
            const path = this.#resultBadge(displayTraits[i])?.path
              || badgeCircularPath(category, targets[i].sym, targets[i].col);
            const img = quads[i].querySelector('.badge-img');
            if (img) { img.src = path; img.style.opacity = '1'; }
          }
          this.#afterSpin(displayTraits, targets, quads);
          // Auto-reveal all quadrants and center
          for (let i = 0; i < 4; i++) this.#revealQuadrant(i, { batch: true });
          this.#revealCenter({ batch: true });
          settle(true);
          return;
        }

        // Each locked quadrant is already revealable. Keep the last lock's
        // paint interval before stopping the center and completing the spin.
        if (finalLockSettling) {
          this.#spinning = false;
          for (let i = 0; i < 4; i++) {
            if (this.#quadRevealReady[i]) continue;
            const category = BADGE_QUADRANTS[DISPLAY_ORDER[i]];
            const img = quads[i].querySelector('.badge-img');
            if (img) img.src = this.#resultBadge(displayTraits[i])?.path
              || badgeCircularPath(category, targets[i].sym, targets[i].col);
          }
          this.#afterSpin(displayTraits, targets, quads);
          settle(true);
          return;
        }

        // Advance one color/symbol lock before painting this frame. That makes
        // the selected quadrant visibly assume its final trait and eligibility
        // state at the moment its lock sound plays, including the eighth lock.
        let lockedQuadrant = null;
        if (idleCount <= 0 && locksDone < totalLocks) {
          const available = [];
          for (let q = 0; q < 4; q++) {
            if (!lockedColors[q]) available.push({ q, type: 'color' });
            else if (!lockedSymbols[q]) available.push({ q, type: 'symbol' });
          }
          if (available.length > 0) {
            const pick = available[Math.floor(Math.random() * available.length)];
            if (pick.type === 'color') lockedColors[pick.q] = true;
            else lockedSymbols[pick.q] = true;
            locksDone++;
            if (pick.type === 'symbol') lockedQuadrant = pick.q;
            idleCount = 2 + Math.floor(Math.random() * 3);
          }
        } else {
          idleCount--;
        }

        // Render random or locked badges and capture the exact four traits
        // shown by this painted frame in contract-quadrant order.
        let frameWinnableCount = 0;
        let frameHasGoldWinnable = false;
        const liveTraits = [null, null, null, null];
        for (let i = 0; i < 4; i++) {
          const contractQ = DISPLAY_ORDER[i];
          const locked = lockedSymbols[i] && lockedColors[i];
          const { sym, col } = previewSpinTrait(contractQ,
            lockedSymbols[i] ? targets[i].sym : Math.floor(Math.random() * 8),
            lockedColors[i] ? targets[i].col : Math.floor(Math.random() * 8),
            { symbolLocked: lockedSymbols[i], colorLocked: lockedColors[i] });
          const shownTrait = contractQ * 64 + col * 8 + sym;
          liveTraits[contractQ] = shownTrait;
          if (this.#quadRevealReady[i]) {
            if (this.#quadOwned[i]) {
              frameWinnableCount += 1;
              if (!this.#bonusPhase && targets[i].col === 7) frameHasGoldWinnable = true;
            }
            continue;
          }
          const category = BADGE_QUADRANTS[contractQ];
          const path = locked
            ? this.#resultBadge(displayTraits[i])?.path || badgeCircularPath(category, sym, col)
            : badgeCircularPath(category, sym, col);

          const img = quads[i].querySelector('.badge-img');
          if (img) { img.src = path; img.style.opacity = '1'; }

          // Background colouring during the spin. Blue means "you hold THIS
          // trait" and is checked against the badge currently on screen, so the
          // colour tracks the reels instead of flashing at random (user call).
          // Which holdings count depends on the roll: the main spin draws from
          // the day's level, the bonus spin from the levels above it.
          quads[i].classList.remove(
            'q-has-trait',
            'q-no-tickets',
            'q-traits-pending',
            'q-scratchable',
            'q-has-tickets',
            'q-gold-trait',
            'q-win-impossible-lock',
          );
          if (lockedSymbols[i] && lockedColors[i]) {
            // Fully locked: settle eligibility before preparing this result.
            quads[i].classList.add(this.#quadOwned[i] ? 'q-has-trait' : 'q-no-tickets');
            // A target not owned for this draw = guaranteed loss. Commit the darker
            // cover color as THIS quadrant locks. Result preparation preserves
            // it on the bubble while placing the prize underneath.
            if (!this.#quadOwned[i]) {
              quads[i].classList.add('q-win-impossible-lock');
            } else {
              quads[i].classList.remove('q-win-impossible-lock');
            }
            if (!this.#bonusPhase && this.#quadOwned[i] && targets[i].col === 7) {
              quads[i].classList.add('q-gold-trait');
            }
            if (this.#quadOwned[i]) {
              frameWinnableCount += 1;
              if (!this.#bonusPhase && targets[i].col === 7) frameHasGoldWinnable = true;
            }
          } else {
            quads[i].classList.remove('q-win-impossible-lock');
            const ownsShown = spinOwned.has(shownTrait);
            quads[i].classList.add(ownsShown ? 'q-has-trait' : 'q-no-tickets');
            if (ownsShown) frameWinnableCount += 1;
            if (!this.#bonusPhase && ownsShown && col === 7) {
              quads[i].classList.add('q-gold-trait');
              frameHasGoldWinnable = true;
            }
          }
        }
        if (lockedQuadrant != null) {
          this.#prepareQuadrantReveal(lockedQuadrant, displayTraits, quads);
        }
        this.#syncOwnedGoldState(quads);
        emitSpinProgress(liveTraits);
        // Ordinary frames get one terse digital pulse whose pitch/volume
        // follows the blue count. A lock frame substitutes its red/blue/gold
        // cue instead of stacking both sounds on the same animation frame.
        if (lockedQuadrant != null) {
          this.#sfxLock({
            winnable: this.#quadOwned[lockedQuadrant],
            gold: quads[lockedQuadrant].classList.contains('q-gold-trait'),
          });
        } else {
          this.#sfxSpinFrame(frameWinnableCount, frameHasGoldWinnable, locksDone);
        }

        // Check if all locked
        if (locksDone >= totalLocks) {
          const anyOwned = this.#quadOwned.some(o => o);
          this.#sfxAllLocked(anyOwned);
          finalLockSettling = true;
          frameTimer = setTimeout(step, FINAL_LOCK_SETTLE_MS);
          return;
        }

        const delay = 80 + Math.floor((locksDone / totalLocks) * 120);
        frameTimer = setTimeout(step, delay);
      };
      emitSpinProgress();
      step();
    });
  }

  #resultBadge(traitId) {
    // Every confirmed gold-six result in this widget uses the monkey, including
    // a legacy second draw. Cosmetic reel frames use the preview filter instead.
    return traitToBadge(traitId, { mainJackpot: true });
  }

  #prepareQuadrantReveal(i, displayTraits, quads, { covered = true } = {}) {
    // A landed quadrant owns its result from here on. Later reel frames and
    // whole-ticket completion must not rebuild a popped bubble or its prizes.
    if (this.#quadRevealReady[i]) return;
    quads[i].classList.remove(
      'q-has-trait',
      'q-no-tickets',
      'q-traits-pending',
      'q-public-result',
      'q-win-impossible',
      'q-win-impossible-lock',
      'q-owned-miss',
      'q-player-win',
      'q-gold-trait',
      'q-scratch-underlay',
      'q-result-pending',
      'q-result-revealed',
      'q-solo-eth-win', 'q-craps-comp-win',
    );
    const hasPlayerWin = this.#quadWinArrays[i]
      .some((d) => d.awardType !== 'overflow');
    const isSoloEthWin = hasPlayerWin
      && !this.#bonusPhase
      && this.#isSoloEthWinner(i);
    const heldTraits = this.#bonusPhase ? this.#futureTraitIds : this.#playerTraitIds;
    const winnerProvesDisplayedOwnership = this.#quadWinArrays[i]
      .some((d) => d.traitId != null && Number(d.traitId) === Number(displayTraits[i]));
    const ownsDisplayedTrait = displayTraits[i] != null
      && (heldTraits.has(displayTraits[i]) || winnerProvesDisplayedOwnership);
    // Gold is a special main-jackpot eligibility signal. The bonus jackpot
    // does not pay that gold-trait mechanic, so its held/winning gold badges
    // keep the ordinary blue/green treatment instead of advertising a prize
    // that is not available in Roll 2.
    const ownsDisplayedGold = !this.#bonusPhase
      && ownsDisplayedTrait
      && isGoldTrait(displayTraits[i]);
    const publicResult = !hasPlayerWin
      ? (this.#quadPublicSummaries[i] || {
          traitId: displayTraits[i],
          winnerCount: null,
          perWinWei: null,
          currency: (this.#bonusPhase || this.#openingFlipDay) ? 'FLIP' : 'ETH',
        })
      : null;
    // The bubble carries blue/red eligibility. Beneath it, keep
    // potential/actual wins neutral until the completion threshold (or an actual
    // win badge is uncovered). The same neutral grey also sits beneath a
    // darker-pink known-loss cover, so a partial scratch never leaks the final
    // pink paper before the completion threshold.
    quads[i].classList.add('q-scratchable');
    quads[i].classList.add('q-scratch-underlay');
    if (isSoloEthWin) quads[i].classList.add('q-solo-eth-win');
    if (this.#quadWinArrays[i].some((win) => winningBadgeRewardLines(win)
      .some((line) => line.kind === 'craps-pass'))) {
      quads[i].classList.add('q-craps-comp-win');
    }
    if (ownsDisplayedGold) {
      quads[i].classList.add('q-gold-trait');
    } else if (hasPlayerWin) {
      quads[i].classList.add('q-player-win');
    } else if (publicResult && ownsDisplayedTrait) {
      quads[i].classList.add('q-owned-miss');
    } else if (publicResult) {
      quads[i].classList.add('q-win-impossible');
    }
    if (!(publicResult && !ownsDisplayedTrait)) {
      quads[i].classList.add('q-result-pending');
    }
    const badge = quads[i].querySelector('.badge-img');
    if (covered) this.#mountQuadrantBubble(i, badge?.src || '',
      hasPlayerWin || ownsDisplayedTrait ? POSSIBLE_WIN_COVER_FILL : NO_WIN_COVER_FILL);
    const prize = quads[i].querySelector('.replay-prize-reveal');
    if (prize) { prize.inert = true; prize.setAttribute('aria-hidden', 'true'); }

    if (publicResult) {
      quads[i].classList.add('q-public-result');
      // The badge is already painted into the removable canvas; the smaller
      // result badge below should be what appears as the player scratches.
      if (badge) badge.style.display = 'none';
      this.#renderPublicBucketReveal(i, publicResult);
    }

    const wins = this.#quadWinArrays[i];
    if (hasPlayerWin) {
      const mainBadge = quads[i].querySelector('.badge-img');

      // Determine the canonical winning traitId for this quadrant.
      // For Roll 2 bonus the displayed RNG trait can differ from the player's
      // actual winning trait — use the first win entry's traitId when present.
      const canonicalTraitId = wins.length > 0 && wins[0].traitId != null
        ? wins[0].traitId
        : displayTraits[i];
      const canonicalBadge = this.#resultBadge(canonicalTraitId);
      const canonicalSrc = canonicalBadge ? canonicalBadge.path : (mainBadge ? mainBadge.src : '');

      // The cover and every result retain their authoritative winning trait.
      if (canonicalSrc) {
        if (covered) this.#mountQuadrantBubble(i, canonicalSrc, POSSIBLE_WIN_COVER_FILL);
        if (!this.#bonusPhase && isGoldTrait(canonicalTraitId)) {
          quads[i].classList.add('q-gold-trait');
          quads[i].classList.remove('q-player-win');
        } else quads[i].classList.add('q-player-win');
      }
      // Also update the visible badge-img to match (shown briefly before hide).
      if (mainBadge && canonicalSrc) mainBadge.src = canonicalSrc;

      if (mainBadge) mainBadge.style.display = 'none';
    }
    quads[i].classList.remove('q-reveal-preparing');
    this.#quadRevealReady[i] = true;
  }

  #afterSpin(displayTraits, targets, quads, { announce = true, covered = true } = {}) {
    // Stop flame spinning
    const center = this.querySelector('[data-bind="center"]');
    if (center) center.classList.remove('spinning');

    let anyScratchable = false;
    for (let i = 0; i < 4; i++) {
      this.#prepareQuadrantReveal(i, displayTraits, quads, { covered });
      anyScratchable = true;
    }
    this.#syncOwnedGoldState(quads);

    // The main center is its own reveal, including a flame when no battle was drawn.
    if (!this.#bonusPhase || this.#centerWins.length > 0) {
      if (covered) this.#showCenterScratch();
      anyScratchable = true;
    }

    // Blue/gold already identifies possible wins. Require those covers even
    // when they hide an owned miss; red guaranteed-loss results stay optional.
    if (!this.#bonusPhase) this.#refreshMainPotentialScratchGate();

    // Phase 64 (app embed): announce spin completion so host shells can sync
    // post-spin visuals such as foil match lighting. Scratch completion below
    // remains the spoiler/persistence gate. Additive — no behavior change for
    // /play or /beta consumers.
    if (announce) {
      try {
        this.dispatchEvent(new CustomEvent('replay:spin-complete', {
          detail: {
            day: this.#selectedDay,
            player: this.#selectedPlayer,
            bonusPhase: this.#bonusPhase,
          },
          bubbles: true,
        }));
      } catch { /* headless / CustomEvent shim absent */ }
    }

    // Nothing scratchable this roll (defensive malformed/empty board) —
    // the reveal is trivially complete, so fire scratch-complete right away
    // or host gates would never open on lossless days.
    if (!anyScratchable && announce) this.#dispatchScratchComplete();
    // A bonus draw without a center prize can have every bubble popped before
    // its final settling frame. Publish completion only once the spin finishes.
    else if (announce && this.#scratched.every(Boolean)) this.#checkAllScratched();
  }

  // Phase 64 (app embed): announce full scratch completion — every owned
  // quadrant revealed and the center diamond (when present) scratched.
  // Fires once per roll (Roll 1 and the bonus Roll 2 complete independently);
  // #revealQuadrant/#revealCenter guards keep it single-shot within a roll.
  // Additive — no behavior change for /play or /beta consumers.
  //
  // detail.bonusAvailable — a bonus Roll 2 is still ahead of the player
  // (eligible + not yet in the bonus phase). Hosts that gate "the whole
  // board is played out" on this: final = bonusPhase || !bonusAvailable.
  #dispatchScratchComplete() {
    try {
      this.dispatchEvent(new CustomEvent('replay:scratch-complete', {
        detail: {
          day: this.#selectedDay,
          player: this.#selectedPlayer,
          bonusPhase: this.#bonusPhase,
          bonusAvailable: this.#hasBonus && !this.#bonusPhase,
        },
        bubbles: true,
      }));
    } catch { /* headless / CustomEvent shim absent */ }
  }

  #clearBubbleCovers() {
    for (const { cover, button } of this.#bubbleCovers.values()) {
      cover.dispose(); button.remove();
    }
    this.#bubbleCovers.clear();
  }

  #mountQuadrantBubble(index, badgeSrc, color, ready = true) {
    const quad = this.querySelectorAll('.replay-tq')[index];
    if (!quad) return;
    badgeSrc = new URL(badgeSrc, (this.ownerDocument || document).baseURI).href;
    const previous = this.#bubbleCovers.get(index);
    if (previous?.badgeSrc === badgeSrc && previous?.color === color) {
      previous.button.disabled = !ready;
      return;
    }
    previous?.cover.dispose();
    previous?.button.remove();
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'replay-bubble-trigger';
    button.setAttribute('data-button-feedback', 'off');
    button.disabled = !ready;
    button.setAttribute('aria-label', `Pop ${['top left', 'top right', 'bottom left', 'bottom right'][index]} quadrant`);
    const cover = createBubbleCover(this.ownerDocument || document, { badgeSrc, color, durationMs: JACKPOT_POP_MS });
    button.appendChild(cover.element);
    button.addEventListener('click', event => {
      event.stopPropagation();
      this.#revealQuadrant(index, { event });
    });
    quad.appendChild(button);
    this.#bubbleCovers.set(index, { cover, button, badgeSrc, color });
  }

  #popBubble(key, event, instant, finish = () => {}, batch = false) {
    const entry = this.#bubbleCovers.get(key);
    if (!entry) { finish(); return; }
    entry.button.setAttribute('aria-disabled', 'true');
    entry.cover.pop(event, { instant, batch, onComplete: () => {
      const focused = this.ownerDocument?.activeElement === entry.button;
      const parent = entry.button.parentElement;
      entry.button.remove();
      this.#bubbleCovers.delete(key);
      finish();
      if (focused && parent) {
        const target = parent.querySelector('.replay-badge-wrap') || parent;
        if (target === parent && parent.getAttribute('role') !== 'button') target.tabIndex = -1;
        target.focus?.({ preventScroll: true });
      }
    } });
  }

  // --- Canvas scratch initialization ---

  /**
   * Paint a main-draw bucket result under the scratch cover when the viewed
   * player has no winning entry in that quadrant.
   */
  #renderPublicBucketReveal(qIdx, summary) {
    const quads = this.querySelectorAll('.replay-tq');
    const host = quads[qIdx] && quads[qIdx].querySelector('.replay-prize-reveal');
    if (!host) return;
    host.textContent = '';
    host.classList.remove(
      'replay-player-win-reveal',
      ...BUCKET_REVEAL_POSITION_CLASSES,
      ...BUCKET_REVEAL_VARIANT_CLASSES,
    );
    host.classList.add('replay-bucket-reveal', `replay-bucket-reveal--q${qIdx}`);
    if (!this.#bonusPhase) host.classList.add('replay-bucket-reveal--main-miss');

    const currencyWinnerCount = summary.winnerCount == null
      ? null
      : Number(summary.winnerCount);
    const currency = summary.currency === 'FLIP' ? 'FLIP' : 'ETH';
    const isSoloEth = currency === 'ETH' && currencyWinnerCount === 1;
    if (isSoloEth) {
      host.classList.add('replay-bucket-reveal--solo-eth');
    }

    const badge = this.#resultBadge(summary.traitId);
    // Keep badge geometry independent from the variable-height receipt below.
    // A solo result may use larger type or omit tickets entirely; neither may
    // move the badge off the center shared by its neighboring quadrant.
    const badgeStage = document.createElement('div');
    badgeStage.className = 'replay-bucket-badge-stage';
    if (badge) {
      const badgeImg = document.createElement('img');
      badgeImg.className = 'replay-bucket-badge';
      badgeImg.src = badge.path;
      badgeImg.alt = '';
      badgeStage.appendChild(badgeImg);
    }
    host.appendChild(badgeStage);

    const receipt = document.createElement('div');
    receipt.className = 'replay-bucket-receipt';

    const amount = document.createElement('div');
    amount.className = 'replay-bucket-amount';
    if (isSoloEth) amount.classList.add('replay-bucket-amount--solo-eth');
    const num = document.createElement('span');
    num.className = 'replay-bucket-value';
    const formattedCurrencyAward = summary.perWinWei == null
      ? '—'
      : currency === 'FLIP'
        ? formatFlip(summary.perWinWei.toString())
        : currencyWinnerCount === 1
          ? formatEthTruncated(summary.perWinWei.toString())
          : formatEth(summary.perWinWei.toString());
    num.textContent = formattedCurrencyAward;
    const currencyIcon = document.createElement('img');
    currencyIcon.className = 'replay-bucket-eth replay-bucket-currency';
    currencyIcon.src = currency === 'FLIP'
      ? '/whitepaper/flame-logo-split.svg'
      : '/symbols/crypto_06_ethereum_silver.svg';
    currencyIcon.alt = currency;
    if (currency === 'FLIP') {
      amount.appendChild(currencyIcon);
      amount.appendChild(num);
    } else {
      amount.appendChild(num);
      amount.appendChild(currencyIcon);
    }
    // The featured treatment already communicates that this is the lone ETH
    // winner; repeating ×1 beside it adds noise without adding information.
    if (!isSoloEth) {
      const currencyWinners = document.createElement('span');
      currencyWinners.className = 'replay-bucket-row-count replay-bucket-row-count--currency';
      currencyWinners.textContent = `×${Number.isFinite(currencyWinnerCount) ? currencyWinnerCount : '—'}`;
      amount.appendChild(currencyWinners);
    }
    const passes = summary.crapsPasses;
    const decimator = summary.decimatorEntries;
    const hasPassAward = Number(passes?.winnerCount) > 0;
    // A comp quadrant paid passes instead of FLIP. Never label it 0 FLIP.
    if ((!hasPassAward && !decimator) || currencyWinnerCount > 0) receipt.appendChild(amount);
    let decimatorLabel = '';
    if (decimator) {
      const row = document.createElement('div');
      row.className = 'replay-bucket-tickets replay-bucket-decimator';
      const icon = document.createElement('img'); icon.src = '/app/assets/decimator-draw-mark.svg?v=craps-battle-v3'; icon.alt = '';
      const value = document.createElement('span'); value.className = 'replay-bucket-ticket-count';
      value.textContent = `×${Number(decimator.entries).toLocaleString('en-US')}`;
      row.append(icon, value); receipt.appendChild(row);
      decimatorLabel = `${decimator.entries} Decimator entries, ${decimator.survivors} survived the draw`;
      row.setAttribute('aria-label', decimatorLabel);
    }
    let passAwardLabel = '';
    if (hasPassAward) {
      const count = String(passes.total);
      const row = document.createElement('div');
      row.className = 'replay-bucket-tickets replay-bucket-craps';
      const value = document.createElement('span');
      value.className = 'replay-bucket-ticket-count';
      value.textContent = count;
      const unit = document.createElement('span');
      unit.className = 'replay-bucket-craps__unit';
      unit.textContent = passes.total === 1n ? 'CRAPS COMP' : 'CRAPS COMPS';
      row.appendChild(value);
      row.appendChild(unit);
      receipt.appendChild(row);
      passAwardLabel = `${count} Craps ${passes.total === 1n ? 'comp' : 'comps'}`;
    }

    let whaleAwardLabel = '';
    if (isSoloEth && summary.whaleHalfPasses > 0n) {
      host.classList.add('replay-bucket-reveal--whale');
      whaleAwardLabel = formatWhalePassAward(summary.whaleHalfPasses);
      const row = document.createElement('div');
      row.className = 'replay-bucket-whale';
      row.setAttribute('aria-label', whaleAwardLabel);
      row.title = whaleAwardLabel;
      const icon = document.createElement('img'); icon.src = '/app/assets/whale-pass-mark.svg'; icon.alt = '';
      const count = document.createElement('span'); count.textContent = `×${formatWhalePassAmount(summary.whaleHalfPasses)}`;
      row.append(icon, count);
      receipt.appendChild(row);
    }

    const ticketAwards = (summary.ticketAwards || []).filter(award => award.entries > 0n);
    const ticketAwardLabels = [];
    for (const award of ticketAwards) {
      const ticketCount = joScaledToTickets(award.entries);
      const ticketCountLabel = ticketCount.toLocaleString('en-US', { maximumFractionDigits: 2 });
      const drawLabel = award.sourceLevel == null || award.level == null ? 'Ticket draw'
        : award.sourceLevel === award.level ? 'Early-bird draw'
          : award.sourceLevel + 1 === award.level ? 'Daily draw' : `Level ${award.sourceLevel} draw`;
      const awardLabel = `${drawLabel}: ${ticketCountLabel} ticket${ticketCount === 1 ? '' : 's'} each, ${award.winnerCount} awards`;
      ticketAwardLabels.push(awardLabel);
    }
    // The public loss receipt shows all tickets awarded in this bucket as one
    // pile. Sum entry units before conversion so fractional tickets stay exact.
    const displayedTicketEntries = positiveBigInt(summary.ticketEntriesTotal)
      + (isSoloEth ? 0n : whalePassTicketEntries(summary.whaleHalfPasses));
    if (displayedTicketEntries > 0n) {
      const totalTickets = joScaledToTickets(displayedTicketEntries);
      const countLabel = totalTickets.toLocaleString('en-US', { maximumFractionDigits: 2 });
      const tickets = document.createElement('div');
      tickets.className = 'replay-bucket-tickets';
      const totalLabel = `${countLabel} ticket${totalTickets === 1 ? '' : 's'} awarded in total`;
      tickets.title = [totalLabel, ...ticketAwardLabels].join('; ');
      tickets.setAttribute('aria-label', totalLabel);
      const pile = document.createElement('span');
      pile.className = 'replay-bucket-ticket-pile';
      pile.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < 3; i++) pile.appendChild(createJackpotTicketIcon());
      const count = document.createElement('span');
      count.className = 'replay-bucket-ticket-count';
      count.textContent = `×${countLabel}`;
      tickets.appendChild(pile);
      tickets.appendChild(count);
      receipt.appendChild(tickets);
    }
    host.appendChild(receipt);

    const perWin = summary.perWinWei == null
      ? `unknown ${currency}`
      : `${formattedCurrencyAward} ${currency}`;
    const currencyWinnersLabel = Number.isFinite(currencyWinnerCount)
      ? `${currencyWinnerCount} currency winner${currencyWinnerCount === 1 ? '' : 's'}`
      : 'unknown currency winners';
    const ticketAwardLabel = ticketAwardLabels.length ? `; ${ticketAwardLabels.join('; ')}` : '';
    host.setAttribute(
      'aria-label',
      `You did not win this quadrant. Bucket result: ${decimator ? decimatorLabel : hasPassAward ? passAwardLabel : `currency award ${perWin}, ${currencyWinnersLabel}`}${whaleAwardLabel ? `; ${whaleAwardLabel}` : ''}${ticketAwardLabel}`,
    );
  }

  #syncOwnedGoldState(quads = this.querySelectorAll('.replay-tq')) {
    const hasOwnedGold = Array.from(quads || [])
      .some((quad) => quad.classList?.contains('q-gold-trait'));
    this.querySelector('[data-bind="card-grid"]')
      ?.classList.toggle('replay-ticket--has-owned-gold', hasOwnedGold);
  }

  /** Render the viewed player's actual payout inside a revealed win quadrant. */
  #preparePlayerWinResult(qIdx, traitId, quad, animate) {
    const wins = this.#quadWinArrays[qIdx];
    if (!wins.some(win => win.awardType !== 'overflow')) return;
    quad.classList.add('q-reveal-preparing');
    const prize = quad.querySelector('.replay-prize-reveal');
    if (prize) { prize.inert = true; prize.setAttribute('aria-hidden', 'true'); }
    this.#preparedWinReceipts[qIdx] = this.#renderPlayerWinReveal(qIdx, prize);
    const canonical = wins[0]?.traitId ?? traitId;
    this.#placeWinBadges(qIdx, canonical);
    const overflow = wins.find(win => win.awardType === 'overflow');
    if (overflow?.overflowCount > 0) {
      const label = document.createElement('div');
      label.className = 'replay-badge-overflow-label';
      label.textContent = '+' + overflow.overflowCount + ' more';
      label.setAttribute('aria-hidden', 'true');
      quad.appendChild(label);
    }
    if (animate) prepareWinFlights(quad, this.querySelector('[data-bind="center"]'));
  }

  #renderPlayerWinReveal(qIdx, host) {
    if (!host) return;
    const wins = (this.#quadWinArrays[qIdx] || [])
      .filter((win) => win.awardType !== 'overflow');
    if (wins.length === 0) return;

    let ethTotal = 0n;
    let flipTotal = 0n;
    let dgnrsTotal = 0n;
    let ticketEntries = 0;
    let whaleCount = 0;
    let crapsPassTotal = 0n;
    let decimatorEntryTotal = 0n;
    for (const win of wins) {
      const type = String(win.awardType || '').toLowerCase();
      if (type === 'aggregated') {
        ethTotal += BigInt(win.ethTotal || '0');
        flipTotal += BigInt(win.flipTotal || '0');
        dgnrsTotal += BigInt(win.dgnrsTotal || '0');
        ticketEntries += Number(win.ticketTotal || 0);
        whaleCount += Number(win.whalePassCount || 0);
        crapsPassTotal += positiveBigInt(win.crapsPassTotal);
        decimatorEntryTotal += positiveBigInt(win.decimatorEntryTotal);
      } else if (type === 'flip' || type === 'farfuturecoin' || win.currency === 'FLIP') {
        flipTotal += BigInt(win.amount || '0');
      } else if (type === 'dgnrs' || win.currency === 'DGNRS') {
        dgnrsTotal += BigInt(win.amount || '0');
      } else if (type === 'tickets' || type === 'ticket') {
        ticketEntries += Number(win.amount || 0);
      } else if (type === 'craps_pass') {
        crapsPassTotal += positiveBigInt(win.amount);
      } else if (type === 'decimator_entry') {
        decimatorEntryTotal += positiveBigInt(win.amount);
      } else if (type === 'whale_pass') {
        whaleCount += Number(win.amount || 1);
      } else {
        ethTotal += BigInt(win.amount || '0');
      }
    }

    const lines = [];
    const isSoloBucket = Number(this.#quadPublicSummaries[qIdx]?.winnerCount) === 1
      || wins.some((win) => win.isSolo);
    if (ethTotal > 0n) {
      const formattedEth = isSoloBucket
        ? formatEthTruncated(ethTotal.toString())
        : formatEth(ethTotal.toString());
      lines.push({ text: `${formattedEth} ETH`, aria: `${formattedEth} ETH` });
    }
    if (flipTotal > 0n) {
      const amount = formatFlip(flipTotal.toString());
      lines.push({
        text: amount,
        aria: `${amount} FLIP`,
        icon: '/whitepaper/flame-logo-split.svg',
      });
    }
    if (dgnrsTotal > 0n) {
      const amount = formatFlip(dgnrsTotal.toString());
      lines.push({ text: `${amount} DGNRS`, aria: `${amount} DGNRS` });
    }
    if (isSoloBucket && whaleCount > 0) {
      lines.push({ text: `×${formatWhalePassAmount(whaleCount)}`, aria: formatWhalePassAward(whaleCount),
        icon: '/app/assets/whale-pass-mark.svg', kind: 'whale-pass' });
    }
    // JackpotTicketWin stores entryCount (4 entries = one whole ticket).
    // Day Summary already uses this conversion; keeping raw entries here is
    // what produced contradictory receipts such as 56 tickets versus 14.
    const ticketCount = joScaledToTickets(ticketEntries
      + (isSoloBucket ? 0 : Number(whalePassTicketEntries(whaleCount))));
    if (ticketCount > 0) {
      const text = `${ticketCount} ticket${ticketCount === 1 ? '' : 's'}`;
      lines.push({ text, aria: text });
    }
    if (crapsPassTotal > 0n) {
      const text = `${crapsPassTotal} Craps comp${crapsPassTotal === 1n ? '' : 's'}`;
      lines.push({ text, aria: text, kind: 'craps-comp' });
    }
    if (decimatorEntryTotal > 0n) {
      const text = `${decimatorEntryTotal} Decimator ${decimatorEntryTotal === 1n ? 'entry' : 'entries'}`;
      lines.push({ text, aria: text, icon: '/app/assets/decimator-draw-mark.svg?v=craps-battle-v3', kind: 'decimator-entry' });
    }
    if (lines.length === 0) {
      const text = `${wins.length} win${wins.length === 1 ? '' : 's'}`;
      lines.push({ text, aria: text });
    }

    host.textContent = '';
    host.classList.remove(
      'replay-bucket-reveal',
      ...BUCKET_REVEAL_POSITION_CLASSES,
      ...BUCKET_REVEAL_VARIANT_CLASSES,
    );
    host.classList.add('replay-player-win-reveal');
    const receipt = document.createElement('div');
    // Keep the receipt sealed until reveal. Individual prize callouts leave
    // the total visible; automatic flights reserve its bounds.
    receipt.className = 'replay-win-description is-waiting-for-reward-popups';
    if (crapsPassTotal > 0n) receipt.classList.add('replay-win-description--craps-comp');
    receipt.dataset.rewardPopGate = 'pending';
    const title = document.createElement('strong');
    title.className = 'replay-win-description__title';
    title.textContent = 'YOU WON';
    receipt.appendChild(title);
    const details = document.createElement('div');
    details.className = 'replay-win-description__lines';
    for (const line of lines) {
      const item = document.createElement('span');
      item.className = 'replay-win-description__line';
      if (line.kind === 'craps-comp') item.classList.add('replay-win-description__line--craps-comp');
      if (line.kind === 'whale-pass') {
        item.classList.add('replay-win-description__line--whale');
        item.setAttribute('aria-label', line.aria);
        item.title = line.aria;
      }
      if (line.icon) {
        const icon = document.createElement('img');
        icon.className = 'replay-win-description__currency-icon';
        icon.src = line.icon;
        icon.alt = '';
        item.appendChild(icon);
      }
      const copy = document.createElement('span');
      copy.className = 'replay-win-description__line-copy';
      copy.textContent = line.text;
      item.appendChild(copy);
      details.appendChild(item);
    }
    receipt.appendChild(details);
    host.appendChild(receipt);
    host.setAttribute('aria-label', `You won ${lines.map((line) => line.aria).join(', ')}`);
    return receipt;
  }

  #syncWinReceiptVisibility() {

    for (const receipt of this.querySelectorAll('.replay-win-description')) {
      const openingGateActive = receipt.dataset.rewardPopGate === 'pending';
      receipt.classList.toggle(
        'is-waiting-for-reward-popups',
        openingGateActive,
      );
    }
  }

  #releaseWinReceipt(receipt) {
    if (receipt) delete receipt.dataset.rewardPopGate;
    this.#syncWinReceiptVisibility();
  }

  #initScratchCanvasWithBadge(canvas, badgeSrc, fillColor) {
    const quad = canvas.parentElement;
    const rect = quad.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    // The style box is in layout px; the rect is zoomed when the desktop board scales.
    const zoom = quad.currentCSSZoom || 1;
    canvas.style.width = (rect.width / zoom) + 'px';
    canvas.style.height = (rect.height / zoom) + 'px';
    const ctx = canvas.getContext('2d');
    // Draw the cover with badge image (matching demo's drawBadgeCover). Default
    // is the blue scratch cover; a known loser passes the darker locked-face
    // pink while the lighter loser paper remains visible through scratches.
    ctx.fillStyle = fillColor || POSSIBLE_WIN_COVER_FILL;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const img = new Image();
    img.onload = () => {
      // Circular badge SVGs have a generous transparent artboard. Match the
      // larger live badge without letting the painted ring crowd the center
      // diamond; the canvas clips the small intentional outer bleed.
      const size = Math.min(canvas.width, canvas.height) * 1.18;
      const x = (canvas.width - size) / 2;
      const y = (canvas.height - size) / 2;
      ctx.drawImage(img, x, y, size, size);
    };
    img.src = badgeSrc;
  }

  #scratchAt(canvas, cx, cy) {
    const dpr = window.devicePixelRatio || 1;
    const r = BRUSH_R * dpr;
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  }

  // --- Quadrant scratch wiring ---

  #wireCanvas(canvas, qIdx) {
    const knownLoser = canvas.parentElement?.classList?.contains('q-win-impossible');
    const revealThreshold = knownLoser
      ? KNOWN_LOSER_REVEAL_THRESHOLD
      : REVEAL_THRESHOLD;
    let lastPos = null;
    const onScratch = (cx, cy) => {
      if (this.#scratched[qIdx]) return;
      this.#sfxScratchStart();
      const dpr = window.devicePixelRatio || 1;
      const brushR = BRUSH_R * dpr;
      this.#scratchAt(canvas, cx, cy);
      if (!this.#scratchGrids[qIdx]) this.#scratchGrids[qIdx] = makeScratchGrid(GRID_RES);
      markGridCells(this.#scratchGrids[qIdx], GRID_RES, canvas.width, canvas.height, cx, cy, brushR);

      // Check if the scratch stroke reveals a paid badge and its win surface.
      if (this.#quadWinArrays[qIdx].length > 0 && this.#quadBadgeBounds[qIdx]) {
        const pctX = (cx / canvas.width) * 100;
        const pctY = (cy / canvas.height) * 100;
        const circles = this.#quadBadgeBounds[qIdx];
        const quads = this.querySelectorAll('.replay-tq');
        const quad = quads[qIdx];
        const badgeWraps = quad?.querySelectorAll?.('.replay-badge-wrap') || [];
        for (let ci = 0; ci < circles.length; ci++) {
          if (this.#badgesRevealed[qIdx].indexOf(ci) !== -1) continue;
          const ddx = pctX - circles[ci].cx, ddy = pctY - circles[ci].cy;
          if (ddx * ddx + ddy * ddy <= circles[ci].r * circles[ci].r) {
            this.#badgesRevealed[qIdx].push(ci);
            if (!this.#greenRevealed[qIdx]) {
              this.#greenRevealed[qIdx] = true;
              quad.classList.remove('q-scratchable', 'q-scratch-underlay', 'q-result-pending');
              quad.classList.add('q-result-revealed', 'q-has-tickets');
            }
            // The reward readout belongs to the exact paid-badge hit, not the
            // later full-quadrant threshold. Keep it on the same event turn as
            // the arcade coin cue so sight and sound identify one reveal.
            this.#sfxGreenReveal();
            this.#activateBadgeReward(badgeWraps[ci]);
          }
        }
      }

      // Interpolate between last position for smooth strokes
      if (lastPos) {
        const dx = cx - lastPos.x, dy = cy - lastPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const steps = Math.max(1, Math.floor(dist / 4));
        for (let s = 1; s < steps; s++) {
          const ix = lastPos.x + dx * s / steps, iy = lastPos.y + dy * s / steps;
          this.#scratchAt(canvas, ix, iy);
          markGridCells(this.#scratchGrids[qIdx], GRID_RES, canvas.width, canvas.height, ix, iy, brushR);
        }
      }
      lastPos = { x: cx, y: cy };
      if (gridCoverage(this.#scratchGrids[qIdx]) >= revealThreshold) {
        this.#revealQuadrant(qIdx);
      }
    };

    const getPos = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      return { x: (clientX - rect.left) * dpr, y: (clientY - rect.top) * dpr };
    };

    const onMouseMove = (e) => {
      if (this.#scratched[qIdx]) return;
      const p = getPos(e.clientX, e.clientY);
      onScratch(p.x, p.y);
    };
    const onMouseLeave = () => { lastPos = null; this.#sfxScratchStop(); };
    const onTouchStart = (e) => {
      if (this.#scratched[qIdx]) return;
      e.preventDefault(); lastPos = null;
      const t = e.touches[0], p = getPos(t.clientX, t.clientY);
      onScratch(p.x, p.y);
    };
    const onTouchMove = (e) => {
      if (this.#scratched[qIdx]) return;
      e.preventDefault();
      const t = e.touches[0], p = getPos(t.clientX, t.clientY);
      onScratch(p.x, p.y);
    };
    const onTouchEnd = () => { lastPos = null; this.#sfxScratchStop(); };
    replaceScratchListeners(canvas, [
      { type: 'mousemove', handler: onMouseMove },
      { type: 'mouseleave', handler: onMouseLeave },
      { type: 'touchstart', handler: onTouchStart, options: { passive: false } },
      { type: 'touchmove', handler: onTouchMove, options: { passive: false } },
      { type: 'touchend', handler: onTouchEnd },
    ]);
  }

  // --- Center diamond scratch ---

  #hideCenterScratch() {
    const bubble = this.#bubbleCovers.get('center');
    bubble?.cover.dispose(); bubble?.button.remove();
    this.#bubbleCovers.delete('center');
    const canvas = this.querySelector('[data-bind="center-canvas"]');
    const prize = this.querySelector('[data-bind="center-prize"]');
    const flame = this.querySelector('.replay-flame');
    const center = this.querySelector('[data-bind="center"]');
    if (canvas) { canvas.style.display = 'none'; canvas.style.opacity = '1'; canvas.style.pointerEvents = 'auto'; }
    if (prize) {
      prize.style.display = 'none';
      prize.innerHTML = '';
      prize.classList.remove('visible', 'replay-bucket-reveal');
      prize.removeAttribute('aria-label');
    }
    if (flame) { flame.style.display = ''; flame.style.filter = ''; }
    if (center) { center.classList.remove('revealed', 'replay-ticket-center--covered',
      'replay-ticket-center--popped', 'replay-ticket-center--flame', 'replay-ticket-center--craps'); }
  }

  #showCenterScratch() {
    const center = this.querySelector('[data-bind="center"]');
    if (!center || this.#bubbleCovers.has('center')) return;
    center.classList.add('replay-ticket-center--covered');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'replay-bubble-trigger replay-bubble-trigger--center';
    button.setAttribute('data-button-feedback', 'off');
    button.setAttribute('aria-label', 'Pop center to reveal');
    const cover = createBubbleCover(this.ownerDocument || document, {
      badgeSrc: '/app/assets/jackpot/flame-center-silver.svg', color: '#2e392f', durationMs: JACKPOT_POP_MS,
    });
    button.appendChild(cover.element);
    button.addEventListener('click', event => {
      event.stopPropagation();
      this.#revealCenter({ event });
    });
    button.addEventListener('keydown', event => event.stopPropagation());
    center.appendChild(button);
    this.#bubbleCovers.set('center', { cover, button });
  }

  #wireCenterCanvas(canvas) {
    let lastPos = null;
    const onScratch = (cx, cy) => {
      if (this.#centerScratched) return;
      this.#sfxScratchStart();
      const dpr = window.devicePixelRatio || 1;
      const brushR = BRUSH_R * dpr;
      this.#scratchAt(canvas, cx, cy);
      if (!this.#centerScratchGrid) this.#centerScratchGrid = makeScratchGrid(CENTER_GRID_RES);
      markGridCells(this.#centerScratchGrid, CENTER_GRID_RES, canvas.width, canvas.height, cx, cy, brushR);

      // Interpolate between last position for smooth strokes
      if (lastPos) {
        const dx = cx - lastPos.x, dy = cy - lastPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const steps = Math.max(1, Math.floor(dist / 4));
        for (let s = 1; s < steps; s++) {
          const ix = lastPos.x + dx * s / steps, iy = lastPos.y + dy * s / steps;
          this.#scratchAt(canvas, ix, iy);
          markGridCells(this.#centerScratchGrid, CENTER_GRID_RES, canvas.width, canvas.height, ix, iy, brushR);
        }
      }
      lastPos = { x: cx, y: cy };
      // Center uses 50% threshold (smaller area)
      if (gridCoverage(this.#centerScratchGrid) >= 0.5) {
        this.#revealCenter();
      }
    };

    const getPos = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      return { x: (clientX - rect.left) * dpr, y: (clientY - rect.top) * dpr };
    };

    const onMouseMove = (e) => {
      if (this.#centerScratched) return;
      const p = getPos(e.clientX, e.clientY);
      onScratch(p.x, p.y);
    };
    const onMouseLeave = () => { lastPos = null; this.#sfxScratchStop(); };
    const onTouchStart = (e) => {
      if (this.#centerScratched) return;
      e.preventDefault(); lastPos = null;
      const t = e.touches[0], p = getPos(t.clientX, t.clientY);
      onScratch(p.x, p.y);
    };
    const onTouchMove = (e) => {
      if (this.#centerScratched) return;
      e.preventDefault();
      const t = e.touches[0], p = getPos(t.clientX, t.clientY);
      onScratch(p.x, p.y);
    };
    const onTouchEnd = () => { lastPos = null; this.#sfxScratchStop(); };
    replaceScratchListeners(canvas, [
      { type: 'mousemove', handler: onMouseMove },
      { type: 'mouseleave', handler: onMouseLeave },
      { type: 'touchstart', handler: onTouchStart, options: { passive: false } },
      { type: 'touchmove', handler: onTouchMove, options: { passive: false } },
      { type: 'touchend', handler: onTouchEnd },
    ]);
  }

  #prepareCenterWinResult() {
    const prize = this.querySelector('[data-bind="center-prize"]');
    if (!prize || this.#centerWins.length === 0) return;
    const totalFlip = this.#centerWins.reduce((s, d) => s + BigInt(d.amount || '0'), 0n);
    const compactAmountStr = formatCenterBonusFlip(totalFlip);
    const seatFlip = this.#centerWins.filter(d => d.awardType === 'afking_seat_flip')
      .reduce((sum, d) => sum + BigInt(d.amount || '0'), 0n);
    const farFutureFlip = totalFlip - seatFlip;
    const battle = !this.#dayBonusTraitDraw;
    const label = seatFlip > 0n ? (farFutureFlip > 0n ? 'BONUS + AFKING' : 'AFKING SEAT') : battle ? 'CRAPS BATTLE' : 'BONUS';
    const description = [
      farFutureFlip > 0n ? `${formatFlip(farFutureFlip.toString())} FLIP ${battle ? 'craps battle' : 'far-future bonus'}` : '',
      seatFlip > 0n ? `${formatFlip(seatFlip.toString())} FLIP AFKing Seat Draw — credited to your coinflip stake` : '',
    ].filter(Boolean).join(' · ');
    prize.innerHTML = `
      <img class="ff-logo" src="/whitepaper/flame-logo-split.svg" alt="" aria-hidden="true">
      <span class="ff-amount">${compactAmountStr}</span>
      <span class="ff-label">${label}</span>`;
    prize.setAttribute('aria-label', description);
    prize.setAttribute('title', description);
  }

  #revealCenter({ instant = false, silent = false, event, batch = false } = {}) {
    if (this.#centerScratched || (this.#bonusPhase && this.#centerWins.length === 0)) return;
    this.#centerScratched = true;
    this.#sfxScratchStop();
    const revealedWin = this.#centerWins.length > 0 || this.#coinDrawDrawnIn();
    if (!silent && revealedWin && (this.revealChimes || instant)) this.#sfxGreenReveal();

    const canvas = this.querySelector('[data-bind="center-canvas"]');
    const prize = this.querySelector('[data-bind="center-prize"]');
    const center = this.querySelector('[data-bind="center"]');

    // Darken diamond background
    if (center) center.classList.add('revealed');

    if (canvas) {
      canvas.style.transition = instant ? 'none' : 'opacity 0.35s ease';
      canvas.style.opacity = '0';
      canvas.style.pointerEvents = 'none';
    }

    const finish = () => {
      center?.classList.remove('replay-ticket-center--covered');
      if (!instant) center?.classList.add('replay-ticket-center--popped');
      this.#syncDrawToggleAffordance();
    };
    if (this.#centerWins.length === 0) {
      if (!silent && !instant) sfxBubblePop({ tier: this.#coinDrawDrawnIn() ? 2 : 0 });
      this.#popBubble('center', event, instant, finish, batch);
    } else if (prize) {
      prize.style.display = 'flex';
      prize.classList.remove('visible');
      if (!silent && !instant) sfxBubblePop({ tier: 2 });
      this.#popBubble('center', event, instant, () => {
        prize.classList.add('visible');
        finish();
      }, batch);
    }

    this.#checkAllScratched({ silent, revealedWin });
  }

  // --- Quadrant reveal ---

  #revealQuadrant(qIdx, { instant = false, silent = false, event, batch = false } = {}) {
    if (this.#scratched[qIdx]) return;
    this.#scratched[qIdx] = true;
    this.#sfxScratchStop();
    const quads = this.querySelectorAll('.replay-tq');
    const quad = quads[qIdx];
    const canvas = quad.querySelector('.replay-scratch-canvas');
    const prize = quad.querySelector('.replay-prize-reveal');

    // Fade out canvas
    canvas.style.transition = instant ? 'none' : 'opacity 0.35s ease';
    canvas.style.opacity = '0';
    canvas.style.pointerEvents = 'none';

    const isWin = this.#quadWinArrays[qIdx].some(d => d.awardType !== 'overflow');
    const isSoloEthWin = isWin && !this.#bonusPhase && this.#isSoloEthWinner(qIdx);
    const publicSummary = this.#quadPublicSummaries[qIdx];
    const isSoloEthLoss = !isWin
      && !this.#bonusPhase
      && String(publicSummary?.currency || 'ETH').toUpperCase() !== 'FLIP'
      && Number(publicSummary?.winnerCount) === 1;
    // q-result-pending means the cover was blue/gold and the player could not
    // know the outcome yet. Give that specific blue→pink miss a small cue;
    // already-red guaranteed losses keep the ordinary quiet loss landing.
    const wasPotentialWin = quad.classList.contains('q-result-pending');
    // The bubble's own pop tells what is underneath before the reveal cue lands:
    // a dull miss, a near miss, a FLIP win, an ETH win, and your solo ETH win in gold.
    if (!silent && !instant) {
      const ethQuadrant = String(publicSummary?.currency || 'ETH').toUpperCase() !== 'FLIP';
      sfxBubblePop(isSoloEthWin ? { tier: 3, gold: true }
        : isWin ? { tier: ethQuadrant ? 3 : 2 }
          : { tier: !isSoloEthLoss && wasPotentialWin ? 1 : 0 });
    }
    this.#popSounded = !silent && !instant;
    if (!silent) {
      if (isSoloEthWin) {
        this.#soloEthCuePlayed = true;
        this.#sfxSoloEthReveal();
      } else if (isSoloEthLoss) {
        // The special cue belongs only to the actual solo winner. A losing
        // viewer uncovering the public solo bucket gets no reveal sound.
      } else if (isWin) this.#sfxReveal(true);
      else if (wasPotentialWin) this.#sfxPinkReveal();
      else this.#sfxReveal(false);
    }

    quad.classList.remove('q-scratchable', 'q-scratch-underlay', 'q-result-pending');
    quad.classList.add('q-result-revealed');
    if (isWin) {
      quad.classList.add('q-has-tickets');
      const badges = quad.querySelectorAll('.replay-badge-wrap');
      for (const badge of badges) { badge.tabIndex = 0; badge.removeAttribute('aria-hidden'); }
    } else {
      quad.classList.add('q-no-tickets');
      // Show main badge again for non-win owned quadrants
      const mainBadge = quad.querySelector('.badge-img');
      if (mainBadge) mainBadge.style.display = '';
    }

    if (prize) { prize.inert = false; prize.removeAttribute('aria-hidden'); }

    // A public bucket result uses the same prize layer as a player win. Since
    // `isWin` is false, explicitly reveal it and keep the full-size live badge
    // hidden; the result carries its own smaller badge.
    if (quad.classList.contains('q-public-result') && prize && !isWin) {
      prize.classList.add('visible');
      const mainBadge = quad.querySelector('.badge-img');
      if (mainBadge) mainBadge.style.display = 'none';
    }

    if (prize && isWin) {
      prize.classList.add('visible');
      this.#releaseWinReceipt(this.#preparedWinReceipts[qIdx]);
    }

    const revealDetail = { day: Number(this.#selectedDay), player: this.#selectedPlayer,
      runId: this.#announcedSpinId, bonusPhase: this.#bonusPhase, quadrant: qIdx };
    this.#popBubble(qIdx, event, instant, () => {
      if (!silent && !instant) this.dispatchEvent(new CustomEvent('replay:quadrant-revealed', {
        bubbles: true, detail: revealDetail,
      }));
      if (isWin && !silent && !instant) launchWinFlights(quad, this.querySelector('[data-bind="center"]'));
    }, batch);
    this.#checkAllScratched({ silent, revealedWin: isWin });
  }

  #checkAllScratched({ silent = false, revealedWin = false } = {}) {
    if (this.#spinning) return;
    const centerPending = (!this.#bonusPhase || this.#centerWins.length > 0) && !this.#centerScratched;
    const allDone = this.#scratched.every(s => s) && !centerPending;
    if (!this.#bonusPhase) this.#refreshMainPotentialScratchGate();
    if (allDone) {
      if (!this.#bonusPhase) {
        this.#mainScratchComplete = true;
        this.#mainPotentialScratchComplete = true;
      } else {
        this.#bonusScratchComplete = true;
      }
      const anyWon = this.#quadWinArrays.some(w => w.some(d => d.awardType !== 'overflow')) || this.#centerWins.length > 0;
      if (!silent) {
        // Completing the card must not turn a final empty bubble into a win cue.
        if (anyWon) this.#celebrate({ sound: revealedWin && !this.#soloEthCuePlayed });
        this.#dispatchScratchComplete();
      }
    }
    this.#syncSpinControlState();
    if (!this.#bonusPhase) this.#showBonusSection();
    this.#syncDrawToggleAffordance();
  }

  #isSoloEthWinner(qIdx) {
    const wins = (this.#quadWinArrays[qIdx] || [])
      .filter((win) => win.awardType !== 'overflow');
    const summary = this.#quadPublicSummaries[qIdx];
    if (String(summary?.currency || 'ETH').toUpperCase() === 'FLIP') return false;
    const positive = (value) => {
      try { return BigInt(value || '0') > 0n; }
      catch { return false; }
    };
    const hasEth = wins.some((win) => {
      const type = String(win.awardType || '').toLowerCase();
      if (type === 'aggregated' || win.isSolo) return positive(win.ethTotal);
      return (type === 'eth' || String(win.currency || '').toUpperCase() === 'ETH')
        && positive(win.amount ?? win.ethTotal);
    });
    return hasEth && (
      Number(summary?.winnerCount) === 1
      || wins.some((win) => win.isSolo)
    );
  }

  // --- Scattered win badges ---

  #activateBadgeReward(wrap) {
    if (!wrap || wrap.parentElement?.dataset.flying === 'true') return;
    const reward = wrap.querySelector?.('.replay-badge-reward-pop');
    if (!reward) return;
    wrap.classList.remove('is-reward-pop');
    void wrap.offsetWidth;
    // Several fresh wins may reveal together. Let their arcade callouts
    // coexist briefly; descending layers keep the first one in front without
    // forcing later rewards to wait or erase it.
    const sequence = this.#rewardPopSequence++;
    const stack = Math.max(1, 20 - sequence);
    // Quadrants need ordering against one another, but their paper must stay
    // beneath the center seal at z20. Badge callouts retain the full stack.
    const quadrantStack = Math.max(1, stack - 2);
    wrap.style.setProperty('--replay-reward-stack', String(stack));
    const quad = wrap.parentElement;
    if (quad) {
      const currentQuadStack = Number.parseInt(
        quad.style.getPropertyValue('--replay-quadrant-reward-stack'),
        10,
      ) || 0;
      quad.style.setProperty(
        '--replay-quadrant-reward-stack',
        String(Math.max(currentQuadStack, quadrantStack)),
      );
      quad.classList.add('q-reward-pop-active');
    }
    try {
      const quadRect = quad?.getBoundingClientRect?.();
      const badgeRect = wrap.getBoundingClientRect?.();
      const popupRect = reward.getBoundingClientRect?.();
      const direction = winningBadgeRewardDirection({
        badge: {
          left: badgeRect.left - quadRect.left,
          top: badgeRect.top - quadRect.top,
          width: badgeRect.width,
          height: badgeRect.height,
        },
        popup: { width: popupRect.width, height: popupRect.height },
        container: { width: quadRect.width, height: quadRect.height },
        randomValue: this.#rewardDirectionPhase,
        sequence,
      });
      wrap.dataset.rewardDirection = direction;
    } catch (_e) {
      // Headless/legacy DOM fallback still varies concurrent rewards instead
      // of collapsing them back into the old always-above position.
      wrap.dataset.rewardDirection = ['above', 'right', 'below', 'left'][sequence % 4];
    }
    positionBadgeReward(wrap, this.querySelector('[data-bind="center"]'));
    wrap.classList.add('is-reward-pop');
    this.#syncWinReceiptVisibility();
  }

  #placeWinBadges(qIdx, traitId) {
    const quads = this.querySelectorAll('.replay-tq');
    const quad = quads[qIdx];
    const wins = this.#quadWinArrays[qIdx];
    if (!wins || wins.length === 0) return;

    // Separate overflow sentinel (awardType='overflow') from real badge entries
    const realWins = wins.filter(w => w.awardType !== 'overflow');
    if (realWins.length === 0) return;

    // Default badge for this quadrant (used for wins without traitId)
    const defaultBadge = this.#resultBadge(traitId);
    const defaultPath = defaultBadge ? defaultBadge.path : '';
    const allBounds = [];
    // Keep the solo prize prominent within the same safe art lane as all wins.
    const soloIdx = !this.#bonusPhase && this.#isSoloEthWinner(qIdx)
      ? Math.max(0, realWins.findIndex((win) => win.isSolo))
      : -1;
    const soloSize = soloIdx < 0 ? 0 : 92;
    // Pack badges into stable cells inside the art band, then apply bounded
    // deterministic scatter. That preserves every result while keeping large
    // winning reveals loose and celebratory instead of grid-like.
    const layout = winningBadgeLayout({
      count: realWins.length,
      quadrant: qIdx,
      soloIndex: soloIdx,
      soloSize,
    });
    for (let w = 0; w < realWins.length; w++) {
      const position = layout[w];
      if (!position) continue;
      const isSoloBadge = w === soloIdx;
      const sizePct = position.size;
      const bestLeft = position.left;
      const bestTop = position.top;
      allBounds.push({ left: bestLeft, top: bestTop, right: bestLeft + sizePct, bottom: bestTop + sizePct });

      // Use each win's own traitId for its badge; fall back to quadrant default
      const winBadge = realWins[w].traitId != null ? this.#resultBadge(realWins[w].traitId) : defaultBadge;
      const winPath = winBadge ? winBadge.path : defaultPath;

      const wrap = document.createElement('div');
      wrap.className = isSoloBadge
        ? 'replay-badge-wrap replay-badge-wrap--solo'
        : 'replay-badge-wrap';
      wrap.tabIndex = -1;
      wrap.setAttribute('aria-hidden', 'true');
      wrap.style.width = sizePct + '%';
      wrap.style.left = bestLeft + '%';
      wrap.style.top = bestTop + '%';
      wrap.style.setProperty('--replay-badge-rotation', `${position.rotation || 0}deg`);
      wrap.style.setProperty('--replay-badge-layer', String(isSoloBadge ? 0 : (position.layer || 1)));
      const img = document.createElement('img');
      img.src = winPath; img.className = 'replay-scattered-badge'; img.alt = '';
      wrap.appendChild(img);
      const rewardLines = winningBadgeRewardLines({ ...realWins[w],
        isSolo: realWins[w].isSolo || (!this.#bonusPhase && Number(this.#quadPublicSummaries[qIdx]?.winnerCount) === 1) });
      if (rewardLines.length > 0) {
        wrap.setAttribute('role', 'button');
        wrap.setAttribute('aria-label', `Winning badge: ${rewardLines.map((line) => line.aria).join(', ')}`);
        const reward = document.createElement('span');
        reward.className = 'replay-badge-reward-pop';
        reward.setAttribute('aria-hidden', 'true');
        for (const line of rewardLines) {
          const row = document.createElement('span');
          row.className = `replay-badge-reward-pop__row replay-badge-reward-pop__row--${line.kind}`;
          if (line.kind === 'tickets') {
            row.appendChild(createJackpotTicketIcon('replay-badge-reward-pop__ticket'));
          } else {
            const icon = document.createElement('img');
            icon.className = 'replay-badge-reward-pop__icon';
            icon.src = line.kind === 'whale-pass'
              ? '/app/assets/whale-pass-mark.svg'
              : line.kind === 'decimator-entry'
              ? '/app/assets/decimator-draw-mark.svg?v=craps-battle-v3'
              : line.kind === 'craps-pass'
              ? '/symbols/dice_05_6_blue.svg'
              : line.kind === 'flip'
              ? '/whitepaper/flame-logo-split.svg'
              : '/symbols/crypto_06_ethereum_silver.svg';
            icon.alt = '';
            row.appendChild(icon);
          }
          const amount = document.createElement('b');
          amount.textContent = line.amount;
          row.appendChild(amount);
          reward.appendChild(row);
        }
        wrap.appendChild(reward);
        const showReward = () => this.#activateBadgeReward(wrap);
        wrap.addEventListener('mouseenter', showReward);
        wrap.addEventListener('focus', showReward);
        const inspect = () => { clearWinFlights(quad); showReward(); };
        wrap.addEventListener('click', inspect);
        wrap.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); inspect(); }
        });
        reward.addEventListener('animationend', () => {
          wrap.classList.remove('is-reward-pop');
          if (!quad.querySelector('.replay-badge-wrap.is-reward-pop')) {
            quad.classList.remove('q-reward-pop-active');
            quad.style.removeProperty('--replay-quadrant-reward-stack');
          }
          this.#syncWinReceiptVisibility();
        });
      }
      quad.appendChild(wrap);
    }

    // Overflow "+N more" label is deferred until the quadrant is fully
    // scratched — appended inside #revealQuadrant.

    // Store badge hit circles for green-reveal detection during scratch
    const circles = [];
    for (const bb of allBounds) {
      circles.push({ cx: (bb.left + bb.right) / 2, cy: (bb.top + bb.bottom) / 2, r: (bb.right - bb.left) / 2 });
    }
    this.#quadBadgeBounds[qIdx] = circles;
  }

  #clearScatteredBadges() {
    this.#preparedWinReceipts = [null, null, null, null];
    this.querySelectorAll('.q-reveal-preparing').forEach(quad => quad.classList.remove('q-reveal-preparing'));
    this.#clearBubbleCovers();
    for (const quad of this.querySelectorAll('.replay-tq')) clearWinFlights(quad);
    this.#rewardPopSequence = 0;
    this.#rewardDirectionPhase = Math.random();
    const els = this.querySelectorAll('.replay-badge-wrap, .replay-badge-overflow-label');
    for (const el of els) el.remove();
    const activeQuads = this.querySelectorAll('.replay-tq.q-reward-pop-active');
    for (const quad of activeQuads) {
      quad.classList.remove('q-reward-pop-active');
      quad.style.removeProperty('--replay-quadrant-reward-stack');
    }
  }

  #resetCards() {
    this.#animId++; // cancel any running spin
    this.#skipSpinId = null;
    this.#spinning = false;
    this.#stopIdleSpin();
    this.#sfxScratchStop();
    const quads = this.querySelectorAll('.replay-tq');
    quads.forEach(q => {
      q.classList.remove(
        'revealed', 'q-has-trait', 'q-no-tickets', 'q-traits-pending', 'q-scratchable',
        'q-has-tickets', 'q-public-result', 'q-win-impossible', 'q-win-impossible-lock',
        'q-owned-miss', 'q-player-win', 'q-solo-eth-win', 'q-craps-comp-win', 'q-gold-trait', 'q-scratch-underlay',
        'q-result-pending', 'q-result-revealed',
      );
      const img = q.querySelector('.badge-img');
      if (img) { img.src = ''; img.alt = ''; img.style.opacity = '0'; img.style.display = ''; img.removeAttribute('width'); img.removeAttribute('height'); }
      const canvas = q.querySelector('.replay-scratch-canvas');
      if (canvas) { canvas.style.opacity = '0'; canvas.style.pointerEvents = 'none'; }
      const prize = q.querySelector('.replay-prize-reveal');
      if (prize) {
        prize.classList.remove(
          'visible',
          'replay-bucket-reveal',
          'replay-player-win-reveal',
          ...BUCKET_REVEAL_POSITION_CLASSES,
          ...BUCKET_REVEAL_VARIANT_CLASSES,
        );
        prize.innerHTML = '';
        prize.removeAttribute('aria-label');
      }
    });
    this.#clearScatteredBadges();
    this.querySelector('[data-bind="card-grid"]')
      ?.classList.remove('replay-ticket--has-owned-gold');

    // Reset center diamond
    this.#hideCenterScratch();

    this.#quadRevealReady = [false, false, false, false];
    this.#scratched = [false, false, false, false];
    this.#scratchGrids = [null, null, null, null];
    this.#greenRevealed = [false, false, false, false];
    this.#badgesRevealed = [[], [], [], []];
    this.#quadBadgeBounds = [null, null, null, null];
    this.#quadOwned = [false, false, false, false];
    this.#quadWinArrays = [[], [], [], []];
    this.#quadPublicSummaries = [null, null, null, null];
    this.#centerWins = [];
    this.#centerScratched = false;
    this.#centerScratchGrid = null;

    // Reset per-player roll win caches
    this.#playerRoll1Wins = [];
    this.#playerRoll2Wins = [];

    // Reset bonus roll state
    this.#bonusPhase = false;
    this.#mainScratchComplete = false;
    this.#mainPotentialScratchComplete = false;
    this.#bonusScratchComplete = false;
    this.#mainSpinComplete = false;
    this.#bonusSpinComplete = false;
    this.#coinflipHandoffStarting = false;
    this.#drawViewSwitching = false;
    this.#bonusTraitIds = new Set();
    this.#bonusQuadrants = new Set();
    this.#syncDrawToggleAffordance();
    // Single-button mode hides/relabels the main button as the spins play out;
    // a new day (or a re-spin) puts it back to the main action.
    this.#btnMode = 'reveal';
    const revealBtn = this.querySelector('[data-bind="reveal-btn"]');
    if (revealBtn) {
      revealBtn.hidden = false;
      revealBtn.classList?.remove('is-bonus', 'is-coinflip', 'is-spinning');
      revealBtn.removeAttribute?.('aria-busy');
      revealBtn.textContent = MAIN_SPIN_LABEL;
    }
    const bonusSection = this.querySelector('[data-bind="bonus-section"]');
    if (bonusSection) bonusSection.hidden = true;
    const bonusBtn = this.querySelector('[data-bind="bonus-btn"]');
    if (bonusBtn) {
      bonusBtn.disabled = true;
      bonusBtn.hidden = false;
      bonusBtn.textContent = BONUS_SPIN_LOCKED_LABEL;
      bonusBtn.title = 'Pop the blue panels first';
    }
  }

  #celebrate({ sound = true } = {}) {
    if (sound) this.#sfxFanfare();
    celebrateProtocol({
      target: this.querySelector('[data-bind="card-grid"]') || this,
      tone: 'jackpot',
      big: true,
    });
  }

  // --- Web Audio SFX: short, dry crypto-slot cues ---

  #getAudio() {
    if (!this.#audioCtx) this.#audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (this.#audioCtx.state === 'suspended') this.#audioCtx.resume();
    return this.#audioCtx;
  }

  #sfxOutput(ctx) {
    if (this.#sfxBus) return this.#sfxBus;
    if (typeof ctx.createDynamicsCompressor !== 'function') return ctx.destination;
    const bus = ctx.createDynamicsCompressor();
    bus.threshold.value = -10;
    bus.knee.value = 3;
    bus.ratio.value = 6;
    bus.attack.value = 0.002;
    bus.release.value = 0.055;
    bus.connect(ctx.destination);
    this.#sfxBus = bus;
    return bus;
  }

  #slotTone({
    frequency,
    endFrequency = frequency,
    type = 'square',
    gain: level = 0.1,
    delay = 0,
    duration = 0.05,
  }) {
    if (isSfxMuted()) return;
    const ctx = this.#getAudio();
    const start = ctx.currentTime + Math.max(0, delay);
    const stop = start + Math.max(0.012, duration);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(Math.max(20, frequency), start);
    if (endFrequency !== frequency) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), stop);
    }
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.linearRampToValueAtTime(Math.max(0.001, level), start + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, stop);
    osc.connect(gain);
    gain.connect(this.#sfxOutput(ctx));
    osc.start(start);
    osc.stop(stop + 0.008);
  }

  #slotNoise({
    center = 2200,
    type = 'bandpass',
    q = 0.7,
    gain: level = 0.06,
    delay = 0,
    duration = 0.025,
  } = {}) {
    if (isSfxMuted()) return;
    const ctx = this.#getAudio();
    if (typeof ctx.createBuffer !== 'function'
      || typeof ctx.createBufferSource !== 'function'
      || typeof ctx.createBiquadFilter !== 'function') return;
    const seconds = Math.max(0.012, duration);
    const samples = Math.max(1, Math.ceil(ctx.sampleRate * seconds));
    const buffer = ctx.createBuffer(1, samples, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < samples; i++) data[i] = (Math.random() * 2) - 1;
    const source = ctx.createBufferSource();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    const start = ctx.currentTime + Math.max(0, delay);
    source.buffer = buffer;
    filter.type = type;
    filter.frequency.setValueAtTime(center, start);
    filter.Q.setValueAtTime(q, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.linearRampToValueAtTime(Math.max(0.001, level), start + 0.0015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + seconds);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.#sfxOutput(ctx));
    source.start(start);
    source.stop(start + seconds + 0.005);
  }

  #stopReelVoices() {
    for (const voice of this.#reelVoices) {
      if (!voice) continue;
      try { voice.oscillator.stop(); } catch { /* already stopped */ }
      try { voice.oscillator.disconnect(); } catch { /* defensive */ }
      try { voice.gain.disconnect(); } catch { /* defensive */ }
    }
    this.#reelVoices = [];
  }

  #reelTone(voiceIndex, {
    frequency,
    type,
    gain: level,
    delay = 0,
    duration,
  }) {
    if (isSfxMuted()) return;
    const ctx = this.#getAudio();
    let voice = this.#reelVoices[voiceIndex];
    if (!voice || voice.context !== ctx) {
      if (voice) {
        try { voice.oscillator.stop(); } catch { /* already stopped */ }
        try { voice.oscillator.disconnect(); } catch { /* defensive */ }
        try { voice.gain.disconnect(); } catch { /* defensive */ }
      }
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = type;
      oscillator.connect(gain);
      gain.connect(this.#sfxOutput(ctx));
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      oscillator.start(ctx.currentTime);
      voice = { context: ctx, oscillator, gain };
      this.#reelVoices[voiceIndex] = voice;
    }

    const start = ctx.currentTime + Math.max(0, delay);
    const stop = start + Math.max(0.012, duration);
    const frequencyParam = voice.oscillator.frequency;
    const gainParam = voice.gain.gain;
    frequencyParam.cancelScheduledValues?.(start);
    frequencyParam.setValueAtTime(Math.max(20, frequency), start);
    gainParam.cancelScheduledValues?.(start);
    gainParam.setValueAtTime(0.0001, start);
    gainParam.linearRampToValueAtTime(Math.max(0.001, level), start + 0.002);
    gainParam.exponentialRampToValueAtTime(0.0001, stop);
  }

  #sfxSpinFrame(winnableCount, goldWinnable, lockCount) {
    const count = Math.max(0, Math.min(4, Math.trunc(Number(winnableCount) || 0)));
    const progress = Math.max(0, Math.min(8, Math.trunc(Number(lockCount) || 0)));
    // A dry musical pulse on EVERY painted frame. The chord walks upward as
    // more blue faces appear, while lock progress cycles a four-step arp so the
    // reel has actual motion instead of isolated mechanical clicks.
    const reelPitches = [330, 392, 494, 587, 740];
    const reelGains = [0.105, 0.135, 0.17, 0.215, 0.265];
    const arpRatios = [1, 1.125, 1.25, 1.5];
    const root = goldWinnable
      ? 988 * arpRatios[progress % arpRatios.length]
      : reelPitches[count] * arpRatios[progress % arpRatios.length];
    this.#reelTone(0, {
      frequency: root,
      type: 'triangle',
      gain: goldWinnable ? 0.3 : reelGains[count],
      duration: 0.072,
    });
    this.#reelTone(1, {
      frequency: root * 2,
      type: 'sine',
      gain: goldWinnable ? 0.115 : 0.035 + (count * 0.012),
      delay: 0.012,
      duration: 0.058,
    });
  }

  #sfxLock({ winnable = false, gold = false } = {}) {
    if (gold) {
      // Gold: a metal strike and a short upper-register sparkle.
      this.#slotNoise({ center: 6500, type: 'highpass', q: 0.55, gain: 0.09, duration: 0.018 });
      this.#slotTone({ frequency: 1047, type: 'triangle', gain: 0.22, duration: 0.11 });
      [1568, 2093, 3136].forEach((frequency, index) => {
        this.#slotTone({
          frequency,
          type: 'sine',
          gain: 0.145 - (index * 0.025),
          delay: 0.025 + index * 0.035,
          duration: 0.15,
        });
      });
    } else if (winnable) {
      // Blue: a crisp latch followed by a rising two-note chime.
      this.#slotNoise({ center: 3000, type: 'bandpass', q: 0.8, gain: 0.085, duration: 0.014 });
      this.#slotTone({
        frequency: 784, type: 'triangle', gain: 0.245, duration: 0.12,
      });
      this.#slotTone({
        frequency: 1175, type: 'sine', gain: 0.15, delay: 0.035, duration: 0.16,
      });
    } else {
      // Red: a compact low knock, clearly separate from the reel's pitches.
      this.#slotNoise({ center: 850, type: 'lowpass', q: 0.7, gain: 0.16, duration: 0.026 });
      this.#slotTone({
        frequency: 165, endFrequency: 82, type: 'triangle', gain: 0.22, duration: 0.085,
      });
    }
  }

  #sfxAllLocked(anyOwned) {
    // Delay the final confirmation until the eighth lock cue has cleared.
    this.#slotTone({
      frequency: anyOwned ? 1047 : 262,
      type: 'triangle',
      gain: anyOwned ? 0.235 : 0.14,
      delay: 0.27,
      duration: anyOwned ? 0.16 : 0.1,
    });
    if (anyOwned) this.#slotTone({
      frequency: 1568,
      type: 'sine',
      gain: 0.13,
      delay: 0.315,
      duration: 0.18,
    });
  }

  #sfxScratchStart() {
    if (this.#scratchNode || isSfxMuted()) return;
    const ctx = this.#getAudio();
    if (!this.#scratchNoiseBuffer) {
      const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      this.#scratchNoiseBuffer = buffer;
    }
    const noise = ctx.createBufferSource();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    noise.buffer = this.#scratchNoiseBuffer; noise.loop = true;
    filter.type = 'bandpass'; filter.frequency.value = 3000; filter.Q.value = 0.5;
    noise.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
    gain.gain.value = 0.05;
    noise.start();
    this.#scratchNode = { noise, gain };
  }

  #sfxScratchStop() {
    if (!this.#scratchNode) return;
    try { this.#scratchNode.noise.stop(); } catch {}
    this.#scratchNode = null;
  }

  #sfxGreenReveal() {
    // A crisp arcade-coin sparkle for EACH paid badge. A 14ms high-frequency
    // attack gives it definition over the scratch bed; short triangle/square
    // voices keep it bright without the rounded pitch glide that read as a
    // water droplet.
    this.#slotNoise({ center: 5800, type: 'highpass', q: 0.55, gain: 0.07, duration: 0.014 });
    this.#slotTone({
      frequency: 1568, type: 'triangle', gain: 0.22, duration: 0.075,
    });
    this.#slotTone({
      frequency: 2349, type: 'square', gain: 0.085,
      delay: 0.018, duration: 0.06,
    });
    this.#slotTone({
      frequency: 3136, type: 'sine', gain: 0.075,
      delay: 0.04, duration: 0.12,
    });
  }

  #sfxPinkReveal() {
    if (!this.revealChimes && this.#popSounded) return;
    // A restrained paper-puff + soft falling pair for a blue/gold cover that
    // resolves pink. It acknowledges the near-miss without sounding like a
    // jackpot loss stinger or competing with paid-badge dings.
    this.#slotNoise({
      center: 1650, type: 'bandpass', q: 0.65, gain: 0.025, duration: 0.018,
    });
    this.#slotTone({
      frequency: 523, endFrequency: 392,
      type: 'triangle', gain: 0.07, duration: 0.12,
    });
    this.#slotTone({
      frequency: 330, endFrequency: 294,
      type: 'sine', gain: 0.035, delay: 0.045, duration: 0.13,
    });
  }

  #sfxReveal(isWin) {
    if (!this.revealChimes && this.#popSounded) return;
    if (isWin) {
      this.#slotTone({
        frequency: 784, type: 'triangle', gain: 0.17, duration: 0.09,
      });
      this.#slotTone({
        frequency: 1175, type: 'sine', gain: 0.1, delay: 0.025, duration: 0.12,
      });
    } else {
      this.#slotTone({
        frequency: 170, endFrequency: 72, type: 'triangle', gain: 0.145, duration: 0.09,
      });
    }
  }

  #sfxSoloEthReveal() {
    if (!this.revealChimes && this.#popSounded) return;
    // A solo bucket is the main draw's rare one-wallet ETH hit. Give it a
    // two-stage vault-open cue: a low rising foundation, then a crystalline
    // C-major coin burst. It replaces both the ordinary two-note reveal and
    // the end-of-roll fanfare so the identity stays unmistakable.
    this.#slotNoise({
      center: 6400, type: 'highpass', q: 0.5, gain: 0.085, duration: 0.022,
    });
    this.#slotTone({
      frequency: 130.81, endFrequency: 261.63,
      type: 'sawtooth', gain: 0.075, duration: 0.56,
    });
    this.#slotTone({
      frequency: 261.63, endFrequency: 523.25,
      type: 'triangle', gain: 0.15, delay: 0.025, duration: 0.5,
    });
    [523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) => {
      this.#slotTone({
        frequency,
        type: 'triangle',
        gain: 0.245 - (index * 0.022),
        delay: 0.075 + (index * 0.075),
        duration: 0.34,
      });
    });
    [1568, 2093, 3136].forEach((frequency, index) => {
      this.#slotTone({
        frequency,
        type: 'sine',
        gain: 0.105 - (index * 0.018),
        delay: 0.24 + (index * 0.06),
        duration: 0.42,
      });
    });
  }

  #sfxFanfare() {
    // Bright C-major cabinet win: no low drone and no descending sample, so it
    // resolves celebratory rather than ominous. The octave sparkle supplies a
    // crypto/arcade edge without turning into a long coin-shower loop.
    [1047, 1319, 1568, 2093].forEach((frequency, index) => {
      this.#slotTone({
        frequency,
        type: 'triangle',
        gain: 0.255 - (index * 0.025),
        delay: index * 0.07,
        duration: 0.24,
      });
    });
    [2093, 2637, 3136].forEach((frequency, index) => this.#slotTone({
      frequency,
      type: 'sine',
      gain: 0.11 - (index * 0.015),
      delay: 0.19 + (index * 0.055),
      duration: 0.28,
    }));
  }
}

customElements.define('replay-panel', ReplayPanel);
